// src/pages/UserProfile.js
/**
 * UserProfile.js
 *
 * This component renders the user dashboard for a car management application.
 * It includes user information, a list of vehicles, service history, VIN decoding,
 * and the ability to schedule a service. The user can also toggle dark mode and log out.
 */
import React, { use, useEffect, useState } from "react";
import {
  Box,
  CircularProgress,
  Typography,
  Container,
  Grid,
  Card,
  CardContent,
  Avatar,
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  Chip,
  IconButton,
  createTheme,
  ThemeProvider,
  Slide,
  MenuItem,
  Tooltip,
  Paper,
  Fade,
  Stack,
  Rating,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Badge,
  Collapse,
} from "@mui/material";
import {
  InfoOutlined,
  DirectionsCarFilled,
  History as HistoryIcon,
  Build as BuildIcon,
  Settings as SettingsIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Email as EmailIcon,
} from "@mui/icons-material";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import StarIcon from "@mui/icons-material/Star";
import PhoneIcon from "@mui/icons-material/Phone";
import BusinessIcon from "@mui/icons-material/Business";
import { useNavigate } from "react-router-dom";
import Header from "../components/header.js";
import Footer from "../components/footer.js";
import CarDropdown from "../components/CarDropdown.js";
import getApiBase from "../components/getApiBase";
import AddIcon from "@mui/icons-material/Add";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import ServiceRequestForm from "../components/ServiceRequestForm";
import { AlertCircle } from "lucide-react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { format, parseISO, set } from "date-fns";
import { usStates } from "../data/states";
import dayjs from "dayjs";
import GoogleReviewsModal from "../components/GoogleReviewsModal";
import ConversationModal from "../components/ConversationModal";
import AutoshopLogo from '../assets/MaddyAutoshopLogo.png'

/** Problem / job text from description when catalog services are missing (Voice notes). */
const problemFromServiceRequestDescription = (description) => {
  const skip =
    /^(Voice booking|Caller:|EZ Voice|Preferred window)/i;
  const line = String(description || "")
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean)
    .find((l) => !skip.test(l));
  if (!line) return "";
  return line.length > 80 ? `${line.slice(0, 77)}…` : line;
};

/** Same list pattern as normal requests: year make model – work. */
const formatServiceRequestListTitle = (entry) => {
  const carParts = [entry.car_year, entry.car_make, entry.car_model].filter(
    (p) => p != null && String(p).trim() !== "" && String(p) !== "undefined"
  );
  const carLabel = carParts.join(" ");
  const services = (entry.services || "").trim();
  const problem = problemFromServiceRequestDescription(entry.description);
  const work = services || problem;
  let detail = "";
  if (carLabel && work) detail = `${carLabel} – ${work}`;
  else if (carLabel) detail = carLabel;
  else if (work) detail = work;
  else detail = "Voice booking";
  return `#${entry.request_id}: ${detail}`;
};

/** Full catalog, one row per name. Staging copies General Mechanic under `generic`; keep the earlier id. */
const uniqueServicesByName = (rows) => {
  if (!Array.isArray(rows)) return [];
  const byName = new Map();
  for (const row of rows) {
    const key = String(row?.service_name || "").trim().toLowerCase();
    if (!key) continue;
    const prev = byName.get(key);
    if (!prev || Number(row.id) < Number(prev.id)) byName.set(key, row);
  }
  return [...byName.values()];
};

// Format 24h time (HH:mm or HH:mm:ss) for bid appointment display
const formatBidTime = (time24) => {
  if (!time24) return "";
  const s = String(time24).trim();
  const match = s.match(/^(\d{1,2}):(\d{2})/);
  if (!match) return s;
  const h = parseInt(match[1], 10);
  const m = match[2];
  const ampm = h < 12 ? "AM" : "PM";
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${h12}:${m} ${ampm}`;
};

// CSS for animations
const styles = `
@keyframes pulseBackground {
  0%, 100% {
    background-color: rgba(230, 57, 38, 0.05);
  }
  50% {
    background-color: rgba(198, 47, 30, 0.35);
  }
}

.service-tile-new-message {
  animation: pulseBackground 2s infinite;
}

@keyframes pulseButton {
  0%, 100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(76, 175, 80, 0.7);
  }
  50% {
    transform: scale(1.05);
    box-shadow: 0 0 0 10px rgba(76, 175, 80, 0);
  }
}

.pulse-button {
  animation: pulseButton 2s infinite;
}
`;

// Inject styles
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement("style");
  styleSheet.innerText = styles;
  document.head.appendChild(styleSheet);
}

const UserProfile = () => {
  const [failedVehicleImages, setFailedVehicleImages] = useState({});
  const [visitedShops, setVisitedShops] = useState([]);
  const navigate = useNavigate();
  const [selectedCarId, setSelectedCarId] = useState(1);
  const [darkMode, setDarkMode] = useState(false);
  const [vin, setVin] = useState("");
  const [mileage, setMileage] = useState("");
  const [decodedInfo, setDecodedInfo] = useState(null);
  const [user, setUser] = useState(null);
  const [isEditingUser, setIsEditingUser] = useState(false);
  const [editedUser, setEditedUser] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone_number: "",
    street_address: "",
    city: "",
    state: "",
    zip_code: "",
  });
  const [cars, setCars] = useState([]);
  const [address, setAddress] = useState(null);
  const [serviceHistoryByCar, setServiceHistoryByCar] = useState({});
  const [addingCar, setAddingCar] = useState(false);
  const [newVin, setNewVin] = useState("");
  const [newMileage, setNewMileage] = useState("");
  const [decodedCar, setDecodedCar] = useState(null);
  const [manualEntry, setManualEntry] = useState(false);
  const [manualCarData, setManualCarData] = useState({
    make: "",
    model: "",
    year: "",
    trim: "",
    engine: "",
  });
  const [obd2HistoryByCar, setObd2HistoryByCar] = useState({});
  const [services, setServices] = useState([]);
  const [autoshops, setAutoshops] = useState([]);
  const [selectedAutoshop, setSelectedAutoshop] = useState("");
  const [selectedServices, setSelectedServices] = useState([]);
  const [serviceDescription, setServiceDescription] = useState("");
  const [availableDates, setAvailableDates] = useState([]);

  // NEW: Request type selection
  const [requestType, setRequestType] = useState(""); // 'direct', 'announcement', or 'bidding'
  const [availableRequestTypes, setAvailableRequestTypes] = useState({
    direct: true,
    announcement: false,
    bidding: false
  });
  const [requestTypesLoaded, setRequestTypesLoaded] = useState(false);
  const [isSubmittingServiceRequest, setIsSubmittingServiceRequest] = useState(false);
  const [dropoffWeekStart, setDropoffWeekStart] = useState(null); // Monday of week (YYYY-MM-DD)
  const [dropoffAvailableDays, setDropoffAvailableDays] = useState([]); // e.g. ['Mon','Tue','Wed','Thu'] for that week

  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [comments, setComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [toast, setToast] = useState({
    open: false,
    severity: "success",
    msg: "",
  });

  const [zipCode, setZipCode] = useState("");
  const [expandedIndex, setExpandedIndex] = useState(null);
  const [scheduleServiceExpanded, setScheduleServiceExpanded] = useState(false);
  const [servicesInProgress, setServicesInProgress] = useState([]);

  // Separate service requests (quotes being negotiated) from scheduled/in-progress services
  // If any bid is shop_confirmed, treat as scheduled (show in Services In Progress) even if DB status is stale
  const hasShopConfirmed = (entry) => entry.bids?.some(b => (b.bid_status || '') === 'shop_confirmed');
  const serviceRequests = servicesInProgress.filter(entry =>
    ['Open', 'Pending', 'Accepted'].includes(entry.status) && !hasShopConfirmed(entry)
  );
  const scheduledServices = servicesInProgress.filter(entry =>
    ['Scheduled', 'In Progress'].includes(entry.status) || hasShopConfirmed(entry)
  );
  const [selectedServiceDetail, setSelectedServiceDetail] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  // Platform ratings state
  const [platformRatings, setPlatformRatings] = useState({
    customer_rating: null, // Mechanic rates customer (display only)
    mechanic_rating: null, // Customer rates mechanic (editable)
    platform_rating: null, // User rates Autoshops.com (editable)
  });
  // Separate modals for each rating type
  const [mechanicRatingModalOpen, setMechanicRatingModalOpen] = useState(false);
  const [platformRatingModalOpen, setPlatformRatingModalOpen] = useState(false);
  const [customerRatingModalOpen, setCustomerRatingModalOpen] = useState(false);

  // Separate state for each rating type
  const [mechanicRating, setMechanicRating] = useState(0);
  const [mechanicComment, setMechanicComment] = useState("");
  const [platformRating, setPlatformRating] = useState(0);
  const [platformComment, setPlatformComment] = useState("");
  const [savingMechanicRating, setSavingMechanicRating] = useState(false);
  const [savingPlatformRating, setSavingPlatformRating] = useState(false);
  const [editingCarId, setEditingCarId] = useState(null);
  const [editedCar, setEditedCar] = useState({
    vin: "",
    mileage: "",
  });
  const [isEditingCar, setIsEditingCar] = useState(false);
  // Address field validation errors
  const [addressErrors, setAddressErrors] = useState({
    street_address: "",
    city: "",
    state: "",
    zip_code: "",
  });
  // User field validation errors
  const [userFieldErrors, setUserFieldErrors] = useState({
    first_name: "",
    last_name: "",
    phone_number: "",
  });
  const [formData, setFormData] = useState({
    carYear: "",
    carMake: "",
    carModel: "",
    carTrim: "",
    engineSize: "",
    mileage: "",
  });
  const [radius, setRadius] = useState(10);
  const [newRadius, setNewRadius] = useState(10);
  const [latitude, setLatitude] = useState(null);
  const [longitude, setLongitude] = useState(null);
  const [userZip, setUserZip] = useState("");
  const [selectedSlot, setSelectedSlot] = useState({});
  const [selectedCounterTime, setSelectedCounterTime] = useState(null);

  const [loading, setLoading] = useState(true);

  // Google Reviews Modal state
  const [reviewsModalOpen, setReviewsModalOpen] = useState(false);
  const [selectedShopForReviews, setSelectedShopForReviews] = useState(null);
  const [shopRatings, setShopRatings] = useState({});

  // Conversation Modal state
  const [conversationModalOpen, setConversationModalOpen] = useState(false);
  const [selectedServiceForConversation, setSelectedServiceForConversation] = useState(null);
  const [conversationComments, setConversationComments] = useState([]);
  const [conversationPreviewMap, setConversationPreviewMap] = useState({});
  const [loadingConversation, setLoadingConversation] = useState(false);

  // New message detection - store both Set for quick lookup and Map for counts
  const [servicesWithNewMessages, setServicesWithNewMessages] = useState(new Set());
  const [unreadMessageCounts, setUnreadMessageCounts] = useState(new Map());

  const getServiceRequestId = (service) => {
    if (service === null || service === undefined) return null;
    if (typeof service === "number" || typeof service === "string") return service;
    return (
      service.service_request_id ??
      service.serviceRequestId ??
      service.request_id ??
      service.requestId ??
      service.id ??
      service.__serviceRequestId ??
      service?.Service_Request?.id ??
      null
    );
  };

  // Email change states
  const [emailChangeModalOpen, setEmailChangeModalOpen] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [emailChangeLoading, setEmailChangeLoading] = useState(false);

  const today = new Date();

  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0"); // zero-padded
  const day = String(today.getDate()).padStart(2, "0"); // zero-padded

  const requestDate = `${year}-${month}-${day}`; // → "2025-06-23"

  const theme = createTheme({
    palette: {
      mode: darkMode ? "dark" : "light",
    },
  });

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // ===== NEW HANDLERS FOR GOOGLE REVIEWS & CONVERSATION =====

  // Open Google Reviews Modal
  const handleOpenReviewsModal = async (shopId, shopName, placeId) => {
    setSelectedShopForReviews({ shopId, shopName, placeId });
    setReviewsModalOpen(true);
  };

  // Fetch Platform Ratings for a service request
  const fetchPlatformRatings = async (serviceRequestId, userId, mechanicId) => {
    if (!serviceRequestId || !userId) return;

    try {
      // Fetch service-specific ratings (customer rating, mechanic rating)
      const serviceRes = await fetch(
        `${getApiBase()}/api/platform-ratings/service/${serviceRequestId}`,
        { credentials: 'include' }
      );

      if (serviceRes.ok) {
        const serviceData = await serviceRes.json();
        setPlatformRatings(prev => ({
          ...prev,
          customer_rating: serviceData.customer_rating,
          mechanic_rating: serviceData.mechanic_rating,
        }));
      }

      // Fetch platform rating (Autoshops.com rating) for current user
      const platformRes = await fetch(
        `${getApiBase()}/api/platform-ratings/platform?userId=${userId}${mechanicId ? `&mechanicId=${mechanicId}` : ''}`,
        { credentials: 'include' }
      );

      if (platformRes.ok) {
        const platformData = await platformRes.json();
        setPlatformRatings(prev => ({
          ...prev,
          platform_rating: platformData.platform_rating || null,
        }));
      }
    } catch (error) {
      console.error('Failed to fetch platform ratings:', error);
    }
  };

  // Handle opening mechanic rating modal
  const handleOpenMechanicRatingModal = () => {
    if (platformRatings.mechanic_rating) {
      setMechanicRating(platformRatings.mechanic_rating.rating || 0);
      setMechanicComment(platformRatings.mechanic_rating.comment || "");
    } else {
      setMechanicRating(0);
      setMechanicComment("");
    }
    setMechanicRatingModalOpen(true);
  };

  // Handle opening platform rating modal
  const handleOpenPlatformRatingModal = () => {
    if (platformRatings.platform_rating) {
      setPlatformRating(platformRatings.platform_rating.rating || 0);
      setPlatformComment(platformRatings.platform_rating.comment || "");
    } else {
      setPlatformRating(0);
      setPlatformComment("");
    }
    setPlatformRatingModalOpen(true);
  };

  // Save mechanic rating
  const handleSaveMechanicRating = async () => {
    if (!selectedServiceDetail || !user) return;
    if (mechanicRating < 1 || mechanicRating > 5) {
      setToast({ open: true, severity: "error", msg: "Please select a rating between 1 and 5 stars" });
      return;
    }

    setSavingMechanicRating(true);
    try {
      const serviceRequestId = getServiceRequestId(selectedServiceDetail);
      const mechanicId = selectedServiceDetail.mechanics?.[0]?.id || null;

      if (!serviceRequestId) {
        throw new Error("Service request ID is missing. Please refresh the page and try again.");
      }
      if (!mechanicId) {
        throw new Error("Mechanic information is missing for this service. Cannot rate mechanic.");
      }

      const payload = {
        rating_type: 'mechanic',
        rating: mechanicRating,
        comment: mechanicComment.trim() || null,
        service_request_id: serviceRequestId,
        rated_id: mechanicId,
      };

      const response = await fetch(`${getApiBase()}/api/platform-ratings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to save rating');
      }

      const savedRating = await response.json();

      setPlatformRatings(prev => ({
        ...prev,
        mechanic_rating: savedRating.rating || { rating: mechanicRating, comment: mechanicComment.trim() || null },
      }));

      setToast({ open: true, severity: "success", msg: "Mechanic rating saved successfully!" });
      setMechanicRatingModalOpen(false);
    } catch (error) {
      console.error('Failed to save mechanic rating:', error);
      setToast({ open: true, severity: "error", msg: error.message || "Failed to save rating" });
    } finally {
      setSavingMechanicRating(false);
    }
  };

  // Save platform rating
  const handleSavePlatformRating = async () => {
    if (!selectedServiceDetail || !user) return;
    if (platformRating < 1 || platformRating > 5) {
      setToast({ open: true, severity: "error", msg: "Please select a rating between 1 and 5 stars" });
      return;
    }

    setSavingPlatformRating(true);
    try {
      const payload = {
        rating_type: 'platform',
        rating: platformRating,
        comment: platformComment.trim() || null,
      };

      // TASK-564: null/missing Users.role must still rate as Car Owner (user_id path)
      if (user.role === 'Owner' || user.role === 'Mechanic') {
        payload.mechanic_id = user.id;
      } else {
        payload.user_id = user.id;
      }

      const response = await fetch(`${getApiBase()}/api/platform-ratings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || errorData.detail || 'Failed to save rating');
      }

      const savedRating = await response.json();

      setPlatformRatings(prev => ({
        ...prev,
        platform_rating: savedRating.rating || { rating: platformRating, comment: platformComment.trim() || null },
      }));

      setToast({ open: true, severity: "success", msg: "Platform rating saved successfully!" });
      setPlatformRatingModalOpen(false);
    } catch (error) {
      console.error('Failed to save platform rating:', error);
      setToast({ open: true, severity: "error", msg: error.message || "Failed to save rating" });
    } finally {
      setSavingPlatformRating(false);
    }
  };

  // Open Conversation Modal
  const handleOpenConversationModal = async (service) => {
    const serviceRequestId = getServiceRequestId(service);
    if (!serviceRequestId) {
      console.error("No service_request_id found for conversation", service);
      return;
    }
    setSelectedServiceForConversation({ ...service, __serviceRequestId: serviceRequestId });
    setLoadingConversation(true);
    setConversationModalOpen(true);

    // Fetch comments
    try {
      const response = await fetch(
        `${getApiBase()}/api/messages/${serviceRequestId}`,
        { credentials: 'include' }
      );
      if (response.ok) {
        const data = await response.json();
        const comments = data.comments || [];
        setConversationComments(comments);
        setConversationPreviewMap(prev => ({
          ...prev,
          [serviceRequestId]: comments,
        }));

        // Messages are marked as read on the backend when modal opens
        // Update local state immediately to reflect read status
        setServicesWithNewMessages(prev => {
          const newSet = new Set(prev);
          newSet.delete(serviceRequestId);
          return newSet;
        });
        // Also clear the unread count for this service
        setUnreadMessageCounts(prev => {
          const newMap = new Map(prev);
          newMap.delete(serviceRequestId);
          return newMap;
        });
      }
    } catch (error) {
      console.error('Failed to fetch comments:', error);
    } finally {
      setLoadingConversation(false);
    }
  };

  // Send message in conversation
  const handleSendMessage = async (message) => {
    if (!selectedServiceForConversation) return;
    const serviceRequestId = getServiceRequestId(selectedServiceForConversation);
    if (!serviceRequestId) {
      console.error("Cannot send message without service_request_id", selectedServiceForConversation);
      return;
    }
    try {
      const response = await fetch(
        `${getApiBase()}/api/messages`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            service_request_id: serviceRequestId,
            comment: message,
          }),
        }
      );

      if (response.ok) {
        const newComment = await response.json();
        setConversationComments(prev => [...prev, newComment]);
        setConversationPreviewMap(prev => {
          const existing = prev[serviceRequestId] || [];
          return {
            ...prev,
            [serviceRequestId]: [...existing, newComment],
          };
        });
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      throw error;
    }
  };

  // Track "Leave Review" button click
  const handleLeaveReviewClick = async (serviceRequestId) => {
    try {
      await fetch(`${getApiBase()}/api/reviews/track-button-click`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ service_request_id: serviceRequestId }),
      });
      console.log('Review button click tracked');
    } catch (error) {
      console.error('Failed to track review button click:', error);
    }
  };

  // Check if service has new messages
  const hasNewMessage = (serviceOrId) => {
    const id = typeof serviceOrId === "object" ? getServiceRequestId(serviceOrId) : serviceOrId;
    if (!id) return false;
    return servicesWithNewMessages.has(id);
  };

  // Get unread message count for a service
  const getUnreadMessageCount = (serviceOrId) => {
    const id = typeof serviceOrId === "object" ? getServiceRequestId(serviceOrId) : serviceOrId;
    if (!id) return 0;
    return unreadMessageCounts.get(id) || 0;
  };

  // Check if service has any new activity from shop (new quotes, quote updates, accepted quotes)
  const hasNewShopActivity = (serviceOrId) => {
    const service = typeof serviceOrId === "object" ? serviceOrId : null;
    if (!service) return false;

    // Check for new messages
    if (hasNewMessage(service)) return true;

    // Check for new quotes (quotes submitted in last 24 hours that haven't been viewed)
    const hasNewQuotes = service.bids?.some(bid => {
      if (!bid.bid_time) return false;
      const bidTime = new Date(bid.bid_time);
      const hoursSinceBid = (Date.now() - bidTime.getTime()) / (1000 * 60 * 60);
      // Consider "new" if submitted in last 24 hours and status is still Pending (not accepted/rejected yet)
      return hoursSinceBid < 24 && bid.status === 'Pending' && bid.bid_status === 'pending';
    });

    return hasNewQuotes;
  };

  // Fetch shop ratings
  const fetchShopRatings = async (services) => {
    const shopIds = [...new Set(services.map(s => s.shop_id).filter(Boolean))];

    const ratings = {};
    await Promise.all(
      shopIds.map(async (shopId) => {
        try {
          const response = await fetch(
            `${getApiBase()}/api/services/shops/${shopId}/reviews?limit=5`,
            { credentials: 'include' }
          );
          if (response.ok) {
            const data = await response.json();
            ratings[shopId] = {
              avg_rating: data.avg_rating,
              review_count: data.review_count,
              reviews: data.reviews,
            };
          }
        } catch (error) {
          console.error(`Failed to fetch ratings for shop ${shopId}:`, error);
        }
      })
    );

    return ratings;
  };

  // Check for new messages
  const checkForNewMessages = async (services) => {
    const newMessageServiceIds = new Set();
    const messageCounts = new Map();

    console.log('🔍 Checking for new messages across', services.length, 'services');

    await Promise.all(
      services.map(async (service) => {
        const serviceRequestId = getServiceRequestId(service);
        if (!serviceRequestId) {
          console.warn('⚠️ No serviceRequestId found for service:', service);
          return;
        }
        try {
          // Don't mark messages as read when just checking for new messages
          const response = await fetch(
            `${getApiBase()}/api/messages/${serviceRequestId}?markAsRead=false`,
            { credentials: 'include' }
          );
          if (response.ok) {
            const data = await response.json();
            const comments = data.comments || [];

            // Count unread messages from shop/mechanic
            // Must check BOTH: author_type (shop/mechanic) AND is_read (false)
            // This ensures we only show notifications for unread shop messages, not customer messages
            const unreadShopMessages = comments.filter(
              comment => {
                const isFromShop = comment.author_type === 'mechanic' || comment.author_type === 'shop';
                const isUnread = comment.is_read === false || comment.is_read === undefined;
                return isFromShop && isUnread;
              }
            );

            if (unreadShopMessages.length > 0) {
              console.log(`✅ Found ${unreadShopMessages.length} unread message(s) for service ${serviceRequestId}`);
              newMessageServiceIds.add(serviceRequestId);
              messageCounts.set(serviceRequestId, unreadShopMessages.length);
            } else {
              console.log(`ℹ️ No unread messages for service ${serviceRequestId}`);
            }
          } else {
            console.error(`❌ Failed to fetch messages for service ${serviceRequestId}:`, response.status);
          }
        } catch (error) {
          console.error(`❌ Error checking messages for service ${serviceRequestId}:`, error);
        }
      })
    );

    console.log('📊 Message check complete:', {
      servicesWithNewMessages: Array.from(newMessageServiceIds),
      counts: Object.fromEntries(messageCounts)
    });

    setServicesWithNewMessages(newMessageServiceIds);
    setUnreadMessageCounts(messageCounts);
  };


  const [vehiclePhotos, setVehiclePhotos] = useState({});
  const fetchVehiclePhoto = async (vin) => {
    try {
      const res = await fetch(
        `${getApiBase()}/api/auth/photos/${vin}`,
        {
          credentials: "include",
        }
      );
      const data = await res.json();

      if (data.photo) {
        setVehiclePhotos((prev) => ({
          ...prev,
          [vin]: data.photo,
        }));
      }
    } catch (err) {
      console.error("Failed to load vehicle image:", err);
    }
  };
  // ===== END NEW HANDLERS =====

  useEffect(() => {
    cars.forEach((car) => {
      if (car.vin && !(car.vin in vehiclePhotos)) {
        fetchVehiclePhoto(car.vin);
      }
    });
  }, [cars]);

  const submitReview = async () => {
    // Check if user can rate (is the service requester)
    const canRate = user?.id === selectedServiceDetail?.user_id;

    if (!canRate && !comment.trim()) {
      setToast({
        open: true,
        severity: "warning",
        msg: "Please add a comment.",
      });
      return;
    }

    if (canRate && !rating && !comment.trim()) {
      setToast({
        open: true,
        severity: "warning",
        msg: "Please add a rating or comment.",
      });
      return;
    }

    try {
      setSubmitting(true);

      // Submit rating if provided AND user is the service requester
      if (rating && canRate) {
        const ratingRes = await fetch(`${getApiBase()}/api/reviews`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            service_request_id: selectedServiceDetail.request_id,
            shop_id: selectedServiceDetail.shop_id,
            rating,
          }),
        });

        if (!ratingRes.ok) {
          const text = await ratingRes.text();
          throw new Error(`Failed to submit rating: ${text}`);
        }

        // Update local state with new rating
        const savedRating = await ratingRes.json();
        setSelectedServiceDetail((prev) =>
          prev ? { ...prev, review: { ...prev.review, rating: savedRating.rating } } : prev
        );
      }

      // Submit comment if provided
      if (comment.trim()) {
        const commentRes = await fetch(`${getApiBase()}/api/comments`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            service_request_id: selectedServiceDetail.request_id,
            comment: comment.trim(),
          }),
        });

        if (!commentRes.ok) {
          const text = await commentRes.text();
          throw new Error(`Failed to submit comment: ${text}`);
        }

        const newComment = await commentRes.json();
        // Add the new comment to the comments list
        setComments((prev) => [...prev, newComment]);
      }

      // Success message based on what was submitted
      let successMsg = "Submitted!";
      if (rating && canRate && comment.trim()) {
        successMsg = "Rating and comment submitted!";
      } else if (rating && canRate) {
        successMsg = "Rating updated!";
      } else if (comment.trim()) {
        successMsg = "Comment added!";
      }

      setToast({ open: true, severity: "success", msg: successMsg });
      setComment(""); // Clear comment field
    } catch (err) {
      setToast({ open: true, severity: "error", msg: err.message });
    } finally {
      setSubmitting(false);
    }
  };

  const deleteSearch = async (shopID) => {
    if (!window.confirm("Delete Shop? ")) return;

    try {
      const res = await fetch(`${getApiBase()}/api/visitedShops/${shopID}`,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        });

      if (res.ok) {
        console.log('Shop deleted');

        setVisitedShops((prev) => prev.filter((shop) => shop.autoshop_id !== shopID));
      }
    } catch (err) {
      console.error('Failure to delete: ', err);
    }
  }

  // Pre-fill rating and fetch comments when modal opens
  useEffect(() => {
    const fetchComments = async () => {
      if (!selectedServiceDetail?.request_id) return;

      setLoadingComments(true);
      try {
        const res = await fetch(
          `${getApiBase()}/api/comments/${selectedServiceDetail.request_id}`,
          { credentials: "include" }
        );

        if (res.ok) {
          const data = await res.json();
          setComments(data.comments || []);
        } else {
          console.error("Failed to fetch comments");
          setComments([]);
        }
      } catch (err) {
        console.error("Error fetching comments:", err);
        setComments([]);
      } finally {
        setLoadingComments(false);
      }
    };

    if (selectedServiceDetail?.review) {
      setRating(selectedServiceDetail.review.rating || 0);
    } else {
      setRating(0);
    }
    setComment(""); // Always start with empty comment field for new comments

    // Fetch conversation history
    fetchComments();

    // Fetch platform ratings when modal opens
    if (modalOpen && selectedServiceDetail) {
      const serviceRequestId = getServiceRequestId(selectedServiceDetail);
      const mechanicId = selectedServiceDetail.mechanics?.[0]?.id || null;
      fetchPlatformRatings(serviceRequestId, user?.id, mechanicId);
    }
  }, [selectedServiceDetail, modalOpen, user]);

  // Fetch available request types on mount
  useEffect(() => {
    const fetchAvailableTypes = async () => {
      try {
        const res = await fetch(`${getApiBase()}/api/serviceRequest/available-types`, {
          credentials: "include",
        });
        const data = await res.json();

        if (data.availableTypes) {
          setAvailableRequestTypes(data.availableTypes);

          // SMART DEFAULTS: Auto-select if only 1 option available
          if (data.count === 1) {
            // Find the only enabled type
            const enabledType = Object.keys(data.availableTypes).find(
              key => data.availableTypes[key] === true
            );
            setRequestType(enabledType || 'direct');
          }
        }

        setRequestTypesLoaded(true);
      } catch (err) {
        console.error("Failed to fetch available request types", err);
        // Fallback: Only direct requests available
        setAvailableRequestTypes({ direct: true, announcement: false, bidding: false });
        setRequestType('direct');
        setRequestTypesLoaded(true);
      }
    };
    fetchAvailableTypes();
  }, []);

  useEffect(() => {
    const loadAllData = async () => {
      setLoading(true);

      let lat = null;
      let lng = null;

      // 🌎 Try to get geolocation first (non-blocking if it fails)
      if (navigator.geolocation) {
        try {
          await new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(
              (position) => {
                lat = position.coords.latitude;
                lng = position.coords.longitude;
                setLatitude(lat);
                setLongitude(lng);
                resolve();
              },
              (error) => {
                console.warn("Geolocation not available:", error.message);
                resolve(); // Continue even if location fails
              }
            );
          });
        } catch (err) {
          console.warn("Geo fetch error:", err);
        }
      }

      try {
        // 🧠 1. Auth
        const now = Date.now();
        let userData = JSON.parse(localStorage.getItem("user"));
        if (!userData || now > userData.expiry) {
          localStorage.removeItem("user");
          const res = await fetch(`${getApiBase()}/api/auth/me`, {
            credentials: "include",
          });
          if (!res.ok) throw new Error("Unauthorized");

          const data = await res.json();
          const expiryMs = data.expiry * 1000;
          userData = { ...data.user, expiry: expiryMs };
          localStorage.setItem("user", JSON.stringify(userData));
        }

        // Redirect mechanics/owners to their proper profile page
        if (["Mechanic", "Owner"].includes(userData.role)) {
          navigate("/mechanic-profile");
          return;
        }

        setUser(userData);

        // 📦 2. Fetch all other data in parallel
        const [
          profileRes,
          historyRes,
          visitedRes,
          obd2Res,
          inProgressRes,
          shopsRes,
          servicesRes,
        ] = await Promise.allSettled([
          fetch(`${getApiBase()}/api/user/profile`, { credentials: "include" }),
          fetch(`${getApiBase()}/api/user/service-history`, {
            credentials: "include",
          }),
          fetch(`${getApiBase()}/api/visitedShops`, { credentials: "include" }),
          fetch(`${getApiBase()}/api/obdcodes/user/saved`, {
            credentials: "include",
          }),
          fetch(`${getApiBase()}/api/user/open-or-inprogress-requests`, {
            credentials: "include",
          }),
          fetch(
            `${getApiBase()}/api/autoshops/in-network?radius=${radius}&latitude=${lat}&longitude=${lng}`,
            {
              credentials: "include",
            }
          ),
          fetch(`${getApiBase()}/api/services/services`, {
            credentials: "include",
          }),
        ]);

        // ✅ Handle each one
        if (profileRes.status === "fulfilled") {
          const data = await profileRes.value.json();
          setUser(data.user);
          setEditedUser({
            first_name: data.user.first_name,
            last_name: data.user.last_name,
            email: data.user.email,
            phone_number: data.user.phone_number
              ? formatProfilePhone(data.user.phone_number)
              : "",
            street_address: data.address?.street_address || "",
            city: data.address?.city || "",
            state: data.address?.state || "",
            zip_code: data.address?.zip_code || "",
          });
          setZipCode(data.address?.zip_code);
          setUserZip(data.address?.zip_code || "");
          setAddress(data.address);
          if (data.address?.latitude != null && data.address?.longitude != null) {
            setLatitude(Number(data.address.latitude));
            setLongitude(Number(data.address.longitude));
          }
          setCars(data.cars);
          if (data.cars.length > 0) setSelectedCarId(data.cars[0].id);
          // Direct request: if we have profile ZIP but no coords yet, geocode and fetch in-network so "Select Autoshop" works
          const profileZip = (data.address?.zip_code && String(data.address.zip_code).trim()) || "";
          if (profileZip.length >= 5 && (lat == null || lng == null)) {
            const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
            if (apiKey) {
              try {
                const geoRes = await fetch(
                  `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(profileZip)}&key=${apiKey}`
                );
                const geoData = await geoRes.json();
                const loc = geoData.results?.[0]?.geometry?.location;
                if (loc) {
                  setLatitude(loc.lat);
                  setLongitude(loc.lng);
                  const shopRes = await fetch(
                    `${getApiBase()}/api/autoshops/in-network?radius=${radius}&latitude=${loc.lat}&longitude=${loc.lng}`,
                    { credentials: "include" }
                  );
                  const shopData = await shopRes.json();
                  if (Array.isArray(shopData)) setAutoshops(shopData);
                }
              } catch (e) {
                console.warn("Profile zip geocode or in-network fetch failed:", e);
              }
            }
          }
        }

        if (historyRes.status === "fulfilled") {
          const data = await historyRes.value.json();
          const grouped = data.serviceHistory.reduce((acc, row) => {
            const entry = {
              ...row, // keep all existing fields
              review: row.review_id
                ? {
                  id: row.review_id,
                  rating: row.review_rating,
                  comment: row.review_comment,
                  reviewed_at: row.review_updated_at,
                }
                : null,
            };
            if (!acc[row.car_id]) acc[row.car_id] = [];
            acc[row.car_id].push(entry);
            return acc;
          }, {});
          setServiceHistoryByCar(grouped);
        }

        if (visitedRes.status === "fulfilled") {
          const data = await visitedRes.value.json();
          setVisitedShops(data);
        }

        if (obd2Res.status === "fulfilled") {
          const data = await obd2Res.value.json();
          const grouped = {};
          data.savedCodes.forEach((entry) => {
            if (!grouped[entry.car_id]) grouped[entry.car_id] = [];
            grouped[entry.car_id].push(entry);
          });
          setObd2HistoryByCar(grouped);
        }

        if (inProgressRes.status === "fulfilled") {
          const data = await inProgressRes.value.json();
          const services = data.requests || [];

          // Fetch shop ratings for all services
          const ratings = await fetchShopRatings(services);
          setShopRatings(ratings);

          // Merge ratings into services
          const servicesWithRatings = services.map(service => ({
            ...service,
            shop_rating: ratings[service.shop_id]?.avg_rating,
            shop_review_count: ratings[service.shop_id]?.review_count,
            place_id: service.place_id, // Ensure place_id is included
          }));

          setServicesInProgress(servicesWithRatings);
          console.log("Fetched in-progress services with ratings:", servicesWithRatings);

          // Check for new messages
          checkForNewMessages(servicesWithRatings);
        }

        if (shopsRes.status === "fulfilled") {
          const data = await shopsRes.value.json();
          if (Array.isArray(data) && data.length !== 0) {
            setAutoshops(data);
          } else {
            setAutoshops([]); // Ensure it's still an array
          }
        }

        if (servicesRes.status === "fulfilled") {
          const data = await servicesRes.value.json();
          setServices(uniqueServicesByName(data));
        }
      } catch (err) {
        console.error("Error loading user data:", err);
        navigate("/login");
      } finally {
        setLoading(false);
      }
    };

    loadAllData();
  }, [radius]);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const res = await fetch(`${getApiBase()}/api/services/services`, {
          method: "GET",
          credentials: "include",
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || "Failed to fetch services.");
        setServices(uniqueServicesByName(data));
      } catch (err) {
        console.error("Error loading services:", err);
      }
    };
    fetchServices();
  }, []);

  useEffect(() => {
    if (latitude && longitude) {
      fetchAutoshops();
    }
  }, [latitude, longitude, newRadius]);

  // When profile/default ZIP changes, re-geocode so nearby shops match the new ZIP
  // (do not keep stale lat/lng from a previous ZIP).
  useEffect(() => {
    if (!zipCode || String(zipCode).trim().length < 5) return;
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    if (!apiKey) return;
    let cancelled = false;
    fetch(
      `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(String(zipCode).trim())}&key=${apiKey}`
    )
      .then((res) => res.json())
      .then((data) => {
        if (cancelled) return;
        const location = data.results?.[0]?.geometry?.location;
        if (location) {
          setLatitude(location.lat);
          setLongitude(location.lng);
        }
      })
      .catch((err) => console.warn("Profile zip geocode for direct request:", err));
    return () => { cancelled = true; };
  }, [zipCode]);

  const fetchServicesInProgress = async () => {
    try {
      const res = await fetch(
        `${getApiBase()}/api/user/open-or-inprogress-requests`,
        {
          method: "GET",
          credentials: "include",
        }
      );
      const data = await res.json();

      if (!res.ok)
        throw new Error(data.message || "Failed to load service requests");
      setServicesInProgress(data.requests || []);
    } catch (err) {
      console.error("Error loading services in progress:", err);
    }
  };

  const handleRetryLocation = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        alert("Geolocation is not supported by your browser.");
        return reject("Geolocation not supported");
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude: lat, longitude: lng } = position.coords;
          setLatitude(lat);
          setLongitude(lng);
          resolve({ lat, lng });
        },
        (error) => {
          if (error.code === error.PERMISSION_DENIED) {
            alert(
              "Location access denied. Please enable it in browser settings."
            );
          } else {
            alert("Failed to get location. Try again.");
          }
          reject(error);
        }
      );
    });
  };

  const submitServiceRequest = async () => {
    // Prevent duplicate submissions
    if (isSubmittingServiceRequest) {
      return;
    }

    if (
      !selectedCarId ||
      selectedServices.length === 0 ||
      availableDates.length === 0
    ) {
      alert(
        "Please select a car, at least one service, your location is on, and up to 3 dates."
      );
      return;
    }

    if (requestType === "direct" && (!selectedAutoshop || String(selectedAutoshop).trim() === "")) {
      alert("Please select an autoshop for your direct service request.");
      return;
    }

    // If location is missing, prompt the user
    if (longitude === null || latitude === null) {
      try {
        const { lat, lng } = await handleRetryLocation();
        // Use the updated lat/lng for the request
        return submitServiceRequest(); // Recursively call with updated location
      } catch (err) {
        return; // Stop if location still not allowed
      }
    }

    setIsSubmittingServiceRequest(true);
    fetchAutoshops();

    const autoshopId = isNaN(Number(selectedAutoshop))
      ? null
      : Number(selectedAutoshop);

    try {
      const res = await fetch(
        `${getApiBase()}/api/serviceRequest/service-request`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({
            car_id: selectedCarId,
            selectedAutoshop: autoshopId || null,
            serviceIds: selectedServices,
            description: serviceDescription,
            availableDates,
            longitude: longitude,
            latitude: latitude,
            request_type: requestType,
            dropoff_week_start: dropoffWeekStart || null,
            dropoff_days: dropoffAvailableDays,
          }),
        }
      );

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || "Failed to submit service request.");
      }

      alert("Service request submitted successfully!");
      setSelectedAutoshop("");
      setSelectedServices([]);
      setServiceDescription("");
      setAvailableDates([]);
      setDropoffWeekStart(null);
      setDropoffAvailableDays([]);
    } catch (err) {
      console.error("submitServiceRequest error:", err);
      alert("Error submitting service request.");
    } finally {
      setIsSubmittingServiceRequest(false);
    }
  };

  const fetchAutoshops = async () => {
    try {
      const res = await fetch(
        `${getApiBase()}/api/autoshops/in-network?radius=${newRadius}&longitude=${longitude}&latitude=${latitude}`,
        {
          method: "GET",
          credentials: "include",
        }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to fetch autoshops.");
      setAutoshops(data);
    } catch (err) {
      console.error("Error loading autoshops:", err);
    }
  };

  const handleAddDropdownCar = async () => {
    if (!formData.carYear || !formData.carMake || !formData.carModel) {
      return alert("Please select Year, Make, Model at minimum.");
    }

    try {
      const res = await fetch(`${getApiBase()}/api/user/add-car`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          year: formData.carYear,
          make: formData.carMake,
          model: formData.carModel,
          trim: formData.carTrim || null,
          engine_size: formData.engineSize || null,
          mileage: formData.mileage || null,
          vin: newVin || null, // optional if VIN provided
        }),
      });

      const data = await res.json();
      if (res.ok) {
        alert("Car added!");
        setCars((prev) => [...prev, data]);
        setAddingCar(false);
        setFormData({
          carYear: "",
          carMake: "",
          carModel: "",
          carTrim: "",
          engineSize: "",
          mileage: "",
        });
        setNewVin("");
        setDecodedCar(null);
      } else {
        alert(data.error || "Failed to add car");
      }
    } catch (err) {
      console.error("Add car error:", err);
      alert("Something went wrong.");
    }
  };

  const confirmServiceRequest = async ({
    requestId,
    confirm_date = null,
    estimate_price,
    proposed_start_time = null,
    autoshop_confirm_date = null,
    counter_available_date = [],
    shopId,
  }) => {
    try {
      const response = await fetch(
        `${getApiBase()}/api/serviceRequest/confirm`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include", // required for cookie-based auth
          body: JSON.stringify({
            requestId,
            confirm_date,
            estimate_price,
            proposed_start_time,
            autoshop_confirm_date,
            counter_available_date,
            shopId,
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to confirm service request");
      }

      alert(data.message);
      fetchServicesInProgress();

      return data;
    } catch (error) {
      console.error("❌ Error confirming service request:", error.message);
      throw error;
    }
  };

  const handleUserCancelServiceRequest = async (requestId, shopId) => {
    try {
      const response = await fetch(
        `${getApiBase()}/api/serviceRequest/cancel/${requestId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include", // required for cookie-based auth
          body: JSON.stringify({
            reason: "User unavailable for proposed service times",
            shopId: shopId,
            mechanicId: null,
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to cancel service request");
      }

      alert("Service cancelled successfully:", data.message);
      fetchServicesInProgress();
    } catch (err) {
      console.error("Error cancelling service request:", err.message);
      // Optionally show error to user
    }
  };

  // Cancel a service request that is still Open (Awaiting Quotes) — no shop assigned yet
  const handleCancelRequestAwaitingQuotes = async (requestId) => {
    if (!window.confirm("Cancel this request? You will no longer receive quotes for it.")) return;
    try {
      const response = await fetch(
        `${getApiBase()}/api/serviceRequest/cancel-request/${requestId}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ reason: "Cancelled by customer while awaiting quotes" }),
        }
      );
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || "Failed to cancel request");
      alert("Request cancelled.");
      fetchServicesInProgress();
    } catch (err) {
      console.error("Error cancelling request:", err.message);
      alert(err.message || "Failed to cancel request.");
    }
  };

  const handleAddDecodedCar = async () => {
    if (!user) return;

    const payload = {
      vin: manualEntry ? null : newVin,
      mileage: newMileage,
      make: manualEntry ? manualCarData.make : decodedCar?.make,
      model: manualEntry ? manualCarData.model : decodedCar?.model,
      year: manualEntry ? manualCarData.year : decodedCar?.year,
      trim: manualEntry ? manualCarData.trim : decodedCar?.trim || null,
      engine_size: manualEntry
        ? manualCarData.engine
        : decodedCar?.engine || null,
    };

    if (!payload.make || !payload.model || !payload.year) {
      return alert("Please complete all required vehicle fields.");
    }

    try {
      const res = await fetch(`${getApiBase()}/api/user/add-car`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      if (res.ok) {
        alert("Car added successfully!");
        setCars((prev) => [...prev, data]);
        setAddingCar(false);
        setNewVin("");
        setNewMileage("");
        setDecodedCar(null);
        setManualCarData({
          make: "",
          model: "",
          year: "",
          trim: "",
          engine: "",
        });
      } else {
        alert(data.error || "Failed to add car.");
      }
    } catch (err) {
      console.error("Error saving new car:", err);
      alert("Something went wrong.");
    }
  };

  const handleUpdateCar = async (carId) => {
    /*console.log("Sending car update:", {
      carId,
      vin: editedCar.vin,
      mileage: editedCar.mileage,
    });*/

    // Validate mileage before sending
    const mileageNum = Number(editedCar.mileage);
    if (editedCar.mileage !== "" && (isNaN(mileageNum) || mileageNum < 0 || mileageNum > 9999999)) {
      alert("Please enter a valid mileage between 0 and 9,999,999 miles.");
      return;
    }

    try {
      const res = await fetch(`${getApiBase()}/api/user/update-car`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          carId,
          vin: editedCar.vin,
          mileage: editedCar.mileage === "" ? null : mileageNum,
        }),
      });

      const data = await res.json();

      if (res.ok) {
        const updated = cars.map((c) =>
          c.id === carId ? { ...c, ...editedCar } : c
        );
        setCars(updated);
        if (editedCar.vin) {
          fetchVehiclePhoto(editedCar.vin);
        }
        setEditingCarId(null);
        alert("Car updated!");
      } else {
        alert(data.message || "Failed to update car.");
      }
    } catch (err) {
      console.error("Update error:", err);
      alert("Error updating car.");
    }
  };

  const handleDeleteCar = async (carId) => {
    if (!window.confirm("Are you sure you want to delete this car?")) return;

    try {
      const res = await fetch(`${getApiBase()}/api/user/delete-car`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({ carId }),
      });

      const data = await res.json();

      if (res.ok) {
        setCars((prev) => prev.filter((car) => car.id !== carId));
        alert("Car deleted!");
      } else {
        alert(data.message || "Failed to delete car.");
      }
    } catch (err) {
      console.error("Delete error:", err);
      alert("Error deleting car.");
    }
  };

  // Validation helper functions
  const validateName = (name, value) => {
    if (!value.trim()) {
      return `${name} is required`;
    }
    if (value.length > 50) {
      return `${name} must be 50 characters or less`;
    }
    // Allow letters, spaces, hyphens, apostrophes
    if (!/^[a-zA-Z\s'-]+$/.test(value)) {
      return `${name} can only contain letters, spaces, hyphens, and apostrophes`;
    }
    return "";
  };

  const formatProfilePhone = (phone) => {
    const digits = String(phone || "").replace(/\D/g, "");
    if (digits.length === 10) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    if (digits.length === 11 && digits.startsWith("1")) {
      return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
    }
    return phone || "";
  };

  const validatePhone = (value) => {
    const digits = String(value || "").replace(/\D/g, "");
    if (!digits) return "Phone number is required";
    if (digits.length === 10 || (digits.length === 11 && digits.startsWith("1"))) return "";
    return "Please enter a valid phone number";
  };

  const validateStreetAddress = (value) => {
    if (!value.trim()) {
      return "Street address is required";
    }
    if (value.length > 100) {
      return "Street address must be 100 characters or less";
    }
    // Allow alphanumeric, spaces, and common address characters (#, -, ., ,)
    if (!/^[a-zA-Z0-9\s#\-.,]+$/.test(value)) {
      return "Street address contains invalid characters";
    }
    return "";
  };

  const validateCity = (value) => {
    if (!value.trim()) {
      return "City is required";
    }
    if (value.length > 50) {
      return "City must be 50 characters or less";
    }
    // Allow letters, spaces, hyphens
    if (!/^[a-zA-Z\s-]+$/.test(value)) {
      return "City can only contain letters, spaces, and hyphens";
    }
    return "";
  };

  const validateZipCode = (value) => {
    if (!value.trim()) {
      return "Zip code is required";
    }
    // Allow 5 digits or 5+4 format (12345 or 12345-6789)
    if (!/^\d{5}(-\d{4})?$/.test(value)) {
      return "Zip code must be 5 digits (e.g., 12345) or 5+4 format (e.g., 12345-6789)";
    }
    return "";
  };

  const handleUserUpdate = async () => {
    // Validate user fields
    const newUserErrors = {
      first_name: validateName("First name", editedUser.first_name),
      last_name: validateName("Last name", editedUser.last_name),
      phone_number: validatePhone(editedUser.phone_number),
    };

    // Validate address fields
    const newAddressErrors = {
      street_address: validateStreetAddress(address?.street_address || ""),
      city: validateCity(address?.city || ""),
      state: address?.state?.trim() ? "" : "State is required",
      zip_code: validateZipCode(address?.zip_code || ""),
    };

    setUserFieldErrors(newUserErrors);
    setAddressErrors(newAddressErrors);

    // Check if there are any errors
    const hasUserErrors = Object.values(newUserErrors).some((error) => error !== "");
    const hasAddressErrors = Object.values(newAddressErrors).some((error) => error !== "");

    if (hasUserErrors || hasAddressErrors) {
      return;
    }

    try {
      const res = await fetch(`${getApiBase()}/api/user/update`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          ...editedUser,
          street_address: address.street_address,
          city: address.city,
          state: address.state,
          zip_code: address.zip_code,
        }),
      });

      const data = await res.json();

      if (!res.ok) throw new Error(data.message || "Failed to update user");

      setUser({
        ...user,
        ...editedUser,
        phone_number: data.phone_number || editedUser.phone_number,
      });
      setIsEditingUser(false);

      // TASK-589: keep broadcast default zip + map coords in sync with saved address
      const savedZip = data.address?.zip_code ?? address?.zip_code ?? "";
      const savedLat = data.address?.latitude;
      const savedLng = data.address?.longitude;
      setZipCode(savedZip);
      setUserZip(savedZip);
      setEditedUser((prev) => ({
        ...prev,
        street_address: data.address?.street_address ?? address?.street_address ?? "",
        city: data.address?.city ?? address?.city ?? "",
        state: data.address?.state ?? address?.state ?? "",
        zip_code: savedZip,
      }));
      setAddress((prev) => ({
        ...(prev || {}),
        street_address: data.address?.street_address ?? address?.street_address ?? "",
        city: data.address?.city ?? address?.city ?? "",
        state: data.address?.state ?? address?.state ?? "",
        zip_code: savedZip,
        latitude: savedLat ?? null,
        longitude: savedLng ?? null,
      }));
      if (savedLat != null && savedLng != null) {
        setLatitude(Number(savedLat));
        setLongitude(Number(savedLng));
      } else {
        setLatitude(null);
        setLongitude(null);
      }

      // Clear all errors on successful save
      setAddressErrors({
        street_address: "",
        city: "",
        state: "",
        zip_code: "",
      });
      setUserFieldErrors({
        first_name: "",
        last_name: "",
      });
      alert("User info updated successfully!");
    } catch (err) {
      console.error("Update error:", err);
      alert("Update failed. Try again.");
    }
  };

  // Handle email change request
  const handleEmailChangeRequest = async () => {
    if (!newEmail.trim()) {
      setToast({
        open: true,
        severity: "error",
        msg: "Please enter a new email address",
      });
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newEmail)) {
      setToast({
        open: true,
        severity: "error",
        msg: "Please enter a valid email address",
      });
      return;
    }

    if (newEmail.toLowerCase() === user.email.toLowerCase()) {
      setToast({
        open: true,
        severity: "error",
        msg: "New email must be different from current email",
      });
      return;
    }

    setEmailChangeLoading(true);

    try {
      const response = await fetch(`${getApiBase()}/api/auth/request-email-change`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({ new_email: newEmail }),
      });

      const data = await response.json();

      if (response.ok) {
        setToast({
          open: true,
          severity: "success",
          msg: "Verification email sent! Please check your new email address (including spam/junk folder).",
        });
        setEmailChangeModalOpen(false);
        setNewEmail("");
      } else {
        setToast({
          open: true,
          severity: "error",
          msg: data.error || "Failed to request email change",
        });
      }
    } catch (error) {
      console.error("Email change request error:", error);
      setToast({
        open: true,
        severity: "error",
        msg: "Failed to request email change. Please try again.",
      });
    } finally {
      setEmailChangeLoading(false);
    }
  };

  const handleAcceptBid = async (
    requestId,
    bidId,
    confirm_date,
    proposed_start_time
  ) => {
    try {
      const res = await fetch(`${getApiBase()}/api/bids/accept`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          requestId,
          bidId,
          confirm_date,
          proposed_start_time,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || "Failed to accept bid.");
      }

      alert("Bid accepted successfully!");
      fetchServicesInProgress();
    } catch (err) {
      console.error("Accept bid error:", err);
      alert("Failed to accept bid.");
    }
  };

  const handleAcceptAnnouncementQuote = async (bidId, selectedSlotData) => {
    try {
      const slotId = selectedSlotData?.slotData?.id != null ? selectedSlotData.slotData.id : selectedSlotData?.slotIndex;
      const payload = {
        bidId,
        customer_selected_slot_id: slotId,
        is_dropoff: selectedSlotData?.isDropoff || false
      };

      const res = await fetch(`${getApiBase()}/api/bids/customer-accept`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to accept quote.");

      alert("✅ Quote accepted! The shop will confirm your appointment shortly.");
      fetchServicesInProgress();
    } catch (err) {
      console.error("Accept announcement quote error:", err);
      alert(`Failed to accept quote: ${err.message}`);
    }
  };

  const handleDeclineBid = async (requestId, bidId) => {
    try {
      const res = await fetch(`${getApiBase()}/api/bids/decline`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({ requestId, bidId }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || "Failed to decline bid.");
      }

      alert("Bid declined successfully!");
      fetchServicesInProgress();
    } catch (err) {
      console.error("Decline bid error:", err);
      alert("Failed to decline bid.");
    }
  };

  const onZipSubmit = async (zipOverride) => {
    try {
      const zip = String(zipOverride || userZip || zipCode || "").trim();
      if (zip.length < 5) {
        alert("Invalid ZIP code. Please try again.");
        return;
      }
      const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
      const res = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(zip)}&key=${apiKey}`
      );
      const data = await res.json();
      const location = data.results?.[0]?.geometry?.location;

      if (location) {
        setUserZip(zip);
        setLatitude(location.lat);
        setLongitude(location.lng);
      } else {
        alert("Invalid ZIP code. Please try again.");
      }
    } catch (err) {
      console.error("Error fetching location from ZIP:", err);
      alert("There was an error. Try again.");
    }
  };

  /**
   * Submits the VIN and fetches decoded vehicle information from the NHTSA API.
   * Also sets mileage from user input.
   */
  const handleVinSubmit = async () => {
    if (!vin) return;
    const res = await fetch(
      `https://vpic.nhtsa.dot.gov/api/vehicles/decodevinvaluesextended/${vin}?format=json`
    );
    const data = await res.json();
    if (data?.Results?.length > 0) {
      const vehicle = data.Results[0];
      setDecodedInfo({
        make: vehicle.Make,
        model: vehicle.Model,
        year: vehicle.ModelYear,
        mileage: mileage,
      });
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
        }}
      >
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading your profile...</Typography>
      </Box>
    );
  }

  return (
    <div>
      <Header />
      <ThemeProvider theme={theme}>
        <Box
          sx={{
            minHeight: "100vh",
            py: 5,
          }}
        >
          {" "}
          <Container>
            {/* Top Header with Toggle and Logout */}
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={3}
              sx={{
                mb: 4,
                p: 2,
                borderRadius: 2,
                boxShadow: 3,
                backgroundColor: "#e63926",
                flexDirection: "column",
              }}
            >
              {user && (
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 800,
                    color: "#fff",
                    mb: 2,
                    mt: 2,
                    //textShadow: "2px 2px 6px rgba(0, 0, 0, 0.8)",
                  }}
                >
                  Hello, {user.first_name}!
                </Typography>
              )}
            </Box>

            {/* Main 2 Column Layout */}
            <Grid
              container
              sx={{
                justifyContent: {
                  xs: "center",
                  md: "space-between",
                },
                mb: 4,
                flexDirection: {
                  sm: "column",
                  md: "row",
                },
              }}
            >
              {/* LEFT COLUMN */}
              <Grid
                item
                size={{ xs: 12, md: 6 }}
                mb={{ xs: 4, sm: 0 }}
                sx={{ minHeight: 1 / 1 }}
              >
                {/* User Info */}
                <Slide direction="up" in mountOnEnter unmountOnExit>
                  <Card
                    sx={{
                      mb: 4,
                      p: 2,
                      borderRadius: 2,
                      boxShadow: 3,
                      backgroundColor: "#fff",
                    }}
                  >
                    <CardContent>
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{
                          mb: 4,
                          p: 2,
                          borderRadius: 2,
                          boxShadow: 3,
                          backgroundColor: "#e63926",
                        }}
                      >
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 600,
                            color: "#fff",
                          }}
                        >
                          <InfoOutlined
                            sx={{
                              color: "#fff",
                              mr: 1,
                            }}
                          />
                          User Information
                        </Typography>
                        {!isEditingUser && (
                          <IconButton
                            sx={{
                              color: "#fff",
                              borderRadius: 2,
                              boxShadow: 3,
                              backgroundColor: "#4285f4",
                            }}
                            size="small"
                            onClick={() => {
                              setIsEditingUser(true);
                            }}
                          >
                            <EditIcon />
                          </IconButton>
                        )}
                      </Box>
                      {user && (
                        <Box display="flex" mt={2}>
                          <Avatar
                            src={`https://ui-avatars.com/api/?name=${user.first_name}+${user.last_name}`}
                            sx={{ width: 64, height: 64, mr: 2 }}
                          />
                          <Box>
                            {isEditingUser ? (
                              <>
                                <TextField
                                  label="First Name"
                                  value={editedUser.first_name}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    // Only allow letters, spaces, hyphens, and apostrophes
                                    if (value === "" || /^[a-zA-Z\s'-]*$/.test(value)) {
                                      setEditedUser({
                                        ...editedUser,
                                        first_name: value,
                                      });
                                      // Clear error when user starts typing
                                      if (userFieldErrors.first_name) {
                                        setUserFieldErrors({
                                          ...userFieldErrors,
                                          first_name: "",
                                        });
                                      }
                                    }
                                  }}
                                  onInput={(e) => {
                                    // Enforce max length
                                    if (e.target.value.length > 50) {
                                      e.target.value = e.target.value.slice(0, 50);
                                    }
                                  }}
                                  fullWidth
                                  required
                                  maxLength={50}
                                  error={!!userFieldErrors.first_name}
                                  helperText={userFieldErrors.first_name || "Max 50 characters"}
                                  sx={{ mb: 2 }}
                                  InputProps={{ sx: { fontSize: "1.1rem" } }}
                                  InputLabelProps={{
                                    sx: {
                                      fontSize: "1.1rem",
                                      "& .MuiFormLabel-asterisk": {
                                        color: "error.main"
                                      }
                                    },
                                    required: true
                                  }}
                                />
                                <TextField
                                  label="Last Name"
                                  value={editedUser.last_name}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    // Only allow letters, spaces, hyphens, and apostrophes
                                    if (value === "" || /^[a-zA-Z\s'-]*$/.test(value)) {
                                      setEditedUser({
                                        ...editedUser,
                                        last_name: value,
                                      });
                                      // Clear error when user starts typing
                                      if (userFieldErrors.last_name) {
                                        setUserFieldErrors({
                                          ...userFieldErrors,
                                          last_name: "",
                                        });
                                      }
                                    }
                                  }}
                                  onInput={(e) => {
                                    // Enforce max length
                                    if (e.target.value.length > 50) {
                                      e.target.value = e.target.value.slice(0, 50);
                                    }
                                  }}
                                  fullWidth
                                  required
                                  maxLength={50}
                                  error={!!userFieldErrors.last_name}
                                  helperText={userFieldErrors.last_name || "Max 50 characters"}
                                  sx={{ mb: 2 }}
                                  InputProps={{ sx: { fontSize: "1.1rem" } }}
                                  InputLabelProps={{
                                    sx: {
                                      fontSize: "1.1rem",
                                      "& .MuiFormLabel-asterisk": {
                                        color: "error.main"
                                      }
                                    },
                                    required: true
                                  }}
                                />
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                                  <TextField
                                    label="Email"
                                    value={editedUser.email}
                                    fullWidth
                                    disabled
                                    helperText="Use the email icon to change your email"
                                    InputProps={{ sx: { fontSize: "1.1rem" } }}
                                    InputLabelProps={{
                                      sx: { fontSize: "1.1rem" },
                                    }}
                                  />
                                  <Tooltip title="Change Email">
                                    <IconButton
                                      size="small"
                                      onClick={() => setEmailChangeModalOpen(true)}
                                      sx={{ mt: -2 }}
                                    >
                                      <EmailIcon />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                                <TextField
                                  label="Phone Number"
                                  value={editedUser.phone_number}
                                  onChange={(e) => {
                                    setEditedUser({
                                      ...editedUser,
                                      phone_number: e.target.value,
                                    });
                                    if (userFieldErrors.phone_number) {
                                      setUserFieldErrors({
                                        ...userFieldErrors,
                                        phone_number: "",
                                      });
                                    }
                                  }}
                                  fullWidth
                                  required
                                  error={!!userFieldErrors.phone_number}
                                  helperText={userFieldErrors.phone_number || "The number on this account"}
                                  sx={{ mb: 2 }}
                                  InputProps={{ sx: { fontSize: "1.1rem" } }}
                                  InputLabelProps={{
                                    sx: {
                                      fontSize: "1.1rem",
                                      "& .MuiFormLabel-asterisk": { color: "error.main" },
                                    },
                                    required: true,
                                  }}
                                />
                                <TextField
                                  label="Street Address"
                                  value={address?.street_address || ""}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    // Allow alphanumeric, spaces, and common address characters
                                    if (value === "" || /^[a-zA-Z0-9\s#\-.,]*$/.test(value)) {
                                      setAddress({
                                        ...address,
                                        street_address: value,
                                      });
                                      // Clear error when user starts typing
                                      if (addressErrors.street_address) {
                                        setAddressErrors({
                                          ...addressErrors,
                                          street_address: "",
                                        });
                                      }
                                    }
                                  }}
                                  onInput={(e) => {
                                    // Enforce max length
                                    if (e.target.value.length > 100) {
                                      e.target.value = e.target.value.slice(0, 100);
                                    }
                                  }}
                                  fullWidth
                                  required
                                  maxLength={100}
                                  error={!!addressErrors.street_address}
                                  helperText={addressErrors.street_address || "Max 100 characters"}
                                  sx={{ mb: 2 }}
                                  InputProps={{ sx: { fontSize: "1.1rem" } }}
                                  InputLabelProps={{
                                    sx: {
                                      fontSize: "1.1rem",
                                      "& .MuiFormLabel-asterisk": {
                                        color: "error.main"
                                      }
                                    },
                                    required: true
                                  }}
                                />
                                <TextField
                                  label="City"
                                  value={address?.city || ""}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    // Only allow letters, spaces, and hyphens
                                    if (value === "" || /^[a-zA-Z\s-]*$/.test(value)) {
                                      setAddress({
                                        ...address,
                                        city: value,
                                      });
                                      // Clear error when user starts typing
                                      if (addressErrors.city) {
                                        setAddressErrors({
                                          ...addressErrors,
                                          city: "",
                                        });
                                      }
                                    }
                                  }}
                                  onInput={(e) => {
                                    // Enforce max length
                                    if (e.target.value.length > 50) {
                                      e.target.value = e.target.value.slice(0, 50);
                                    }
                                  }}
                                  fullWidth
                                  required
                                  maxLength={50}
                                  error={!!addressErrors.city}
                                  helperText={addressErrors.city || "Max 50 characters"}
                                  sx={{ mb: 2 }}
                                  InputProps={{ sx: { fontSize: "1.1rem" } }}
                                  InputLabelProps={{
                                    sx: {
                                      fontSize: "1.1rem",
                                      "& .MuiFormLabel-asterisk": {
                                        color: "error.main"
                                      }
                                    },
                                    required: true
                                  }}
                                />
                                <TextField
                                  select
                                  label="State"
                                  value={address?.state || ""}
                                  onChange={(e) => {
                                    setAddress({
                                      ...address,
                                      state: e.target.value,
                                    });
                                    // Clear error when user starts typing
                                    if (addressErrors.state) {
                                      setAddressErrors({
                                        ...addressErrors,
                                        state: "",
                                      });
                                    }
                                  }}
                                  fullWidth
                                  required
                                  error={!!addressErrors.state}
                                  helperText={addressErrors.state}
                                  sx={{ mb: 2 }}
                                  InputProps={{ sx: { fontSize: "1.1rem" } }}
                                  InputLabelProps={{
                                    sx: {
                                      fontSize: "1.1rem",
                                      "& .MuiFormLabel-asterisk": {
                                        color: "error.main"
                                      }
                                    },
                                    required: true
                                  }}
                                >
                                  <MenuItem value="">Select State</MenuItem>
                                  {usStates.map((state) => (
                                    <MenuItem
                                      key={state.code}
                                      value={state.code}
                                    >
                                      {state.name}
                                    </MenuItem>
                                  ))}
                                </TextField>
                                <TextField
                                  label="Zip Code"
                                  value={address?.zip_code || ""}
                                  onChange={(e) => {
                                    let value = e.target.value;
                                    // Remove any non-digit and non-hyphen characters
                                    value = value.replace(/[^\d-]/g, '');

                                    // Only allow one hyphen, and only after 5 digits
                                    const parts = value.split('-');
                                    if (parts.length > 2) {
                                      // Multiple hyphens, keep only first two parts
                                      value = parts[0] + '-' + parts.slice(1).join('');
                                    }

                                    // Limit first part to 5 digits
                                    if (parts[0] && parts[0].length > 5) {
                                      value = parts[0].slice(0, 5) + (parts[1] ? '-' + parts[1] : '');
                                    }

                                    // Limit second part to 4 digits
                                    if (parts[1] && parts[1].length > 4) {
                                      value = parts[0] + '-' + parts[1].slice(0, 4);
                                    }

                                    // Limit total length to 10 (5+4 format)
                                    if (value.length > 10) {
                                      value = value.slice(0, 10);
                                    }

                                    setAddress({
                                      ...address,
                                      zip_code: value,
                                    });
                                    // Clear error when user starts typing
                                    if (addressErrors.zip_code) {
                                      setAddressErrors({
                                        ...addressErrors,
                                        zip_code: "",
                                      });
                                    }
                                  }}
                                  fullWidth
                                  required
                                  maxLength={10}
                                  error={!!addressErrors.zip_code}
                                  helperText={addressErrors.zip_code || "5 digits (e.g., 12345) or 5+4 format (e.g., 12345-6789)"}
                                  InputProps={{ sx: { fontSize: "1.1rem" } }}
                                  InputLabelProps={{
                                    sx: {
                                      fontSize: "1.1rem",
                                      "& .MuiFormLabel-asterisk": {
                                        color: "error.main"
                                      }
                                    },
                                    required: true
                                  }}
                                />

                                {/* Save/Cancel buttons at bottom of form */}
                                <Box sx={{ display: "flex", gap: 2, mt: 3 }}>
                                  <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={handleUserUpdate}
                                    fullWidth
                                  >
                                    Save Changes
                                  </Button>
                                  <Button
                                    variant="outlined"
                                    onClick={() => {
                                      setIsEditingUser(false);
                                      // Clear all errors when canceling
                                      setAddressErrors({
                                        street_address: "",
                                        city: "",
                                        state: "",
                                        zip_code: "",
                                      });
                                      setUserFieldErrors({
                                        first_name: "",
                                        last_name: "",
                                        phone_number: "",
                                      });
                                      setEditedUser((prev) => ({
                                        ...prev,
                                        phone_number: user?.phone_number
                                          ? formatProfilePhone(user.phone_number)
                                          : "",
                                      }));
                                    }}
                                    fullWidth
                                    sx={{
                                      color: "#dc655aff",
                                      borderColor: "#dc655aff",
                                      "&:hover": {
                                        borderColor: "#dc655aff",
                                        backgroundColor: "rgba(220, 101, 90, 0.1)",
                                      },
                                    }}
                                  >
                                    Cancel
                                  </Button>
                                </Box>
                              </>
                            ) : (
                              <>
                                <Typography sx={{ fontSize: "1.1rem" }}>
                                  <strong>Name: </strong> {user.first_name}{" "}
                                  {user.last_name}
                                </Typography>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                  <Typography sx={{ fontSize: "1.1rem" }}>
                                    <strong>Email: </strong> {user.email}
                                  </Typography>
                                  <Tooltip title="Change Email">
                                    <IconButton
                                      size="small"
                                      onClick={() => setEmailChangeModalOpen(true)}
                                      sx={{ ml: 0.5 }}
                                    >
                                      <EmailIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                                <Typography sx={{ fontSize: "1.1rem" }}>
                                  <strong>Phone: </strong>{" "}
                                  {user.phone_number
                                    ? formatProfilePhone(user.phone_number)
                                    : "Not on file"}
                                </Typography>
                              </>
                            )}

                            {!isEditingUser && address && (
                              <>
                                <Typography sx={{ fontSize: "1.1rem" }}>
                                  <strong>Address: </strong>{" "}
                                  {address.street_address}
                                </Typography>
                                <Typography sx={{ fontSize: "1.1rem" }}>
                                  {address.city}, {address.state}{" "}
                                  {address.zip_code}
                                </Typography>
                              </>
                            )}
                          </Box>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Slide>

                {/* My Vehicles */}
                <Slide direction="up" in mountOnEnter unmountOnExit>
                  <Card
                    sx={{
                      p: 2,
                      mb: 4,
                      borderRadius: 2,
                      boxShadow: 3,
                      backgroundColor: "#fff",
                    }}
                  >
                    <CardContent>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          mb: 4,
                          p: 2,
                          borderRadius: 2,
                          boxShadow: 3,
                          backgroundColor: "#e63926",
                          color: "#fff",
                        }}
                      >
                        <DirectionsCarFilled
                          sx={{
                            mr: 1,
                          }}
                        />
                        My Vehicles
                      </Typography>

                      <Grid container spacing={2} mt={1} direction="column">
                        <Box
                          sx={{
                            maxHeight: "40vh",
                            overflowY: "auto",
                          }}
                        >
                          {cars.map((car) => (
                            <Grid item xs={12} spacing={2} mt={1} key={car.id}>
                              <Card
                                sx={{
                                  p: 2,
                                  backgroundColor: darkMode
                                    ? car.id === selectedCarId
                                      ? "#1976d2"
                                      : "#333"
                                    : car.id === selectedCarId
                                      ? "#e3f2fd"
                                      : "#f5f5f5",
                                  color: darkMode ? "#fff" : "#000",
                                  cursor: "pointer",
                                  transition: "all 0.3s ease",
                                }}
                                onClick={() => setSelectedCarId(car.id)}
                              >
                                {car.vin && (
                                  <Box
                                    sx={{
                                      width: "100%",
                                      mb: 2,
                                      display: "flex",
                                      flexDirection: "column",
                                      alignItems: "center",
                                    }}
                                  >
                                    <Box
                                      component="img"
                                      src={
                                        failedVehicleImages[car.vin]
                                          ? AutoshopLogo
                                          : vehiclePhotos[car.vin] || AutoshopLogo
                                      }
                                      alt={
                                        failedVehicleImages[car.vin] || !vehiclePhotos[car.vin]
                                          ? "Autoshops.com"
                                          : `${car.year} ${car.carMake} ${car.carModel}`
                                      }
                                      onError={() => {
                                        setFailedVehicleImages((prev) => ({
                                          ...prev,
                                          [car.vin]: true,
                                        }));
                                      }}
                                      sx={{
                                        width: "100%",
                                        height: 220,
                                        objectFit: "contain",
                                        borderRadius: 2,
                                        backgroundColor: "#fff",
                                        p: 1,
                                      }}
                                    />

                                    {(failedVehicleImages[car.vin] || !vehiclePhotos[car.vin]) && (
                                      <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{
                                          mt: 1,
                                          fontWeight: 500,
                                        }}
                                      >
                                        Image not available
                                      </Typography>
                                    )}
                                  </Box>
                                )}
                                <Box
                                  display="flex"
                                  justifyContent="space-between"
                                  alignItems="center"
                                  mb={2}
                                >
                                  <Typography
                                    variant="h6"
                                    fontWeight="bold"
                                  >
                                    {car.year} {car.carMake} {car.carModel}
                                  </Typography>

                                  <Box>
                                    <Tooltip title="Edit Car">
                                      <IconButton
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setEditingCarId(car.id); // target this car for editing
                                          setEditedCar({
                                            vin: car.vin || "",
                                            mileage: car.mileage || "",
                                          });
                                          setIsEditingCar(true);
                                        }}
                                      >
                                        <EditIcon />
                                      </IconButton>
                                    </Tooltip>

                                    <Tooltip title="Delete Car">
                                      <IconButton
                                        onClick={() => handleDeleteCar(car.id)}
                                      >
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </Box>
                                </Box>

                                {/* Editable fields if this car is being edited */}
                                {editingCarId === car.id ? (
                                  <>
                                    <TextField
                                      label="VIN"
                                      value={editedCar.vin}
                                      onChange={(e) =>
                                        setEditedCar({
                                          ...editedCar,
                                          vin: e.target.value,
                                        })
                                      }
                                      fullWidth
                                      sx={{ mb: 1, fontSize: "1.1rem" }}
                                    />
                                    <TextField
                                      label="Mileage"
                                      type="number"
                                      value={editedCar.mileage}
                                      onChange={(e) => {
                                        const value = e.target.value;
                                        // Allow empty string (for clearing the field)
                                        if (value === "") {
                                          setEditedCar({
                                            ...editedCar,
                                            mileage: "",
                                          });
                                          return;
                                        }
                                        // Only allow digits (no negative signs, decimals, or special characters)
                                        if (/^\d+$/.test(value)) {
                                          const numValue = Number(value);
                                          // Only allow positive numbers up to 7 digits (9999999)
                                          if (numValue >= 0 && numValue <= 9999999) {
                                            setEditedCar({
                                              ...editedCar,
                                              mileage: value,
                                            });
                                          }
                                        }
                                      }}
                                      inputProps={{
                                        min: 0,
                                        max: 9999999,
                                        step: 1,
                                      }}
                                      helperText="Enter mileage between 0 and 9,999,999 miles (whole numbers only)"
                                      error={
                                        editedCar.mileage !== "" &&
                                        (() => {
                                          const numValue = Number(editedCar.mileage);
                                          return (
                                            isNaN(numValue) ||
                                            numValue < 0 ||
                                            numValue > 9999999 ||
                                            !Number.isInteger(numValue) ||
                                            !/^\d+$/.test(String(editedCar.mileage))
                                          );
                                        })()
                                      }
                                      fullWidth
                                      sx={{ mb: 1, fontSize: "1.1rem" }}
                                    />

                                    <Box mt={1}>
                                      <Button
                                        size="small"
                                        variant="contained"
                                        color="primary"
                                        onClick={() => {
                                          handleUpdateCar(car.id);
                                        }}
                                        sx={{ mr: 1 }}
                                      >
                                        Save
                                      </Button>

                                      <Button
                                        size="small"
                                        variant="text"
                                        color="error"
                                        onClick={() => setEditingCarId(null)}
                                        sx={{
                                          mr: 1,
                                          backgroundColor: "#dc655aff",
                                          color: "#fff",
                                        }}
                                      >
                                        Cancel
                                      </Button>
                                    </Box>
                                  </>
                                ) : (
                                  <>
                                    {car.trim && (
                                      <Typography
                                        sx={{ mb: 1, fontSize: "1.1rem" }}
                                        variant="body2"
                                      >
                                        <strong>Trim: </strong> {car.trim}
                                      </Typography>
                                    )}
                                    {car.engine_size && (
                                      <Typography
                                        sx={{ mb: 1, fontSize: "1.1rem" }}
                                        variant="body2"
                                      >
                                        <strong>Engine: </strong>{" "}
                                        {car.engine_size}
                                      </Typography>
                                    )}
                                    {car.mileage && (
                                      <Typography
                                        sx={{ mb: 1, fontSize: "1.1rem" }}
                                        variant="body2"
                                      >
                                        <strong>Mileage: </strong> {car.mileage}{" "}
                                        mi
                                      </Typography>
                                    )}
                                    {car.vin && (
                                      <Typography
                                        sx={{ mb: 1, fontSize: "1.1rem" }}
                                        variant="body2"
                                      >
                                        <strong> VIN: </strong> {car.vin}
                                      </Typography>
                                    )}

                                    {car.id === selectedCarId && (
                                      <Chip
                                        label="Selected"
                                        size="small"
                                        color="primary"
                                        sx={{ mt: 1 }}
                                      />
                                    )}
                                  </>
                                )}
                              </Card>
                            </Grid>
                          ))}
                        </Box>
                        <Grid item xs={12}>
                          {!addingCar ? (
                            <Button
                              variant="contained"
                              fullWidth
                              onClick={() => setAddingCar(true)}
                            >
                              Add New Vehicle
                            </Button>
                          ) : (
                            <Card sx={{ mt: 2, p: 2 }}>
                              <Typography variant="h6" gutterBottom>
                                <AddIcon sx={{ mr: 1 }} />
                                Add Vehicle
                              </Typography>
                              <Grid container spacing={2}>
                                <Grid item xs={12}>
                                  <CarDropdown
                                    formData={formData}
                                    handleChange={handleFormChange}
                                  />
                                </Grid>
                                <Grid item xs={12}>
                                  <Box
                                    display="flex"
                                    justifyContent="flex-end"
                                    gap={2}
                                  >
                                    <Button
                                      variant="text"
                                      color="error"
                                      onClick={() => {
                                        setAddingCar(false);
                                        setFormData({
                                          carYear: "",
                                          carMake: "",
                                          carModel: "",
                                          carTrim: "",
                                          engineSize: "",
                                          mileage: "",
                                        });
                                      }}
                                      sx={{
                                        mr: 1,
                                        backgroundColor: "#dc655aff",
                                        color: "#fff",
                                      }}
                                    >
                                      Cancel
                                    </Button>
                                    <Button
                                      variant="contained"
                                      onClick={handleAddDropdownCar}
                                    >
                                      Save Vehicle
                                    </Button>
                                  </Box>
                                </Grid>
                              </Grid>
                            </Card>
                          )}
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Slide>
              </Grid>

              {/* RIGHT COLUMN */}
              <Grid item size={{ xs: 12, md: 5 }}>
                {/* Service History */}
                <Slide direction="up" in mountOnEnter unmountOnExit>
                  <Card
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      boxShadow: 3,
                      backgroundColor: "#fff",
                      minWidth: 220,
                      minHeight: 1 / 1,
                    }}
                  >
                    <CardContent>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          mb: 4,
                          p: 2,
                          borderRadius: 2,
                          boxShadow: 3,
                          backgroundColor: "#e63926",
                          color: "#fff",
                        }}
                      >
                        <HistoryIcon sx={{ mr: 1 }} /> Service History
                      </Typography>
                      <List
                        sx={{
                          maxHeight: "75vh",
                          overflowY: "auto",
                        }}
                      >
                        {serviceHistoryByCar[selectedCarId]?.length > 0 ? (
                          serviceHistoryByCar[selectedCarId].map(
                            (entry, idx) => (
                              <ListItem
                                key={idx}
                                button
                                onClick={() => {
                                  setSelectedServiceDetail(entry);
                                  setModalOpen(true);
                                }}
                                divider
                              >
                                <ListItemText
                                  primary={`Request # ${entry.request_id} - ${entry.shop_name}`}
                                  secondary={`${entry.year} ${entry.carMake} ${entry.carModel
                                    } – ${entry.services
                                      .map((s) => s.service_name)
                                      .join(", ")}`}
                                  primaryTypographyProps={{
                                    fontSize: "1.1rem",
                                    fontWeight: "bold",
                                  }}
                                  secondaryTypographyProps={{
                                    fontSize: "1.1rem",
                                  }}
                                />

                                <Chip
                                  label={entry.status}
                                  size="small"
                                  color={
                                    entry.status === "Completed"
                                      ? "success"
                                      : entry.status === "Cancelled"
                                        ? "error"
                                        : "warning"
                                  }
                                />
                              </ListItem>
                            )
                          )
                        ) : (
                          <Typography sx={{ padding: 2 }}>
                            No Services Completed
                          </Typography>
                        )}
                      </List>

                      {/* MODAL OUTSIDE MAP LOOP */}
                      {selectedServiceDetail && (
                        <Fade in={modalOpen}>
                          <Box
                            sx={{
                              position: "fixed",
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              backgroundColor: "rgba(0,0,0,0.5)",
                              zIndex: 1300,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <Paper
                              sx={{
                                p: 4,
                                borderRadius: 2,
                                maxWidth: 500,
                                width: "100%",
                                maxHeight: "90vh",
                                overflowY: "auto",
                              }}
                            >
                              <Box
                                sx={{
                                  border: "1px solid #ccc",
                                  borderRadius: 2,
                                  p: 2,
                                  mb: 2,
                                  backgroundColor: "#e63926",
                                }}
                              >
                                <Typography
                                  sx={{ color: "#fff", fontSize: "1.1rem" }}
                                  variant="h6"
                                  gutterBottom
                                >
                                  <strong>Service Details</strong>
                                </Typography>
                              </Box>

                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <Typography variant="body1">
                                  <strong>Shop:</strong> {selectedServiceDetail.shop_name}
                                </Typography>
                                {selectedServiceDetail.shop_rating && (
                                  <>
                                    <Rating value={selectedServiceDetail.shop_rating || 0} size="small" readOnly />
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        color: '#4285F4',
                                        cursor: 'pointer',
                                        textDecoration: 'underline',
                                      }}
                                      onClick={() => handleOpenReviewsModal(
                                        selectedServiceDetail.shop_id,
                                        selectedServiceDetail.shop_name,
                                        selectedServiceDetail.place_id
                                      )}
                                    >
                                      ({selectedServiceDetail.shop_review_count || 0})
                                    </Typography>
                                  </>
                                )}
                              </Box>
                              <Typography sx={{ fontSize: "1.1rem" }}>
                                <strong>Date:</strong>{" "}
                                {dayjs(
                                  selectedServiceDetail.confirm_date
                                ).format("dddd, MMMM D, YYYY")}
                              </Typography>
                              <Typography sx={{ fontSize: "1.1rem" }}>
                                <strong>Status:</strong>{" "}
                                {selectedServiceDetail.status}
                              </Typography>
                              <Typography sx={{ fontSize: "1.1rem" }}>
                                <strong>Final Price:</strong> $
                                {selectedServiceDetail.final_price}
                              </Typography>
                              <Typography sx={{ fontSize: "1.1rem" }}>
                                <strong>Notes:</strong>{" "}
                                {selectedServiceDetail.notes}
                              </Typography>

                              {selectedServiceDetail.services?.length > 0 && (
                                <>
                                  <Typography
                                    sx={{ fontSize: "1.1rem" }}
                                    mt={2}
                                  >
                                    <strong>Services Performed:</strong>
                                  </Typography>
                                  <ul>
                                    {selectedServiceDetail.services.map(
                                      (svc) => (
                                        <li
                                          sx={{ fontSize: "1.1rem" }}
                                          key={svc.service_name}
                                        >
                                          {svc.service_name} – Parts: $
                                          {svc.final_price_parts}, Labor: $
                                          {svc.final_price_labor}
                                        </li>
                                      )
                                    )}
                                  </ul>
                                </>
                              )}

                              {selectedServiceDetail.mechanics?.length > 0 && (
                                <>
                                  <Typography
                                    sx={{ fontSize: "1.1rem" }}
                                    mt={2}
                                  >
                                    <strong>Mechanics:</strong>
                                  </Typography>
                                  <ul>
                                    {selectedServiceDetail.mechanics.map(
                                      (m, i) => (
                                        <li sx={{ fontSize: "1.1rem" }} key={i}>
                                          {m.first_name} {m.last_name}
                                        </li>
                                      )
                                    )}
                                  </ul>
                                </>
                              )}

                              {/* Customer Rating - Display in same style as Mechanics */}
                              {selectedServiceDetail.status === 'Completed' && (
                                <>
                                  <Typography
                                    sx={{ fontSize: "1.1rem" }}
                                    mt={2}
                                  >
                                    <strong>Customer Rating:</strong>
                                  </Typography>
                                  {platformRatings.customer_rating ? (
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        cursor: 'pointer',
                                        paddingLeft: '1.5rem',
                                        '&:hover': {
                                          opacity: 0.7,
                                        },
                                      }}
                                      onClick={() => setCustomerRatingModalOpen(true)}
                                    >
                                      <Rating
                                        value={platformRatings.customer_rating.rating || 0}
                                        size="small"
                                        readOnly
                                      />
                                      {platformRatings.customer_rating.comment && (
                                        <Typography
                                          variant="body2"
                                          sx={{
                                            color: 'text.secondary',
                                            fontStyle: 'italic',
                                            ml: 1,
                                          }}
                                        >
                                          (Click to view full rating)
                                        </Typography>
                                      )}
                                    </Box>
                                  ) : (
                                    <ul style={{ paddingLeft: "1.5rem" }}>
                                      <li>
                                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: "1rem" }}>
                                          No rating yet
                                        </Typography>
                                      </li>
                                    </ul>
                                  )}
                                </>
                              )}

                              {selectedServiceDetail?.scannedObdCodes?.length >
                                0 && (
                                  <>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 2 }}>
                                      <Typography
                                        sx={{ fontSize: "1.1rem" }}
                                      >
                                        <strong>OBD2 Codes Logged:</strong>
                                      </Typography>
                                      <Tooltip
                                        title="OBD2 (On-Board Diagnostics) codes are diagnostic trouble codes that identify specific issues with your vehicle's engine, transmission, or other systems. These codes help diagnose problems found during your service."
                                        placement="right"
                                        arrow
                                      >
                                        <IconButton size="small" sx={{ padding: 0.5 }}>
                                          <InfoOutlined fontSize="small" sx={{ color: "#1976d2" }} />
                                        </IconButton>
                                      </Tooltip>
                                    </Box>
                                    <ul style={{ paddingLeft: "1.5rem" }}>
                                      {selectedServiceDetail.scannedObdCodes.map(
                                        (code, index) => (
                                          <li key={index}>
                                            <Typography
                                              sx={{ fontSize: "1.1rem" }}
                                            >
                                              <strong>{code.code}</strong>
                                              {code.description
                                                ? ` – ${code.description}`
                                                : ""}
                                            </Typography>
                                          </li>
                                        )
                                      )}
                                    </ul>
                                  </>
                                )}

                              <Box mt={3}>
                                {/* Conversation - Moved to top */}
                                <Box
                                  p={2}
                                  mb={2}
                                  sx={{
                                    border: "1px solid #ddd",
                                    borderRadius: 2,
                                    bgcolor: "#fff",
                                  }}
                                >
                                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                                    Conversation
                                  </Typography>

                                  {/* Show preview of last message if exists */}
                                  {(() => {
                                    const serviceId = getServiceRequestId(selectedServiceDetail);
                                    const previewComments = serviceId
                                      ? conversationPreviewMap[serviceId] || []
                                      : [];
                                    const lastMessage = previewComments[previewComments.length - 1];
                                    return lastMessage ? (
                                      <Box
                                        sx={{
                                          p: 2,
                                          mb: 2,
                                          bgcolor: '#f5f5f5',
                                          borderRadius: 1,
                                          borderLeft: '3px solid #4285F4',
                                        }}
                                      >
                                        <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                                          {lastMessage.author_name} • {new Date(lastMessage.created_at).toLocaleDateString()}
                                        </Typography>
                                        <Typography
                                          variant="body2"
                                          sx={{
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            display: '-webkit-box',
                                            WebkitLineClamp: 2,
                                            WebkitBoxOrient: 'vertical',
                                          }}
                                        >
                                          {lastMessage.comment}
                                        </Typography>
                                      </Box>
                                    ) : null;
                                  })()}

                                  <Button
                                    variant="contained"
                                    onClick={() => handleOpenConversationModal(selectedServiceDetail)}
                                    className={hasNewMessage(selectedServiceDetail) ? 'pulse-button' : ''}
                                    sx={{
                                      bgcolor: hasNewMessage(selectedServiceDetail) ? '#4CAF50' : '#e63926',
                                      '&:hover': {
                                        bgcolor: hasNewMessage(selectedServiceDetail) ? '#45a049' : '#c62f1e',
                                      },
                                    }}
                                  >
                                    {hasNewMessage(selectedServiceDetail) ? 'New message' : 'Send a message'}
                                  </Button>
                                </Box>

                                {/* Rate the Mechanic - Editable after completion */}
                                {selectedServiceDetail.status === 'Completed' && (
                                  <Box
                                    p={2}
                                    mb={2}
                                    sx={{
                                      border: "1px solid #ddd",
                                      borderRadius: 2,
                                      bgcolor: "#fff",
                                    }}
                                  >
                                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                                      Rate the Mechanic
                                    </Typography>
                                    <Rating
                                      value={platformRatings.mechanic_rating?.rating || 0}
                                      size="large"
                                      onChange={(event, newValue) => {
                                        if (newValue !== null) {
                                          handleOpenMechanicRatingModal();
                                        }
                                      }}
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        handleOpenMechanicRatingModal();
                                      }}
                                      sx={{ cursor: 'pointer' }}
                                    />
                                    {platformRatings.mechanic_rating?.comment && (
                                      <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic', color: '#666' }}>
                                        "{platformRatings.mechanic_rating.comment}"
                                      </Typography>
                                    )}
                                  </Box>
                                )}

                                {/* Rate Autoshops.com - One per user, editable */}
                                <Box
                                  p={2}
                                  mb={2}
                                  sx={{
                                    border: "1px solid #ddd",
                                    borderRadius: 2,
                                    bgcolor: "#fff",
                                  }}
                                >
                                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                                    Rate Autoshops.com
                                  </Typography>
                                  <Rating
                                    value={platformRatings.platform_rating?.rating || 0}
                                    size="large"
                                    onChange={(event, newValue) => {
                                      if (newValue !== null) {
                                        handleOpenPlatformRatingModal();
                                      }
                                    }}
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      handleOpenPlatformRatingModal();
                                    }}
                                    sx={{ cursor: 'pointer' }}
                                  />
                                  {platformRatings.platform_rating?.comment && (
                                    <Box
                                      sx={{
                                        mt: 2,
                                        p: 2,
                                        bgcolor: '#f5f5f5',
                                        borderRadius: 1,
                                        border: '1px solid #ddd',
                                      }}
                                    >
                                      <Typography variant="body2" sx={{ fontStyle: 'italic', color: '#333' }}>
                                        "{platformRatings.platform_rating.comment}"
                                      </Typography>
                                    </Box>
                                  )}
                                </Box>

                                {/* Service Rating - Google Review from Service_Reviews table */}
                                <Box
                                  p={2}
                                  mb={2}
                                  sx={{
                                    border: "1px solid #ddd",
                                    borderRadius: 2,
                                    bgcolor: "#fafafa",
                                  }}
                                >
                                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                                    Service Rating
                                  </Typography>
                                  {selectedServiceDetail.review_rating || selectedServiceDetail.service_rating ? (
                                    <Box>
                                      <Rating
                                        value={selectedServiceDetail.review_rating || selectedServiceDetail.service_rating || 0}
                                        size="large"
                                        readOnly
                                      />
                                      <Typography variant="body2" sx={{ mt: 1, color: '#666' }}>
                                        {selectedServiceDetail.review_rating || selectedServiceDetail.service_rating} stars
                                      </Typography>
                                      {(selectedServiceDetail.review_comment || selectedServiceDetail.service_comment) && (
                                        <Typography variant="body2" sx={{ mt: 2, fontStyle: 'italic' }}>
                                          "{(selectedServiceDetail.review_comment || selectedServiceDetail.service_comment)}"
                                        </Typography>
                                      )}
                                      <Box sx={{ mt: 1, display: 'inline-flex', alignItems: 'center', px: 1, py: 0.5, bgcolor: 'rgba(66, 133, 244, 0.1)', borderRadius: 1 }}>
                                        <Typography variant="caption" sx={{ color: '#4285F4', fontWeight: 500 }}>
                                          Google Review
                                        </Typography>
                                      </Box>
                                    </Box>
                                  ) : (
                                    <Box>
                                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                        No rating yet for this service
                                      </Typography>
                                      {selectedServiceDetail.place_id && (
                                        <Button
                                          variant="outlined"
                                          href={`https://search.google.com/local/reviews?placeid=${selectedServiceDetail.place_id}`}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          onClick={() => handleLeaveReviewClick(selectedServiceDetail.request_id || selectedServiceDetail.id)}
                                          sx={{
                                            color: '#4285F4',
                                            borderColor: '#4285F4',
                                            textTransform: 'none',
                                            '&:hover': {
                                              borderColor: '#1a73e8',
                                              bgcolor: 'rgba(66, 133, 244, 0.04)',
                                            },
                                          }}
                                        >
                                          Leave a review on Google →
                                        </Button>
                                      )}
                                    </Box>
                                  )}
                                </Box>
                              </Box>

                              <Stack
                                direction="row"
                                justifyContent="flex-end"
                                mt={2}
                              >
                                <Button onClick={() => setModalOpen(false)}>
                                  Close
                                </Button>
                              </Stack>

                              <Snackbar
                                open={toast.open}
                                autoHideDuration={3000}
                                onClose={() =>
                                  setToast({ ...toast, open: false })
                                }
                                anchorOrigin={{
                                  vertical: "bottom",
                                  horizontal: "center",
                                }}
                              >
                                <Alert
                                  severity={toast.severity}
                                  onClose={() =>
                                    setToast({ ...toast, open: false })
                                  }
                                >
                                  {toast.msg}
                                </Alert>
                              </Snackbar>
                            </Paper>
                          </Box>
                        </Fade>
                      )}
                    </CardContent>
                  </Card>
                </Slide>
              </Grid>
            </Grid>

            {/* Service Requests (Quotes Being Negotiated) */}
            {serviceRequests.length > 0 && (
              <Slide direction="up" in mountOnEnter unmountOnExit>
                <Card
                  sx={{
                    p: 2,
                    mb: 4,
                    borderRadius: 2,
                    boxShadow: 3,
                    backgroundColor: "#fff",
                  }}
                >
                  <CardContent>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        mb: 4,
                        p: 2,
                        borderRadius: 2,
                        boxShadow: 3,
                        backgroundColor: "#e63926",
                        color: "#fff",
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <RequestQuoteIcon sx={{ mr: 1 }} /> Service Requests
                      </Box>
                      {(() => {
                        const totalUnreadMessages = serviceRequests.reduce((sum, entry) => {
                          return sum + getUnreadMessageCount(entry);
                        }, 0);

                        return totalUnreadMessages > 0 ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                              New Messages
                            </Typography>
                            <Badge
                              badgeContent={totalUnreadMessages}
                              color="error"
                              sx={{
                                '& .MuiBadge-badge': {
                                  animation: 'pulse 2s infinite',
                                  '@keyframes pulse': {
                                    '0%, 100%': {
                                      opacity: 1,
                                      transform: 'scale(1)',
                                    },
                                    '50%': {
                                      opacity: 0.7,
                                      transform: 'scale(1.1)',
                                    },
                                  },
                                },
                              }}
                            >
                              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'transparent' }} />
                            </Badge>
                          </Box>
                        ) : null;
                      })()}
                    </Typography>

                    <List>
                      {serviceRequests.map((entry, idx) => {
                        const isExpanded = expandedIndex === `request-${idx}`;
                        const pendingBids =
                          entry.bids?.filter((b) => {
                            const bidStatus = b.bid_status || 'pending';
                            // Exclude rejected quotes (by status or bid_status) so they are cleared from the section
                            if (b.status === 'Rejected' || bidStatus === 'rejected') return false;
                            // Include pending bids AND the accepted/confirmed bid so we show "Appointment Confirmed!" for it
                            const included = (b.status === "Pending" || bidStatus === 'customer_accepted' || bidStatus === 'shop_confirmed') &&
                              bidStatus !== 'customer_chose_other' && bidStatus !== 'shop_rejected';
                            return included;
                          }) || [];
                        const hasPendingBids = pendingBids.length > 0;

                        return (
                          <React.Fragment key={entry.request_id}>
                            <ListItem
                              button
                              onClick={() =>
                                setExpandedIndex(
                                  isExpanded ? null : `request-${idx}`
                                )
                              }
                              divider
                              className={hasNewMessage(entry) ? 'service-tile-new-message' : ''}
                            >
                              <ListItemText
                                primary={formatServiceRequestListTitle(entry)}
                                primaryTypographyProps={{
                                  fontWeight: "bold",
                                  fontSize: "1.1rem",
                                }}
                              />
                              <Box display="flex" alignItems="center">
                                {hasPendingBids && (
                                  <Tooltip
                                    title="New quote to review!"
                                    arrow
                                    componentsProps={{
                                      tooltip: { sx: { fontSize: "1rem" } },
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        backgroundColor: "#ffe6e6",
                                        borderRadius: "50%",
                                        padding: "4px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        mr: 1,
                                      }}
                                    >
                                      <AlertCircle
                                        style={{ color: "#e63946" }}
                                        size={20}
                                      />
                                    </Box>
                                  </Tooltip>
                                )}
                                {(() => {
                                  const activeBids = entry.bids?.filter(b => {
                                    const bidStatus = b.bid_status || 'pending';
                                    return b.status !== 'Rejected' && bidStatus !== 'customer_chose_other' && bidStatus !== 'shop_rejected' && bidStatus !== 'rejected';
                                  }) || [];
                                  return activeBids.length > 0 ? (
                                    <Chip
                                      label={`${activeBids.length} Quote${activeBids.length > 1 ? 's' : ''}`}
                                      size="small"
                                      color="success"
                                      sx={{ mr: 1 }}
                                    />
                                  ) : (
                                    <Chip
                                      label="⏳ Awaiting Quotes"
                                      size="small"
                                      color="warning"
                                      sx={{ mr: 1 }}
                                    />
                                  );
                                })()}
                                {hasNewMessage(entry) && (
                                  <Badge
                                    badgeContent={getUnreadMessageCount(entry)}
                                    color="error"
                                    sx={{ mr: 1 }}
                                  />
                                )}
                                {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                              </Box>
                            </ListItem>

                            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                              <Box px={2} py={1} sx={{ bgcolor: "#f9f9f9" }}>
                                {(entry.status === 'Confirmed' || entry.status === 'Scheduled' || entry.status === 'In Progress') ? (
                                  // CONFIRMED/SCHEDULED/IN PROGRESS APPOINTMENT VIEW - Only show the confirmed shop
                                  <Box>
                                    <Alert severity="success" sx={{ mb: 2 }}>
                                      <Typography variant="body1" fontWeight="bold">
                                        ✅ Appointment {entry.status === 'In Progress' ? 'In Progress' : 'Confirmed'}!
                                      </Typography>
                                    </Alert>

                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}>
                                      <Typography sx={{ fontSize: "1.1rem" }}>
                                        <strong>Shop:</strong> {entry.shop_name}
                                      </Typography>

                                      {/* Message Shop Button */}
                                      {entry.shop_name && (
                                        <Button
                                          variant="contained"
                                          size="small"
                                          onClick={() => handleOpenConversationModal(entry)}
                                          className={hasNewMessage(entry) ? 'pulse-button' : ''}
                                          sx={{
                                            bgcolor: hasNewMessage(entry) ? '#4CAF50' : '#e63926',
                                            color: '#fff',
                                            textTransform: 'none',
                                            '&:hover': {
                                              bgcolor: hasNewMessage(entry) ? '#45a049' : '#c62f1e',
                                            },
                                          }}
                                        >
                                          {hasNewMessage(entry) ? 'New Message' : 'Message'}
                                        </Button>
                                      )}
                                    </Box>

                                    <Typography sx={{ fontSize: "1.1rem", mb: 1 }}>
                                      <strong>Service:</strong> {entry.services}
                                    </Typography>

                                    <Typography sx={{ fontSize: "1.1rem", mb: 1 }}>
                                      <strong>Price:</strong> ${entry.estimated_price}
                                    </Typography>

                                    <Typography sx={{ fontSize: "1.1rem", mb: 1 }}>
                                      <strong>Scheduled:</strong>{" "}
                                      {entry.customer_selected_slot_details ? (
                                        // Customer selected a specific time slot
                                        <>
                                          {format(parseISO(entry.customer_selected_slot_details.available_date), "EEE, MMM d, yyyy")}
                                          {" at "}
                                          {(() => {
                                            const formatTime = (time24) => {
                                              const [hourStr, minuteStr] = time24.split(':');
                                              const hour24 = parseInt(hourStr);
                                              const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
                                              const ampm = hour24 < 12 ? 'AM' : 'PM';
                                              return `${hour12}:${minuteStr} ${ampm}`;
                                            };
                                            const startTime = formatTime(entry.customer_selected_slot_details.start_time);
                                            const endTime = formatTime(entry.customer_selected_slot_details.end_time);
                                            return `${startTime} - ${endTime}`;
                                          })()}
                                        </>
                                      ) : entry.is_dropoff ? (
                                        // Customer selected drop-off
                                        <>
                                          🚗 Drop-off at {entry.proposed_start_time ? new Date(`1970-01-01T${entry.proposed_start_time}`).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : "TBD"}
                                          {entry.confirm_date && <> on {format(parseISO(entry.confirm_date), "EEE, MMM d, yyyy")}</>}
                                          <Typography variant="caption" display="block" color="text.secondary">
                                            (Pick up at close or when notified)
                                          </Typography>
                                        </>
                                      ) : (
                                        // Fallback for older data
                                        <>
                                          {entry.confirm_date ? format(parseISO(entry.confirm_date), "EEE, MMM d, yyyy") : "Date TBD"}
                                          {entry.proposed_start_time && (
                                            <> at {new Date(`1970-01-01T${entry.proposed_start_time}`).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}</>
                                          )}
                                        </>
                                      )}
                                    </Typography>

                                    <Typography sx={{ fontSize: "1.1rem", mb: 2 }}>
                                      <strong>Request Made:</strong>{" "}
                                      {format(parseISO(entry.request_date), "EEE, MMM d, yyyy")}
                                    </Typography>

                                    <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                                      <Button
                                        variant="outlined"
                                        color="error"
                                        onClick={() => {
                                          if (window.confirm('Are you sure you want to cancel this confirmed appointment?')) {
                                            // TODO: Implement cancel confirmed service
                                            alert('Cancel functionality coming soon');
                                          }
                                        }}
                                      >
                                        Cancel Service
                                      </Button>
                                    </Stack>
                                  </Box>
                                ) : (
                                  // REGULAR OPEN/PENDING VIEW
                                  <Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography sx={{ fontSize: "1.1rem" }}>
                                          <strong>Autoshop:</strong>{" "}
                                          {entry.shop_name || "Not assigned yet"}
                                        </Typography>
                                        {entry.shop_name && entry.shop_rating && (
                                          <>
                                            <Rating value={entry.shop_rating || 0} size="small" readOnly />
                                            <Typography variant="body2" sx={{ color: '#666' }}>
                                              {entry.shop_rating?.toFixed(1) || '—'}
                                            </Typography>
                                            <Typography
                                              variant="body2"
                                              sx={{
                                                color: '#4285F4',
                                                textDecoration: 'underline',
                                                cursor: 'pointer',
                                              }}
                                              onClick={() => handleOpenReviewsModal(entry.shop_id, entry.shop_name, entry.place_id)}
                                            >
                                              ({entry.shop_review_count || 0} Reviews)
                                            </Typography>
                                          </>
                                        )}
                                      </Box>

                                      {/* Message Shop Button */}
                                      {entry.shop_name && (
                                        <Button
                                          variant="contained"
                                          size="small"
                                          onClick={() => handleOpenConversationModal(entry)}
                                          className={hasNewMessage(entry) ? 'pulse-button' : ''}
                                          sx={{
                                            bgcolor: hasNewMessage(entry) ? '#4CAF50' : '#e63926',
                                            color: '#fff',
                                            textTransform: 'none',
                                            '&:hover': {
                                              bgcolor: hasNewMessage(entry) ? '#45a049' : '#c62f1e',
                                            },
                                          }}
                                        >
                                          {hasNewMessage(entry) ? 'New Message' : 'Message'}
                                        </Button>
                                      )}
                                    </Box>
                                    <Typography sx={{ fontSize: "1.1rem" }}>
                                      <strong>Request Was Made On:</strong>{" "}
                                      {format(
                                        parseISO(entry.request_date),
                                        "EEE, MMM d, yyyy"
                                      )}
                                    </Typography>
                                    <Typography sx={{ fontSize: "1.1rem" }}>
                                      <strong>Status:</strong> {entry.status}
                                    </Typography>
                                    <Typography sx={{ fontSize: "1.1rem" }}>
                                      <strong>Services:</strong> {entry.services}
                                    </Typography>
                                    <Typography sx={{ fontSize: "1.1rem" }}>
                                      <strong>Estimate Price: </strong>
                                      {entry.estimated_price
                                        ? ` $${entry.estimated_price}`
                                        : (() => {
                                          const pendingBids = entry.bids?.filter(
                                            (b) => (b.bid_status || b.status || "pending") === "pending" && b.offer_price != null
                                          ) || [];
                                          if (pendingBids.length > 0) {
                                            const lowest = Math.min(...pendingBids.map((b) => Number(b.offer_price)));
                                            return pendingBids.length === 1
                                              ? `Quote received: $${lowest}`
                                              : `${pendingBids.length} quotes received (lowest: $${lowest})`;
                                          }
                                          return "Waiting for shop to send a quote.";
                                        })()}
                                    </Typography>

                                    {entry.user_available_dates?.length > 0 && (
                                      <Box mt={1}>
                                        <Typography sx={{ fontSize: "1.1rem" }}>
                                          <strong>Scheduled Start Time:</strong>{" "}
                                          {entry.proposed_start_time
                                            ? (entry.confirm_date
                                              ? `${format(parseISO(entry.confirm_date), "EEE, MMM d")} at `
                                              : "") +
                                            new Date(
                                              `1970-01-01T${entry.proposed_start_time}`
                                            ).toLocaleTimeString([], {
                                              hour: "numeric",
                                              minute: "2-digit",
                                            })
                                            : (() => {
                                              const pendingWithTime = entry.bids?.find(
                                                (b) =>
                                                  (b.bid_status || b.status || "pending") === "pending" &&
                                                  (b.selected_available_date || b.proposed_time)
                                              );
                                              if (pendingWithTime?.selected_available_date || pendingWithTime?.proposed_time) {
                                                const d = pendingWithTime.selected_available_date;
                                                const t = pendingWithTime.proposed_time;
                                                const dateStr = d ? format(parseISO((d.split && d.split("T")[0]) || d), "EEE, MMM d") : "";
                                                const timeStr = t
                                                  ? new Date(`1970-01-01T${t}`).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })
                                                  : "";
                                                return dateStr && timeStr ? `Shop proposed: ${dateStr} at ${timeStr}` : "Not set";
                                              }
                                              return "Not set";
                                            })()}
                                        </Typography>

                                        <Typography sx={{ fontSize: "1.1rem", mt: 1 }}>
                                          <strong>Your Available Times:</strong>
                                        </Typography>

                                        <ul
                                          style={{
                                            paddingLeft: "1.5rem",
                                            marginTop: "0.25rem",
                                          }}
                                        >
                                          {entry.confirm_date ? (
                                            <li style={{ fontSize: "1.1rem" }}>
                                              {format(
                                                parseISO(entry.confirm_date),
                                                "EEE, MMM d, yyyy"
                                              )}
                                            </li>
                                          ) : (
                                            entry.user_available_dates
                                              .filter((d) => d.source === "User")
                                              .map((d, i) => {
                                                const dateOnly =
                                                  d.date.split("T")[0];
                                                const isDropoff =
                                                  d.is_dropoff ||
                                                  !d.start_time ||
                                                  !d.end_time;
                                                const formatted = isDropoff
                                                  ? `Drop-off: ${format(
                                                    parseISO(d.date),
                                                    "EEE, MMM d, yyyy"
                                                  )} — times TBD`
                                                  : `${format(
                                                    parseISO(d.date),
                                                    "EEE, MMM d, yyyy"
                                                  )} (${format(
                                                    parseISO(
                                                      `${dateOnly}T${d.start_time}`
                                                    ),
                                                    "h:mm a"
                                                  )} – ${format(
                                                    parseISO(
                                                      `${dateOnly}T${d.end_time}`
                                                    ),
                                                    "h:mm a"
                                                  )})`;

                                                return (
                                                  <li
                                                    key={i}
                                                    style={{ fontSize: "1.1rem" }}
                                                  >
                                                    {formatted}
                                                  </li>
                                                );
                                              })
                                          )}
                                        </ul>

                                        {entry.status === 'Open' && (
                                          <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                                            <Button
                                              variant="outlined"
                                              color="error"
                                              size="small"
                                              onClick={() => handleCancelRequestAwaitingQuotes(entry.request_id)}
                                            >
                                              {entry.request_type === 'announcement' ? 'Cancel broadcast' : 'Cancel request'}
                                            </Button>
                                          </Stack>
                                        )}

                                        {/* Mechanic Counter Suggestions (Without Bid) */}
                                        {entry.unclaimed_mechanic_times &&
                                          !entry.proposed_start_time &&
                                          Object.values(
                                            entry.unclaimed_mechanic_times
                                          ).some(
                                            (arr) =>
                                              Array.isArray(arr) && arr.length > 0
                                          ) && (
                                            <Box mt={2}>
                                              <Typography
                                                sx={{
                                                  fontSize: "1.1rem",
                                                  fontStyle: "italic",
                                                  mb: 1,
                                                }}
                                              >
                                                <strong>
                                                  ⏳ Counter Times From Shop:
                                                </strong>
                                              </Typography>

                                              <Box
                                                display="flex"
                                                flexWrap="wrap"
                                                gap={1}
                                                mb={2}
                                              >
                                                {Object.values(
                                                  entry.unclaimed_mechanic_times
                                                )
                                                  .flat()
                                                  .filter(
                                                    (d) => d.source === "Mechanic"
                                                  )
                                                  .map((d, i) => {
                                                    const dateOnly =
                                                      d.date.split("T")[0];
                                                    const formatted = `${format(
                                                      parseISO(d.date),
                                                      "EEE, MMM d"
                                                    )} @ ${format(
                                                      parseISO(
                                                        `${dateOnly}T${d.start_time}`
                                                      ),
                                                      "h:mm a"
                                                    )} `;

                                                    const isSelected =
                                                      selectedCounterTime?.date ===
                                                      d.date &&
                                                      selectedCounterTime?.start_time ===
                                                      d.start_time;

                                                    return (
                                                      <Chip
                                                        key={i}
                                                        label={formatted}
                                                        variant={
                                                          isSelected
                                                            ? "filled"
                                                            : "outlined"
                                                        }
                                                        color={
                                                          isSelected
                                                            ? "primary"
                                                            : "default"
                                                        }
                                                        clickable
                                                        onClick={() =>
                                                          setSelectedCounterTime({
                                                            date: d.date,
                                                            start_time:
                                                              d.start_time,
                                                            shop_id: d.shop_id,
                                                          })
                                                        }
                                                      />
                                                    );
                                                  })}
                                              </Box>

                                              <Box display="flex" gap={2}>
                                                <Button
                                                  size="small"
                                                  variant="contained"
                                                  disabled={!selectedCounterTime}
                                                  onClick={() => {
                                                    if (selectedCounterTime) {
                                                      console.log(entry.shop_id);
                                                      confirmServiceRequest({
                                                        requestId: entry.request_id,
                                                        confirm_date:
                                                          selectedCounterTime.date,
                                                        estimate_price:
                                                          entry.estimated_price,
                                                        proposed_start_time:
                                                          selectedCounterTime.start_time,
                                                        autoshop_confirm_date:
                                                          requestDate,
                                                        counter_available_date: [],
                                                        shopId: entry.shop_id,
                                                      });
                                                    }
                                                  }}
                                                >
                                                  Accept Selected Time
                                                </Button>
                                                <Button
                                                  size="small"
                                                  variant="outlined"
                                                  color="error"
                                                  onClick={() => {
                                                    setSelectedCounterTime(null);
                                                    handleUserCancelServiceRequest(
                                                      entry.request_id,
                                                      entry.shop_id
                                                    ); // assuming `entry.request_id` is the correct ID
                                                  }}
                                                >
                                                  None of These Times Work for Me
                                                </Button>
                                              </Box>
                                            </Box>
                                          )}
                                      </Box>
                                    )}

                                    {hasPendingBids && (
                                      <Box
                                        sx={{
                                          mt: 2,
                                          p: 2,
                                          borderRadius: 2,
                                          backgroundColor: "#f0f4ff",
                                          border: "1px solid #d0d7e2",
                                        }}
                                      >
                                        {/* Header with Quotes title and Message Button */}
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                          <Typography
                                            variant="body2"
                                            sx={{
                                              fontSize: "1.1rem",
                                              color:
                                                new Date().getTime() -
                                                  new Date(
                                                    entry.request_date
                                                  ).getTime() <
                                                  24 * 60 * 60 * 1000
                                                  ? "error.main"
                                                  : "text.primary",
                                            }}
                                          >
                                            <strong>{entry.request_type === 'announcement' || entry.request_type === 'direct' ? 'Schedule Time' : 'Bids:'}</strong>{" "}
                                            {(() => {
                                              // Only show wait timer for BIDDING requests (not announcements or direct)
                                              if (entry.request_type === 'announcement' || entry.request_type === 'direct') {
                                                return null;
                                              }

                                              const requestTime = new Date(
                                                entry.request_date + "Z"
                                              ).getTime(); // ✅ fixed
                                              const now = new Date().getTime();
                                              const diff = now - requestTime;
                                              const timeLeft =
                                                24 * 60 * 60 * 1000 - diff;
                                              console.log({
                                                requestTime,
                                                now,
                                                diff,
                                                timeLeft,
                                              });

                                              if (timeLeft > 0) {
                                                const hours = Math.floor(
                                                  timeLeft / (1000 * 60 * 60)
                                                );
                                                const minutes = Math.floor(
                                                  (timeLeft % (1000 * 60 * 60)) /
                                                  (1000 * 60)
                                                );
                                                return `You must wait ${hours}h ${minutes}m before accepting a bid request.`;
                                              } else {
                                                return null;
                                              }
                                            })()}
                                          </Typography>
                                        </Box>

                                        {/* Show rejected bids message */}
                                        {(() => {
                                          const rejectedBids = entry.bids?.filter((b) => b.status === "Rejected") || [];
                                          if (rejectedBids.length > 0) {
                                            return (
                                              <Alert severity="info" sx={{ mb: 2 }}>
                                                <Typography variant="body2">
                                                  <strong>Previous Quote(s) Declined:</strong> You declined {rejectedBids.length} quote{rejectedBids.length > 1 ? 's' : ''} from {rejectedBids.map(b => b.shop_name).join(', ')}.
                                                  {entry.request_type === 'direct' && ' The shop can submit a new quote with different pricing/times.'}
                                                </Typography>
                                              </Alert>
                                            );
                                          }
                                          return null;
                                        })()}

                                        {pendingBids.map((bid, i) => {
                                          // Check if this is an announcement request
                                          const isAnnouncement = entry.request_type === 'announcement';
                                          const bidStatus = bid.bid_status || 'pending';
                                          const customerAccepted = bidStatus === 'customer_accepted';
                                          const shopConfirmed = bidStatus === 'shop_confirmed';

                                          // Check if ANY other bid for this request has been accepted by customer
                                          const anotherBidAccepted = entry.bids?.some(b =>
                                            b.bid_id !== bid.bid_id &&
                                            (b.bid_status === 'customer_accepted' || b.bid_status === 'shop_confirmed')
                                          );
                                          const acceptedShopName = anotherBidAccepted
                                            ? entry.bids?.find(b => b.bid_id !== bid.bid_id && (b.bid_status === 'customer_accepted' || b.bid_status === 'shop_confirmed'))?.shop_name
                                            : null;

                                          const mechanicTimes =
                                            bid.mechanic_available_dates?.filter(
                                              (d) => d.source === "Mechanic"
                                            ) || [];

                                          const hasDateAndTime =
                                            bid.selected_available_date &&
                                            bid.proposed_time;
                                          if (
                                            hasDateAndTime &&
                                            !selectedSlot?.[bid.bid_id] // only initialize once
                                          ) {
                                            const dateOnly =
                                              bid.selected_available_date;
                                            const timeOnly = bid.proposed_time;

                                            setSelectedSlot((prev) => ({
                                              ...prev,
                                              [bid.bid_id]: {
                                                date: dateOnly,
                                                start_time: timeOnly,
                                              },
                                            }));
                                          }

                                          return (
                                            <Box
                                              key={i}
                                              display="flex"
                                              justifyContent="space-between"
                                              alignItems="flex-start"
                                              my={2}
                                              p={2}
                                              border="1px solid #d0d7e2"
                                              borderRadius={2}
                                              bgcolor="#fff"
                                            >
                                              {/* LEFT SIDE */}
                                              <Box
                                                display="flex"
                                                flexDirection="column"
                                                gap={1}
                                                flex={1}
                                              >
                                                <Box
                                                  display="flex"
                                                  alignItems="center"
                                                  gap={1}
                                                >
                                                  <Box
                                                    sx={{
                                                      backgroundColor: "#e63946",
                                                      color: "#fff",
                                                      borderRadius: "6px",
                                                      px: 1,
                                                      py: 0.25,
                                                      fontWeight: "bold",
                                                      fontSize: "1.05rem",
                                                    }}
                                                  >
                                                    ${bid.offer_price}
                                                  </Box>
                                                  <Typography
                                                    sx={{
                                                      fontSize: "1.05rem",
                                                      fontWeight: 600,
                                                    }}
                                                  >
                                                    — {bid.shop_name}
                                                  </Typography>
                                                </Box>

                                                {/* ANNOUNCEMENT: Show shop's offered time slots OR status */}
                                                {isAnnouncement && shopConfirmed && (
                                                  // Shop confirmed - show success
                                                  <Box>
                                                    <Alert severity="info" sx={{ mt: 2 }}>
                                                      <Typography variant="body2" fontWeight="bold">
                                                        ✅ Appointment Confirmed!
                                                      </Typography>
                                                      <Typography variant="caption">
                                                        Price: ${bid.offer_price} - Your appointment with {bid.shop_name} is confirmed
                                                      </Typography>
                                                    </Alert>
                                                    {pendingBids.filter(b => b.bid_id !== bid.bid_id && (b.bid_status || 'pending') === 'pending').length > 0 && (
                                                      <Alert severity="warning" sx={{ mt: 1 }}>
                                                        <Typography variant="caption">
                                                          💡 Reminder: You may want to decline the other {pendingBids.filter(b => b.bid_id !== bid.bid_id && (b.bid_status || 'pending') === 'pending').length} quote(s) from other shops since your appointment is confirmed.
                                                        </Typography>
                                                      </Alert>
                                                    )}
                                                  </Box>
                                                )}

                                                {isAnnouncement && customerAccepted && !shopConfirmed && (
                                                  // Customer accepted, waiting for shop
                                                  <Box>
                                                    <Alert severity="warning" sx={{ mt: 2 }}>
                                                      <Typography variant="body2" fontWeight="bold">
                                                        ⏳ Waiting for Shop Confirmation
                                                      </Typography>
                                                      <Typography variant="caption" display="block">
                                                        You accepted ${bid.offer_price} from {bid.shop_name}. The shop will confirm your appointment shortly.
                                                      </Typography>
                                                      <Typography variant="caption" display="block" sx={{ mt: 1, fontStyle: 'italic' }}>
                                                        Note: Keep other quotes active until this shop confirms, in case you need a backup option.
                                                      </Typography>
                                                    </Alert>
                                                  </Box>
                                                )}

                                                {(isAnnouncement || entry.request_type === 'direct') && !customerAccepted && !shopConfirmed && (
                                                  // Pending - show selection UI (same for direct and broadcast)
                                                  <Box mt={2}>
                                                    {(() => {
                                                      try {
                                                        // Separate slots: preferred = same date AND mechanic window inside customer window (start >= cust start, end <= cust end). Same logic for direct and broadcast.
                                                        let withinRangeSlots = [];
                                                        let alternativeSlots = [];
                                                        let dropoffSlots = [];
                                                        const timeToMins = (t) => {
                                                          if (!t) return 0;
                                                          const s = t.toString().trim();
                                                          const [h, m] = s.split(':').map(n => parseInt(n, 10) || 0);
                                                          return (h || 0) * 60 + (m || 0);
                                                        };
                                                        const isDropoffHeuristic = (startTime, endTime) => {
                                                          const s = timeToMins(startTime);
                                                          const e = timeToMins(endTime);
                                                          const spanMins = e > s ? e - s : (24 * 60 - s) + e;
                                                          return spanMins >= 8 * 60 && s <= 10 * 60 && e >= 17 * 60;
                                                        };
                                                        const isWithinCustomerWindow = (slotDateStr, mechanicStart, mechanicEnd, customerDates) => {
                                                          const onDate = customerDates.filter(c => ((c.available_date || c.date || '').toString().split('T')[0]) === slotDateStr);
                                                          for (const c of onDate) {
                                                            const cStart = timeToMins(c.start_time);
                                                            const cEnd = timeToMins(c.end_time);
                                                            const mStart = timeToMins(mechanicStart);
                                                            const mEnd = timeToMins(mechanicEnd);
                                                            if (mStart >= cStart && mEnd <= cEnd && cEnd > mStart) return true;
                                                          }
                                                          return false;
                                                        };
                                                        const userDatesRaw = entry.user_available_dates || (entry.availableDates || []).filter((d) => d.source === 'User') || [];
                                                        const customerDates = userDatesRaw
                                                          .filter((d) => !d.source || d.source === 'User')
                                                          .map((d) => ({ available_date: d.available_date || d.date, start_time: d.start_time, end_time: d.end_time }));
                                                        const mechanicSlotsForBid = (bid.mechanic_available_dates && Array.isArray(bid.mechanic_available_dates) && bid.mechanic_available_dates.length > 0)
                                                          ? bid.mechanic_available_dates
                                                          : (entry.mechanic_available_dates || []).filter((d) => d.shop_id != null && (Number(d.shop_id) === Number(bid.autoshop_id) || Number(d.shop_id) === Number(bid.shop_id)));
                                                        if (mechanicSlotsForBid.length > 0) {
                                                          for (const date of mechanicSlotsForBid) {
                                                            const rawDropoff = date.is_dropoff ?? date.isDropoff;
                                                            const isDropoff = !!(rawDropoff === true || rawDropoff === 1 || rawDropoff === 'true') ||
                                                              isDropoffHeuristic(date.start_time, date.end_time);
                                                            const slot = {
                                                              id: date.id,
                                                              start: date.start_time,
                                                              end: date.end_time,
                                                              date: date.available_date || date.date,
                                                              is_dropoff: isDropoff
                                                            };
                                                            const slotDateStr = (slot.date || '').toString().split('T')[0];
                                                            if (isDropoff) {
                                                              const matchesCustomerRange = customerDates.some(custDate =>
                                                                custDate.start_time === date.start_time && custDate.end_time === date.end_time
                                                              );
                                                              if (matchesCustomerRange) withinRangeSlots.push(slot);
                                                              else dropoffSlots.push(slot);
                                                            } else {
                                                              const preferred = slotDateStr && isWithinCustomerWindow(slotDateStr, date.start_time, date.end_time, customerDates);
                                                              if (preferred) withinRangeSlots.push(slot);
                                                              else alternativeSlots.push(slot);
                                                            }
                                                          }
                                                        }
                                                        // Fallback: no mechanic slots — use shop_offered_slots
                                                        if (withinRangeSlots.length === 0 && alternativeSlots.length === 0 && dropoffSlots.length === 0 && bid.shop_offered_slots) {
                                                          const parsed = typeof bid.shop_offered_slots === 'string' ? JSON.parse(bid.shop_offered_slots) : bid.shop_offered_slots;
                                                          if (Array.isArray(parsed) && parsed.length > 0) {
                                                            withinRangeSlots = parsed.map(slot => ({ ...slot, is_dropoff: false }));
                                                          }
                                                        }

                                                        if (withinRangeSlots.length === 0 && alternativeSlots.length === 0 && dropoffSlots.length === 0) return null;

                                                        const formatTime = (time24) => {
                                                          if (!time24) return '';
                                                          const [hour, min] = time24.split(':');
                                                          const h = parseInt(hour);
                                                          const ampm = h < 12 ? 'AM' : 'PM';
                                                          const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
                                                          return `${h12}:${min} ${ampm}`;
                                                        };

                                                        return (
                                                          <>
                                                            {/* Times within customer's requested range */}
                                                            {withinRangeSlots.length > 0 && (
                                                              <Box mb={2}>
                                                                <Typography sx={{ fontSize: "1rem", fontWeight: "bold", mb: 1, color: "success.main" }}>
                                                                  Preferred appointments:
                                                                </Typography>
                                                                <Box display="flex" flexDirection="column" gap={1}>
                                                                  {withinRangeSlots.map((slot, idx) => {
                                                                    const start24 = slot.start || '';
                                                                    const end24 = slot.end || '';
                                                                    const isSelected = selectedSlot[bid.bid_id]?.slotIndex === idx && !selectedSlot[bid.bid_id]?.isAlternative && !selectedSlot[bid.bid_id]?.isDropoff;
                                                                    const dateStr = slot.date || '';
                                                                    const formattedDate = dateStr ? format(parseISO(dateStr.split('T')[0]), "EEE, MMM d") : '';
                                                                    const timeStr = `${formatTime(start24)} - ${formatTime(end24)}`;
                                                                    const label = slot.is_dropoff
                                                                      ? (formattedDate ? `🚗 ${formattedDate} | ${timeStr} (Drop-off)` : `🚗 ${timeStr} (Drop-off)`)
                                                                      : (formattedDate ? `⏰ ${formattedDate} | ${timeStr}` : `⏰ ${timeStr}`);

                                                                    return (
                                                                      <Chip
                                                                        key={`within-${idx}`}
                                                                        label={label}
                                                                        variant={isSelected ? "filled" : "outlined"}
                                                                        color={isSelected ? "success" : "default"}
                                                                        clickable
                                                                        onClick={() => setSelectedSlot(prev => ({
                                                                          ...prev,
                                                                          [bid.bid_id]: { slotIndex: idx, slotData: slot, isAlternative: false, isDropoff: slot.is_dropoff }
                                                                        }))}
                                                                        sx={{
                                                                          fontSize: "0.95rem",
                                                                          py: 2,
                                                                          backgroundColor: slot.is_dropoff && !isSelected ? '#fff3e0' : undefined,
                                                                          borderColor: slot.is_dropoff ? '#ff9800' : undefined
                                                                        }}
                                                                      />
                                                                    );
                                                                  })}
                                                                </Box>
                                                              </Box>
                                                            )}

                                                            {/* Alternative appointment times (exclude drop-offs; those go under Drop availability) */}
                                                            {alternativeSlots.filter(s => !s.is_dropoff).length > 0 && (
                                                              <Box mb={2}>
                                                                <Typography sx={{ fontSize: "1rem", fontWeight: "bold", mb: 1, color: "#2196f3" }}>
                                                                  📅 Alternative appointments:
                                                                </Typography>
                                                                <Box display="flex" flexDirection="column" gap={1}>
                                                                  {alternativeSlots.filter(s => !s.is_dropoff).map((slot, idx) => {
                                                                    const start24 = slot.start || '';
                                                                    const end24 = slot.end || '';
                                                                    const dateStr = slot.date || '';
                                                                    const formattedDate = dateStr ? format(parseISO(dateStr.split('T')[0]), "EEE, MMM d") : '';
                                                                    const isSelected = selectedSlot[bid.bid_id]?.slotIndex === idx && selectedSlot[bid.bid_id]?.isAlternative;

                                                                    return (
                                                                      <Chip
                                                                        key={`alt-${idx}`}
                                                                        label={`📅 ${formattedDate} | ${formatTime(start24)} - ${formatTime(end24)}`}
                                                                        variant={isSelected ? "filled" : "outlined"}
                                                                        color={isSelected ? "primary" : "default"}
                                                                        clickable
                                                                        onClick={() => setSelectedSlot(prev => ({
                                                                          ...prev,
                                                                          [bid.bid_id]: { slotIndex: idx, slotData: slot, isAlternative: true, isDropoff: false }
                                                                        }))}
                                                                        sx={{ fontSize: "0.95rem", py: 2 }}
                                                                      />
                                                                    );
                                                                  })}
                                                                </Box>
                                                              </Box>
                                                            )}

                                                            {/* Drop-off section directly under Alternative appointments; selectable chips */}
                                                            {dropoffSlots.length > 0 && (
                                                              <Box mb={2}>
                                                                <Typography sx={{ fontSize: "1rem", fontWeight: "bold", mb: 1, color: "#ed6c02" }}>
                                                                  Drop-off:
                                                                </Typography>
                                                                <Box display="flex" flexDirection="column" gap={1}>
                                                                  {dropoffSlots.map((slot, idx) => {
                                                                    const dropoffTime = formatTime(slot.start);
                                                                    const pickupTime = formatTime(slot.end);
                                                                    const dateStr = slot.date || '';
                                                                    const formattedDate = dateStr ? format(parseISO(dateStr.split('T')[0]), "EEE, MMM d") : '';
                                                                    const isSelected = selectedSlot[bid.bid_id]?.slotIndex === idx && selectedSlot[bid.bid_id]?.isDropoff;

                                                                    return (
                                                                      <Chip
                                                                        key={`dropoff-${idx}`}
                                                                        label={`🚗 ${formattedDate} (Drop-off: ${dropoffTime} - Pickup: ${pickupTime})`}
                                                                        variant={isSelected ? "filled" : "outlined"}
                                                                        color={isSelected ? "warning" : "default"}
                                                                        clickable
                                                                        onClick={() => setSelectedSlot(prev => ({
                                                                          ...prev,
                                                                          [bid.bid_id]: { slotIndex: idx, slotData: slot, isAlternative: false, isDropoff: true }
                                                                        }))}
                                                                        sx={{
                                                                          fontSize: "0.95rem",
                                                                          py: 2,
                                                                          backgroundColor: isSelected ? undefined : '#fff3e0',
                                                                          borderColor: '#ff9800'
                                                                        }}
                                                                      />
                                                                    );
                                                                  })}
                                                                </Box>
                                                              </Box>
                                                            )}
                                                          </>
                                                        );
                                                      } catch (e) {
                                                        console.error('Error parsing time slots:', e);
                                                        return null;
                                                      }
                                                    })()}
                                                  </Box>
                                                )}

                                                {/* BIDDING/DIRECT: Show status banners OR traditional bid UI */}
                                                {!isAnnouncement && shopConfirmed && (
                                                  // Shop confirmed - show success
                                                  <Box>
                                                    <Alert severity="info" sx={{ mt: 2 }}>
                                                      <Typography variant="body2" fontWeight="bold">
                                                        ✅ Appointment Confirmed!
                                                      </Typography>
                                                      <Typography variant="caption">
                                                        Price: ${bid.offer_price} - Your appointment with {bid.shop_name} is confirmed
                                                      </Typography>
                                                    </Alert>
                                                  </Box>
                                                )}

                                                {!isAnnouncement && customerAccepted && !shopConfirmed && (
                                                  // Customer accepted, waiting for shop
                                                  <Box>
                                                    <Alert severity="warning" sx={{ mt: 2 }}>
                                                      <Typography variant="body2" fontWeight="bold">
                                                        ⏳ Waiting for Shop Confirmation
                                                      </Typography>
                                                      <Typography variant="caption" display="block">
                                                        You accepted ${bid.offer_price} from {bid.shop_name}. The shop will confirm your appointment shortly.
                                                      </Typography>
                                                      <Typography variant="caption" display="block" sx={{ mt: 1, fontStyle: 'italic' }}>
                                                        We'll notify you once they confirm!
                                                      </Typography>
                                                    </Alert>
                                                  </Box>
                                                )}

                                                {!isAnnouncement && entry.request_type !== 'direct' && !customerAccepted && !shopConfirmed && (bid.selected_available_date || mechanicTimes.length > 0) ? (
                                                  <>
                                                    {/* Separate times into three groups */}
                                                    {(() => {
                                                      // Filter mechanic times by type
                                                      const appointmentTimes = mechanicTimes.filter(slot => !slot.is_dropoff);
                                                      const dropoffTimes = mechanicTimes.filter(slot => slot.is_dropoff);
                                                      const hasPreferredTime = bid.selected_available_date && bid.proposed_time && !bid.is_dropoff;

                                                      return (
                                                        <>
                                                          {/* Times within customer's preferred range */}
                                                          {hasPreferredTime && (
                                                            <>
                                                              <Typography
                                                                sx={{
                                                                  fontSize: "1rem",
                                                                  fontWeight: "bold",
                                                                  mb: 1,
                                                                  color: '#4caf50'
                                                                }}
                                                              >
                                                                ✓ Time within your preferred range:
                                                              </Typography>
                                                              <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
                                                                {(() => {
                                                                  const dateOnly = bid.selected_available_date.split("T")[0];
                                                                  const formattedDate = format(parseISO(bid.selected_available_date), "EEE, MMM d");
                                                                  const formattedTime = format(parseISO(`${dateOnly}T${bid.proposed_time}`), "h:mm a");
                                                                  const isSelected = selectedSlot[bid.bid_id]?.date === bid.selected_available_date &&
                                                                    selectedSlot[bid.bid_id]?.start_time === bid.proposed_time;

                                                                  return (
                                                                    <Chip
                                                                      key="customer-slot"
                                                                      label={`${formattedDate} @ ${formattedTime}`}
                                                                      clickable
                                                                      color={isSelected ? "primary" : "default"}
                                                                      variant={isSelected ? "filled" : "outlined"}
                                                                      onClick={() =>
                                                                        setSelectedSlot((prev) => ({
                                                                          ...prev,
                                                                          [bid.bid_id]: {
                                                                            date: bid.selected_available_date,
                                                                            start_time: bid.proposed_time,
                                                                          },
                                                                        }))
                                                                      }
                                                                    />
                                                                  );
                                                                })()}
                                                              </Box>
                                                            </>
                                                          )}

                                                          {/* Alternative appointment times */}
                                                          {appointmentTimes.length > 0 && (
                                                            <>
                                                              <Typography
                                                                sx={{
                                                                  fontSize: "1rem",
                                                                  fontWeight: "bold",
                                                                  mb: 1,
                                                                  color: '#2196f3'
                                                                }}
                                                              >
                                                                📅 Alternative appointments:
                                                              </Typography>
                                                              <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
                                                                {appointmentTimes.map((slot, idx) => {
                                                                  const dateOnly = slot.date.split("T")[0];
                                                                  const formattedDate = format(parseISO(slot.date), "EEE, MMM d");
                                                                  const formattedTime = format(parseISO(`${dateOnly}T${slot.start_time}`), "h:mm a");
                                                                  const formattedEndTime = format(parseISO(`${dateOnly}T${slot.end_time}`), "h:mm a");
                                                                  const isSelected = selectedSlot[bid.bid_id]?.date === slot.date &&
                                                                    selectedSlot[bid.bid_id]?.start_time === slot.start_time;

                                                                  return (
                                                                    <Chip
                                                                      key={`apt-${idx}`}
                                                                      label={`${formattedDate} @ ${formattedTime} - ${formattedEndTime}`}
                                                                      clickable
                                                                      color={isSelected ? "primary" : "default"}
                                                                      variant={isSelected ? "filled" : "outlined"}
                                                                      onClick={() =>
                                                                        setSelectedSlot((prev) => ({
                                                                          ...prev,
                                                                          [bid.bid_id]: {
                                                                            date: slot.date,
                                                                            start_time: slot.start_time,
                                                                          },
                                                                        }))
                                                                      }
                                                                    />
                                                                  );
                                                                })}
                                                              </Box>
                                                            </>
                                                          )}

                                                          {/* Drop-off options */}
                                                          {dropoffTimes.length > 0 && (
                                                            <>
                                                              <Typography
                                                                sx={{
                                                                  fontSize: "1rem",
                                                                  fontWeight: "bold",
                                                                  mb: 1,
                                                                  color: '#ff9800'
                                                                }}
                                                              >
                                                                🚗 Drop-off service options:
                                                              </Typography>
                                                              <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
                                                                {dropoffTimes.map((slot, idx) => {
                                                                  const dateOnly = slot.date.split("T")[0];
                                                                  const formattedDate = format(parseISO(slot.date), "EEE, MMM d");
                                                                  const dropoffTime = format(parseISO(`${dateOnly}T${slot.start_time}`), "h:mm a");
                                                                  const pickupTime = format(parseISO(`${dateOnly}T${slot.end_time}`), "h:mm a");
                                                                  const isSelected = selectedSlot[bid.bid_id]?.date === slot.date &&
                                                                    selectedSlot[bid.bid_id]?.start_time === slot.start_time;

                                                                  return (
                                                                    <Chip
                                                                      key={`dropoff-${idx}`}
                                                                      label={`${formattedDate} (Drop-off: ${dropoffTime} - Pickup: ${pickupTime})`}
                                                                      clickable
                                                                      color={isSelected ? "primary" : "default"}
                                                                      variant={isSelected ? "filled" : "outlined"}
                                                                      onClick={() =>
                                                                        setSelectedSlot((prev) => ({
                                                                          ...prev,
                                                                          [bid.bid_id]: {
                                                                            date: slot.date,
                                                                            start_time: slot.start_time,
                                                                          },
                                                                        }))
                                                                      }
                                                                      sx={{
                                                                        backgroundColor: isSelected ? undefined : '#fff3e0',
                                                                        borderColor: '#ff9800'
                                                                      }}
                                                                    />
                                                                  );
                                                                })}
                                                              </Box>
                                                            </>
                                                          )}
                                                        </>
                                                      );
                                                    })()}
                                                  </>
                                                ) : !isAnnouncement ? (
                                                  <Typography
                                                    sx={{
                                                      fontSize: "1rem",
                                                      color: "gray",
                                                    }}
                                                  >
                                                    {/* No times provided. Waiting on
                                                    mechanic. */}
                                                  </Typography>
                                                ) : null}
                                              </Box>

                                              {/* RIGHT SIDE: Accept or Decline */}
                                              <Box
                                                mt={2}
                                                display="flex"
                                                flexDirection="column"
                                                gap={1}
                                              >
                                                {(isAnnouncement || entry.request_type === 'direct') && !customerAccepted && !shopConfirmed ? (
                                                  // Announcement or Direct - This quote is pending: Show accept/decline (same flow)
                                                  <>
                                                    {anotherBidAccepted ? (
                                                      // Another shop's quote was accepted - show disabled state with message
                                                      <Box>
                                                        <Button
                                                          size="small"
                                                          variant="outlined"
                                                          disabled
                                                          fullWidth
                                                          sx={{
                                                            color: "#999",
                                                            borderColor: "#ddd",
                                                            cursor: "not-allowed",
                                                            mb: 1
                                                          }}
                                                        >
                                                          Awaiting Confirmation from {acceptedShopName}
                                                        </Button>
                                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontStyle: 'italic', textAlign: 'center' }}>
                                                          This quote will become available if {acceptedShopName} cancels or counter-offers
                                                        </Typography>
                                                      </Box>
                                                    ) : (
                                                      <>
                                                        <Button
                                                          size="small"
                                                          variant="contained"
                                                          disabled={!selectedSlot[bid?.bid_id]}
                                                          onClick={() =>
                                                            handleAcceptAnnouncementQuote(
                                                              bid.bid_id,
                                                              selectedSlot[bid.bid_id]
                                                            )
                                                          }
                                                          sx={{ backgroundColor: "#4caf50", "&:hover": { backgroundColor: "#45a049" } }}
                                                        >
                                                          ✅ Accept Quote & Select Time
                                                        </Button>
                                                        <Button
                                                          size="small"
                                                          variant="outlined"
                                                          color="error"
                                                          onClick={() =>
                                                            handleDeclineBid(
                                                              entry.request_id,
                                                              bid.bid_id
                                                            )
                                                          }
                                                        >
                                                          Decline Quote
                                                        </Button>
                                                      </>
                                                    )}
                                                  </>
                                                ) : (isAnnouncement || entry.request_type === 'direct') && (customerAccepted || shopConfirmed) ? (
                                                  // Announcement/Direct - This quote accepted/confirmed: No action buttons
                                                  null
                                                ) : !isAnnouncement && entry.request_type !== 'direct' && !customerAccepted && !shopConfirmed ? (
                                                  // Direct/Bidding - Pending: Show Accept/Decline buttons
                                                  <>
                                                    <Button
                                                      size="small"
                                                      variant="contained"
                                                      disabled={
                                                        (entry.request_type === 'bidding' &&
                                                          new Date().getTime() -
                                                          new Date(
                                                            entry.request_date + "Z"
                                                          ).getTime() <
                                                          24 * 60 * 60 * 1000) ||
                                                        !selectedSlot[bid?.bid_id]
                                                      }
                                                      onClick={() =>
                                                        handleAcceptBid(
                                                          entry.request_id,
                                                          bid.bid_id,
                                                          selectedSlot[bid.bid_id].date,
                                                          `${selectedSlot[bid.bid_id]
                                                            .start_time
                                                          }:00`
                                                        )
                                                      }
                                                    >
                                                      {entry.request_type === 'direct' ? 'Accept Quote' : 'Accept Bid'}
                                                    </Button>
                                                    <Button
                                                      size="small"
                                                      variant="contained"
                                                      color="error"
                                                      onClick={() =>
                                                        handleDeclineBid(
                                                          entry.request_id,
                                                          bid.bid_id
                                                        )
                                                      }
                                                    >
                                                      Decline
                                                    </Button>
                                                  </>
                                                ) : (
                                                  // Direct/Bidding - Accepted or Confirmed: No action buttons
                                                  null
                                                )}
                                              </Box>
                                            </Box>
                                          );
                                        })}
                                      </Box>
                                    )}
                                  </Box>
                                )}
                              </Box>
                            </Collapse>
                          </React.Fragment>
                        );
                      })}
                    </List>
                  </CardContent>
                </Card>
              </Slide>
            )}

            {/* Services in Progress (Scheduled/Confirmed Work) */}
            <Slide direction="up" in mountOnEnter unmountOnExit>
              <Card
                sx={{
                  p: 2,
                  mb: 4,
                  borderRadius: 2,
                  boxShadow: 3,
                  backgroundColor: "#fff",
                }}
              >
                <CardContent>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      mb: 4,
                      p: 2,
                      borderRadius: 2,
                      boxShadow: 3,
                      backgroundColor: "#e63926",
                      color: "#fff",
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <BuildIcon sx={{ mr: 1 }} /> Services In Progress
                    </Box>
                    {(() => {
                      // Calculate total unread messages for non-completed services only
                      const nonCompletedServices = scheduledServices.filter(
                        entry => ['Scheduled', 'In Progress'].includes(entry.status)
                      );
                      const totalUnreadMessages = nonCompletedServices.reduce((sum, entry) => {
                        return sum + getUnreadMessageCount(entry);
                      }, 0);

                      return totalUnreadMessages > 0 ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                            New Messages
                          </Typography>
                          <Badge
                            badgeContent={totalUnreadMessages}
                            color="error"
                            sx={{
                              '& .MuiBadge-badge': {
                                animation: 'pulse 2s infinite',
                                '@keyframes pulse': {
                                  '0%, 100%': {
                                    opacity: 1,
                                    transform: 'scale(1)',
                                  },
                                  '50%': {
                                    opacity: 0.7,
                                    transform: 'scale(1.1)',
                                  },
                                },
                              },
                            }}
                          >
                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'transparent' }} />
                          </Badge>
                        </Box>
                      ) : null;
                    })()}
                  </Typography>

                  <List>
                    {scheduledServices.length === 0 ? (
                      <Typography sx={{ fontSize: "1.1rem", p: 2 }}>
                        No services in progress.
                      </Typography>
                    ) : (
                      scheduledServices
                        .map((entry, idx) => {
                          const isExpanded = expandedIndex === `progress-${idx}`;
                          const pendingBids =
                            entry.bids?.filter((b) => {
                              // Exclude rejected quotes so they are cleared from the section
                              const bidStatus = b.bid_status || 'pending';
                              if (b.status === 'Rejected' || bidStatus === 'rejected') return false;
                              const included = (b.status === "Pending" || bidStatus === 'customer_accepted' || bidStatus === 'shop_confirmed') &&
                                bidStatus !== 'customer_chose_other' && bidStatus !== 'shop_rejected';
                              return included;
                            }) || [];
                          const hasPendingBids = pendingBids.length > 0;

                          return (
                            <React.Fragment key={entry.request_id}>
                              <ListItem
                                button
                                onClick={() =>
                                  setExpandedIndex(
                                    isExpanded ? null : `progress-${idx}`
                                  )
                                }
                                divider
                                className={hasNewMessage(entry) ? 'service-tile-new-message' : ''}
                              >
                                <ListItemText
                                  primary={formatServiceRequestListTitle(entry)}
                                  primaryTypographyProps={{
                                    fontWeight: "bold",
                                    fontSize: "1.1rem",
                                  }}
                                />
                                <Box display="flex" alignItems="center" ml="auto">
                                  {hasPendingBids && (
                                    <Tooltip
                                      title="New quote to review!"
                                      arrow
                                      componentsProps={{
                                        tooltip: { sx: { fontSize: "1rem" } },
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          backgroundColor: "#ffe6e6",
                                          borderRadius: "50%",
                                          padding: "4px",
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          mr: 1,
                                        }}
                                      >
                                        <AlertCircle
                                          style={{ color: "#e63946" }}
                                          size={20}
                                        />
                                      </Box>
                                    </Tooltip>
                                  )}
                                  {/* Status Chip - Show "Awaiting Quotes" for Open/Pending with bid count */}
                                  {(entry.status === 'Open' || entry.status === 'Pending') ? (
                                    (() => {
                                      const activeBids = entry.bids?.filter(b => {
                                        const bidStatus = b.bid_status || 'pending';
                                        return b.status !== 'Rejected' && bidStatus !== 'customer_chose_other' && bidStatus !== 'shop_rejected' && bidStatus !== 'rejected';
                                      }) || [];
                                      return activeBids.length > 0 ? (
                                        <Chip
                                          label={`${activeBids.length} Quote${activeBids.length > 1 ? 's' : ''}`}
                                          size="small"
                                          color="success"
                                          sx={{ mr: 1 }}
                                        />
                                      ) : (
                                        <Chip
                                          label="⏳ Awaiting Quotes"
                                          size="small"
                                          color="warning"
                                          sx={{ mr: 1 }}
                                        />
                                      );
                                    })()
                                  ) : (
                                    <Chip
                                      label={entry.status}
                                      size="small"
                                      color={
                                        entry.status === "Accepted" || entry.status === "Confirmed"
                                          ? "success"
                                          : entry.status === "In Progress"
                                            ? "warning"
                                            : entry.bids?.every((b) => b.status === "Rejected")
                                              ? "error"
                                              : "info"
                                      }
                                    />
                                  )}

                                  {isExpanded ? (
                                    <ExpandLessIcon />
                                  ) : (
                                    <ExpandMoreIcon />
                                  )}
                                </Box>
                              </ListItem>

                              {isExpanded && (
                                <Box px={2} py={1} sx={{ bgcolor: "#f9f9f9" }}>
                                  {(entry.status === 'Confirmed' || entry.status === 'Scheduled' || entry.status === 'In Progress') ? (
                                    // CONFIRMED/SCHEDULED/IN PROGRESS APPOINTMENT VIEW - Only show the confirmed shop
                                    <Box>
                                      <Alert severity="success" sx={{ mb: 2 }}>
                                        <Typography variant="body1" fontWeight="bold">
                                          ✅ Appointment {entry.status === 'In Progress' ? 'In Progress' : 'Confirmed'}!
                                        </Typography>
                                      </Alert>

                                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}>
                                        <Typography sx={{ fontSize: "1.1rem" }}>
                                          <strong>Shop:</strong> {entry.shop_name}
                                        </Typography>

                                        {/* Message Shop Button */}
                                        {entry.shop_name && (
                                          <Button
                                            variant="contained"
                                            size="small"
                                            onClick={() => handleOpenConversationModal(entry)}
                                            className={hasNewMessage(entry) ? 'pulse-button' : ''}
                                            sx={{
                                              bgcolor: hasNewMessage(entry) ? '#4CAF50' : '#e63926',
                                              color: '#fff',
                                              textTransform: 'none',
                                              '&:hover': {
                                                bgcolor: hasNewMessage(entry) ? '#45a049' : '#c62f1e',
                                              },
                                            }}
                                          >
                                            {hasNewMessage(entry) ? 'New Message' : 'Message'}
                                          </Button>
                                        )}
                                      </Box>

                                      <Typography sx={{ fontSize: "1.1rem", mb: 1 }}>
                                        <strong>Service:</strong> {entry.services}
                                      </Typography>

                                      <Typography sx={{ fontSize: "1.1rem", mb: 1 }}>
                                        <strong>Price:</strong> ${entry.estimated_price}
                                      </Typography>

                                      <Typography sx={{ fontSize: "1.1rem", mb: 1 }}>
                                        <strong>Scheduled:</strong>{" "}
                                        {entry.customer_selected_slot_details ? (
                                          // Customer selected a specific time slot
                                          <>
                                            {format(parseISO(entry.customer_selected_slot_details.available_date), "EEE, MMM d, yyyy")}
                                            {" at "}
                                            {(() => {
                                              const formatTime = (time24) => {
                                                const [hourStr, minuteStr] = time24.split(':');
                                                const hour24 = parseInt(hourStr);
                                                const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
                                                const ampm = hour24 < 12 ? 'AM' : 'PM';
                                                return `${hour12}:${minuteStr} ${ampm}`;
                                              };
                                              const startTime = formatTime(entry.customer_selected_slot_details.start_time);
                                              const endTime = formatTime(entry.customer_selected_slot_details.end_time);
                                              return `${startTime} - ${endTime}`;
                                            })()}
                                          </>
                                        ) : entry.is_dropoff ? (
                                          // Customer selected drop-off
                                          <>
                                            🚗 Drop-off at {entry.proposed_start_time ? new Date(`1970-01-01T${entry.proposed_start_time}`).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : "TBD"}
                                            {entry.confirm_date && <> on {format(parseISO(entry.confirm_date), "EEE, MMM d, yyyy")}</>}
                                            <Typography variant="caption" display="block" color="text.secondary">
                                              (Pick up at close or when notified)
                                            </Typography>
                                          </>
                                        ) : (
                                          // Fallback for older data
                                          <>
                                            {entry.confirm_date ? format(parseISO(entry.confirm_date), "EEE, MMM d, yyyy") : "Date TBD"}
                                            {entry.proposed_start_time && (
                                              <> at {new Date(`1970-01-01T${entry.proposed_start_time}`).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}</>
                                            )}
                                          </>
                                        )}
                                      </Typography>

                                      <Typography sx={{ fontSize: "1.1rem", mb: 2 }}>
                                        <strong>Request Made:</strong>{" "}
                                        {format(parseISO(entry.request_date), "EEE, MMM d, yyyy")}
                                      </Typography>

                                      <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                                        <Button
                                          variant="outlined"
                                          color="error"
                                          onClick={() => {
                                            if (window.confirm('Are you sure you want to cancel this confirmed appointment?')) {
                                              // TODO: Implement cancel confirmed service
                                              alert('Cancel functionality coming soon');
                                            }
                                          }}
                                        >
                                          Cancel Service
                                        </Button>
                                      </Stack>
                                    </Box>
                                  ) : (
                                    // REGULAR OPEN/PENDING VIEW
                                    <Box>
                                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                          <Typography sx={{ fontSize: "1.1rem" }}>
                                            <strong>Autoshop:</strong>{" "}
                                            {entry.shop_name || "Not assigned yet"}
                                          </Typography>
                                          {entry.shop_name && entry.shop_rating && (
                                            <>
                                              <Rating value={entry.shop_rating || 0} size="small" readOnly />
                                              <Typography variant="body2" sx={{ color: '#666' }}>
                                                {entry.shop_rating?.toFixed(1) || '—'}
                                              </Typography>
                                              <Typography
                                                variant="body2"
                                                sx={{
                                                  color: '#4285F4',
                                                  textDecoration: 'underline',
                                                  cursor: 'pointer',
                                                }}
                                                onClick={() => handleOpenReviewsModal(entry.shop_id, entry.shop_name, entry.place_id)}
                                              >
                                                ({entry.shop_review_count || 0} Reviews)
                                              </Typography>
                                            </>
                                          )}
                                        </Box>

                                        {/* Message Shop Button */}
                                        {entry.shop_name && (
                                          <Button
                                            variant="contained"
                                            size="small"
                                            onClick={() => handleOpenConversationModal(entry)}
                                            className={hasNewMessage(entry) ? 'pulse-button' : ''}
                                            sx={{
                                              bgcolor: hasNewMessage(entry) ? '#4CAF50' : '#e63926',
                                              color: '#fff',
                                              textTransform: 'none',
                                              '&:hover': {
                                                bgcolor: hasNewMessage(entry) ? '#45a049' : '#c62f1e',
                                              },
                                            }}
                                          >
                                            {hasNewMessage(entry) ? 'New Message' : 'Message'}
                                          </Button>
                                        )}
                                      </Box>
                                      <Typography sx={{ fontSize: "1.1rem" }}>
                                        <strong>Request Was Made On:</strong>{" "}
                                        {format(
                                          parseISO(entry.request_date),
                                          "EEE, MMM d, yyyy"
                                        )}
                                      </Typography>
                                      <Typography sx={{ fontSize: "1.1rem" }}>
                                        <strong>Status:</strong> {entry.status}
                                      </Typography>
                                      <Typography sx={{ fontSize: "1.1rem" }}>
                                        <strong>Services:</strong> {entry.services}
                                      </Typography>
                                      <Typography sx={{ fontSize: "1.1rem" }}>
                                        <strong>Estimate Price: </strong>
                                        {entry.estimated_price
                                          ? ` $${entry.estimated_price}`
                                          : (() => {
                                            const pendingBids = entry.bids?.filter(
                                              (b) => (b.bid_status || b.status || "pending") === "pending" && b.offer_price != null
                                            ) || [];
                                            if (pendingBids.length > 0) {
                                              const lowest = Math.min(...pendingBids.map((b) => Number(b.offer_price)));
                                              return pendingBids.length === 1
                                                ? `Quote received: $${lowest}`
                                                : `${pendingBids.length} quotes received (lowest: $${lowest})`;
                                            }
                                            return "Waiting for shop to send a quote.";
                                          })()}
                                      </Typography>

                                      {entry.user_available_dates?.length > 0 && (
                                        <Box mt={1}>
                                          <Typography sx={{ fontSize: "1.1rem" }}>
                                            <strong>Scheduled Start Time:</strong>{" "}
                                            {entry.proposed_start_time
                                              ? (entry.confirm_date
                                                ? `${format(parseISO(entry.confirm_date), "EEE, MMM d")} at `
                                                : "") +
                                              new Date(
                                                `1970-01-01T${entry.proposed_start_time}`
                                              ).toLocaleTimeString([], {
                                                hour: "numeric",
                                                minute: "2-digit",
                                              })
                                              : (() => {
                                                const pendingWithTime = entry.bids?.find(
                                                  (b) =>
                                                    (b.bid_status || b.status || "pending") === "pending" &&
                                                    (b.selected_available_date || b.proposed_time)
                                                );
                                                if (pendingWithTime?.selected_available_date || pendingWithTime?.proposed_time) {
                                                  const d = pendingWithTime.selected_available_date;
                                                  const t = pendingWithTime.proposed_time;
                                                  const dateStr = d ? format(parseISO((d.split && d.split("T")[0]) || d), "EEE, MMM d") : "";
                                                  const timeStr = t
                                                    ? new Date(`1970-01-01T${t}`).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })
                                                    : "";
                                                  return dateStr && timeStr ? `Shop proposed: ${dateStr} at ${timeStr}` : "Not set";
                                                }
                                                return "Not set";
                                              })()}
                                          </Typography>

                                          <Typography sx={{ fontSize: "1.1rem", mt: 1 }}>
                                            <strong>Your Available Times:</strong>
                                          </Typography>

                                          <ul
                                            style={{
                                              paddingLeft: "1.5rem",
                                              marginTop: "0.25rem",
                                            }}
                                          >
                                            {entry.confirm_date ? (
                                              <li style={{ fontSize: "1.1rem" }}>
                                                {format(
                                                  parseISO(entry.confirm_date),
                                                  "EEE, MMM d, yyyy"
                                                )}
                                              </li>
                                            ) : (
                                              entry.user_available_dates
                                                .filter((d) => d.source === "User")
                                                .map((d, i) => {
                                                  const dateOnly =
                                                    d.date.split("T")[0];
                                                  const isDropoff =
                                                    d.is_dropoff ||
                                                    !d.start_time ||
                                                    !d.end_time;
                                                  const formatted = isDropoff
                                                    ? `Drop-off: ${format(
                                                      parseISO(d.date),
                                                      "EEE, MMM d, yyyy"
                                                    )} — times TBD`
                                                    : `${format(
                                                      parseISO(d.date),
                                                      "EEE, MMM d, yyyy"
                                                    )} (${format(
                                                      parseISO(
                                                        `${dateOnly}T${d.start_time}`
                                                      ),
                                                      "h:mm a"
                                                    )} – ${format(
                                                      parseISO(
                                                        `${dateOnly}T${d.end_time}`
                                                      ),
                                                      "h:mm a"
                                                    )})`;

                                                  return (
                                                    <li
                                                      key={i}
                                                      style={{ fontSize: "1.1rem" }}
                                                    >
                                                      {formatted}
                                                    </li>
                                                  );
                                                })
                                            )}
                                          </ul>

                                          {entry.status === 'Open' && (
                                            <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                                              <Button
                                                variant="outlined"
                                                color="error"
                                                size="small"
                                                onClick={() => handleCancelRequestAwaitingQuotes(entry.request_id)}
                                              >
                                                {entry.request_type === 'announcement' ? 'Cancel broadcast' : 'Cancel request'}
                                              </Button>
                                            </Stack>
                                          )}

                                          {/* Mechanic Counter Suggestions (Without Bid) */}
                                          {entry.unclaimed_mechanic_times &&
                                            !entry.proposed_start_time &&
                                            Object.values(
                                              entry.unclaimed_mechanic_times
                                            ).some(
                                              (arr) =>
                                                Array.isArray(arr) && arr.length > 0
                                            ) && (
                                              <Box mt={2}>
                                                <Typography
                                                  sx={{
                                                    fontSize: "1.1rem",
                                                    fontStyle: "italic",
                                                    mb: 1,
                                                  }}
                                                >
                                                  <strong>
                                                    ⏳ Counter Times From Shop:
                                                  </strong>
                                                </Typography>

                                                <Box
                                                  display="flex"
                                                  flexWrap="wrap"
                                                  gap={1}
                                                  mb={2}
                                                >
                                                  {Object.values(
                                                    entry.unclaimed_mechanic_times
                                                  )
                                                    .flat()
                                                    .filter(
                                                      (d) => d.source === "Mechanic"
                                                    )
                                                    .map((d, i) => {
                                                      const dateOnly =
                                                        d.date.split("T")[0];
                                                      const formatted = `${format(
                                                        parseISO(d.date),
                                                        "EEE, MMM d"
                                                      )} @ ${format(
                                                        parseISO(
                                                          `${dateOnly}T${d.start_time}`
                                                        ),
                                                        "h:mm a"
                                                      )} `;

                                                      const isSelected =
                                                        selectedCounterTime?.date ===
                                                        d.date &&
                                                        selectedCounterTime?.start_time ===
                                                        d.start_time;

                                                      return (
                                                        <Chip
                                                          key={i}
                                                          label={formatted}
                                                          variant={
                                                            isSelected
                                                              ? "filled"
                                                              : "outlined"
                                                          }
                                                          color={
                                                            isSelected
                                                              ? "primary"
                                                              : "default"
                                                          }
                                                          clickable
                                                          onClick={() =>
                                                            setSelectedCounterTime({
                                                              date: d.date,
                                                              start_time:
                                                                d.start_time,
                                                              shop_id: d.shop_id,
                                                            })
                                                          }
                                                        />
                                                      );
                                                    })}
                                                </Box>

                                                <Box display="flex" gap={2}>
                                                  <Button
                                                    size="small"
                                                    variant="contained"
                                                    disabled={!selectedCounterTime}
                                                    onClick={() => {
                                                      if (selectedCounterTime) {
                                                        console.log(entry.shop_id);
                                                        confirmServiceRequest({
                                                          requestId: entry.request_id,
                                                          confirm_date:
                                                            selectedCounterTime.date,
                                                          estimate_price:
                                                            entry.estimated_price,
                                                          proposed_start_time:
                                                            selectedCounterTime.start_time,
                                                          autoshop_confirm_date:
                                                            requestDate,
                                                          counter_available_date: [],
                                                          shopId: entry.shop_id,
                                                        });
                                                      }
                                                    }}
                                                  >
                                                    Accept Selected Time
                                                  </Button>
                                                  <Button
                                                    size="small"
                                                    variant="outlined"
                                                    color="error"
                                                    onClick={() => {
                                                      setSelectedCounterTime(null);
                                                      handleUserCancelServiceRequest(
                                                        entry.request_id,
                                                        entry.shop_id
                                                      ); // assuming `entry.request_id` is the correct ID
                                                    }}
                                                  >
                                                    None of These Times Work for Me
                                                  </Button>
                                                </Box>
                                              </Box>
                                            )}
                                        </Box>
                                      )}

                                      {hasPendingBids && (
                                        <Box
                                          sx={{
                                            mt: 2,
                                            p: 2,
                                            borderRadius: 2,
                                            backgroundColor: "#f0f4ff",
                                            border: "1px solid #d0d7e2",
                                          }}
                                        >
                                          {/* Header with Quotes title and Message Button */}
                                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                            <Typography
                                              variant="body2"
                                              sx={{
                                                fontSize: "1.1rem",
                                                color:
                                                  new Date().getTime() -
                                                    new Date(
                                                      entry.request_date
                                                    ).getTime() <
                                                    24 * 60 * 60 * 1000
                                                    ? "error.main"
                                                    : "text.primary",
                                              }}
                                            >
                                              <strong>{entry.request_type === 'announcement' || entry.request_type === 'direct' ? 'Quotes:' : 'Bids:'}</strong>{" "}
                                              {(() => {
                                                // Only show wait timer for BIDDING requests (not announcements or direct)
                                                if (entry.request_type === 'announcement' || entry.request_type === 'direct') {
                                                  return null;
                                                }

                                                const requestTime = new Date(
                                                  entry.request_date + "Z"
                                                ).getTime(); // ✅ fixed
                                                const now = new Date().getTime();
                                                const diff = now - requestTime;
                                                const timeLeft =
                                                  24 * 60 * 60 * 1000 - diff;
                                                console.log({
                                                  requestTime,
                                                  now,
                                                  diff,
                                                  timeLeft,
                                                });

                                                if (timeLeft > 0) {
                                                  const hours = Math.floor(
                                                    timeLeft / (1000 * 60 * 60)
                                                  );
                                                  const minutes = Math.floor(
                                                    (timeLeft % (1000 * 60 * 60)) /
                                                    (1000 * 60)
                                                  );
                                                  return `You must wait ${hours}h ${minutes}m before accepting a bid request.`;
                                                } else {
                                                  return null;
                                                }
                                              })()}
                                            </Typography>

                                            {/* Message Shop Button */}
                                            {entry.shop_name && (
                                              <Button
                                                variant="contained"
                                                size="small"
                                                onClick={() => handleOpenConversationModal(entry)}
                                                className={hasNewMessage(entry) ? 'pulse-button' : ''}
                                                sx={{
                                                  bgcolor: hasNewMessage(entry) ? '#4CAF50' : '#e63926',
                                                  color: '#fff',
                                                  textTransform: 'none',
                                                  '&:hover': {
                                                    bgcolor: hasNewMessage(entry) ? '#45a049' : '#c62f1e',
                                                  },
                                                }}
                                              >
                                                {hasNewMessage(entry) ? 'New Message' : 'Message'}
                                              </Button>
                                            )}
                                          </Box>

                                          {/* Show rejected bids message */}
                                          {(() => {
                                            const rejectedBids = entry.bids?.filter((b) => b.status === "Rejected") || [];
                                            if (rejectedBids.length > 0) {
                                              return (
                                                <Alert severity="info" sx={{ mb: 2 }}>
                                                  <Typography variant="body2">
                                                    <strong>Previous Quote(s) Declined:</strong> You declined {rejectedBids.length} quote{rejectedBids.length > 1 ? 's' : ''} from {rejectedBids.map(b => b.shop_name).join(', ')}.
                                                    {entry.request_type === 'direct' && ' The shop can submit a new quote with different pricing/times.'}
                                                  </Typography>
                                                </Alert>
                                              );
                                            }
                                            return null;
                                          })()}

                                          {pendingBids.map((bid, i) => {
                                            // Check if this is an announcement request
                                            const isAnnouncement = entry.request_type === 'announcement';
                                            const bidStatus = bid.bid_status || 'pending';
                                            const customerAccepted = bidStatus === 'customer_accepted';
                                            const shopConfirmed = bidStatus === 'shop_confirmed';

                                            // Check if ANY other bid for this request has been accepted by customer
                                            const anotherBidAccepted = entry.bids?.some(b =>
                                              b.bid_id !== bid.bid_id &&
                                              (b.bid_status === 'customer_accepted' || b.bid_status === 'shop_confirmed')
                                            );
                                            const acceptedShopName = anotherBidAccepted
                                              ? entry.bids?.find(b => b.bid_id !== bid.bid_id && (b.bid_status === 'customer_accepted' || b.bid_status === 'shop_confirmed'))?.shop_name
                                              : null;

                                            const mechanicTimes =
                                              bid.mechanic_available_dates?.filter(
                                                (d) => d.source === "Mechanic"
                                              ) || [];

                                            const hasDateAndTime =
                                              bid.selected_available_date &&
                                              bid.proposed_time;
                                            if (
                                              hasDateAndTime &&
                                              !selectedSlot?.[bid.bid_id] // only initialize once
                                            ) {
                                              const dateOnly =
                                                bid.selected_available_date;
                                              const timeOnly = bid.proposed_time;

                                              setSelectedSlot((prev) => ({
                                                ...prev,
                                                [bid.bid_id]: {
                                                  date: dateOnly,
                                                  start_time: timeOnly,
                                                },
                                              }));
                                            }

                                            return (
                                              <Box
                                                key={i}
                                                display="flex"
                                                justifyContent="space-between"
                                                alignItems="flex-start"
                                                my={2}
                                                p={2}
                                                border="1px solid #d0d7e2"
                                                borderRadius={2}
                                                bgcolor="#fff"
                                              >
                                                {/* LEFT SIDE */}
                                                <Box
                                                  display="flex"
                                                  flexDirection="column"
                                                  gap={1}
                                                  flex={1}
                                                >
                                                  <Box
                                                    display="flex"
                                                    alignItems="center"
                                                    gap={1}
                                                  >
                                                    <Box
                                                      sx={{
                                                        backgroundColor: "#e63946",
                                                        color: "#fff",
                                                        borderRadius: "6px",
                                                        px: 1,
                                                        py: 0.25,
                                                        fontWeight: "bold",
                                                        fontSize: "1.05rem",
                                                      }}
                                                    >
                                                      ${bid.offer_price}
                                                    </Box>
                                                    <Typography
                                                      sx={{
                                                        fontSize: "1.05rem",
                                                        fontWeight: 600,
                                                      }}
                                                    >
                                                      — {bid.shop_name}
                                                    </Typography>
                                                  </Box>

                                                  {/* ANNOUNCEMENT: Show shop's offered time slots OR status */}
                                                  {isAnnouncement && shopConfirmed && (
                                                    // Shop confirmed - show success
                                                    <Box>
                                                      <Alert severity="info" sx={{ mt: 2 }}>
                                                        <Typography variant="body2" fontWeight="bold">
                                                          ✅ Appointment Confirmed!
                                                        </Typography>
                                                        <Typography variant="caption">
                                                          Price: ${bid.offer_price} - Your appointment with {bid.shop_name} is confirmed
                                                        </Typography>
                                                      </Alert>
                                                      {pendingBids.filter(b => b.bid_id !== bid.bid_id && (b.bid_status || 'pending') === 'pending').length > 0 && (
                                                        <Alert severity="warning" sx={{ mt: 1 }}>
                                                          <Typography variant="caption">
                                                            💡 Reminder: You may want to decline the other {pendingBids.filter(b => b.bid_id !== bid.bid_id && (b.bid_status || 'pending') === 'pending').length} quote(s) from other shops since your appointment is confirmed.
                                                          </Typography>
                                                        </Alert>
                                                      )}
                                                    </Box>
                                                  )}

                                                  {isAnnouncement && customerAccepted && !shopConfirmed && (
                                                    // Customer accepted, waiting for shop
                                                    <Box>
                                                      <Alert severity="warning" sx={{ mt: 2 }}>
                                                        <Typography variant="body2" fontWeight="bold">
                                                          ⏳ Waiting for Shop Confirmation
                                                        </Typography>
                                                        <Typography variant="caption" display="block">
                                                          You accepted ${bid.offer_price} from {bid.shop_name}. The shop will confirm your appointment shortly.
                                                        </Typography>
                                                        <Typography variant="caption" display="block" sx={{ mt: 1, fontStyle: 'italic' }}>
                                                          Note: Keep other quotes active until this shop confirms, in case you need a backup option.
                                                        </Typography>
                                                      </Alert>
                                                    </Box>
                                                  )}

                                                  {(isAnnouncement || entry.request_type === 'direct') && !customerAccepted && !shopConfirmed && (
                                                    // Pending - show selection UI (same for direct and broadcast)
                                                    <Box mt={2}>
                                                      {(() => {
                                                        try {
                                                          // Separate slots: preferred = same date AND mechanic window inside customer window (start >= cust start, end <= cust end). Same logic for direct and broadcast.
                                                          let withinRangeSlots = [];
                                                          let alternativeSlots = [];
                                                          let dropoffSlots = [];
                                                          const timeToMins = (t) => {
                                                            if (!t) return 0;
                                                            const s = t.toString().trim();
                                                            const [h, m] = s.split(':').map(n => parseInt(n, 10) || 0);
                                                            return (h || 0) * 60 + (m || 0);
                                                          };
                                                          const isDropoffHeuristic = (startTime, endTime) => {
                                                            const s = timeToMins(startTime);
                                                            const e = timeToMins(endTime);
                                                            const spanMins = e > s ? e - s : (24 * 60 - s) + e;
                                                            return spanMins >= 8 * 60 && s <= 10 * 60 && e >= 17 * 60;
                                                          };
                                                          const isWithinCustomerWindow = (slotDateStr, mechanicStart, mechanicEnd, customerDates) => {
                                                            const onDate = customerDates.filter(c => ((c.available_date || c.date || '').toString().split('T')[0]) === slotDateStr);
                                                            for (const c of onDate) {
                                                              const cStart = timeToMins(c.start_time);
                                                              const cEnd = timeToMins(c.end_time);
                                                              const mStart = timeToMins(mechanicStart);
                                                              const mEnd = timeToMins(mechanicEnd);
                                                              if (mStart >= cStart && mEnd <= cEnd && cEnd > mStart) return true;
                                                            }
                                                            return false;
                                                          };
                                                          const userDatesRaw = entry.user_available_dates || (entry.availableDates || []).filter((d) => d.source === 'User') || [];
                                                          const customerDates = userDatesRaw
                                                            .filter((d) => !d.source || d.source === 'User')
                                                            .map((d) => ({ available_date: d.available_date || d.date, start_time: d.start_time, end_time: d.end_time }));
                                                          const mechanicSlotsForBid = (bid.mechanic_available_dates && Array.isArray(bid.mechanic_available_dates) && bid.mechanic_available_dates.length > 0)
                                                            ? bid.mechanic_available_dates
                                                            : (entry.mechanic_available_dates || []).filter((d) => d.shop_id != null && (Number(d.shop_id) === Number(bid.autoshop_id) || Number(d.shop_id) === Number(bid.shop_id)));
                                                          if (mechanicSlotsForBid.length > 0) {
                                                            for (const date of mechanicSlotsForBid) {
                                                              const rawDropoff = date.is_dropoff ?? date.isDropoff;
                                                              const isDropoff = !!(rawDropoff === true || rawDropoff === 1 || rawDropoff === 'true') ||
                                                                isDropoffHeuristic(date.start_time, date.end_time);
                                                              const slot = {
                                                                id: date.id,
                                                                start: date.start_time,
                                                                end: date.end_time,
                                                                date: date.available_date || date.date,
                                                                is_dropoff: isDropoff
                                                              };
                                                              const slotDateStr = (slot.date || '').toString().split('T')[0];
                                                              if (isDropoff) {
                                                                const matchesCustomerRange = customerDates.some(custDate =>
                                                                  custDate.start_time === date.start_time && custDate.end_time === date.end_time
                                                                );
                                                                if (matchesCustomerRange) withinRangeSlots.push(slot);
                                                                else dropoffSlots.push(slot);
                                                              } else {
                                                                const preferred = slotDateStr && isWithinCustomerWindow(slotDateStr, date.start_time, date.end_time, customerDates);
                                                                if (preferred) withinRangeSlots.push(slot);
                                                                else alternativeSlots.push(slot);
                                                              }
                                                            }
                                                          }
                                                          // Fallback: no mechanic slots — use shop_offered_slots
                                                          if (withinRangeSlots.length === 0 && alternativeSlots.length === 0 && dropoffSlots.length === 0 && bid.shop_offered_slots) {
                                                            const parsed = typeof bid.shop_offered_slots === 'string' ? JSON.parse(bid.shop_offered_slots) : bid.shop_offered_slots;
                                                            if (Array.isArray(parsed) && parsed.length > 0) {
                                                              withinRangeSlots = parsed.map(slot => ({ ...slot, is_dropoff: false }));
                                                            }
                                                          }

                                                          if (withinRangeSlots.length === 0 && alternativeSlots.length === 0 && dropoffSlots.length === 0) return null;

                                                          const formatTime = (time24) => {
                                                            if (!time24) return '';
                                                            const [hour, min] = time24.split(':');
                                                            const h = parseInt(hour);
                                                            const ampm = h < 12 ? 'AM' : 'PM';
                                                            const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
                                                            return `${h12}:${min} ${ampm}`;
                                                          };

                                                          return (
                                                            <>
                                                              {/* Times within customer's requested range */}
                                                              {withinRangeSlots.length > 0 && (
                                                                <Box mb={2}>
                                                                  <Typography sx={{ fontSize: "1rem", fontWeight: "bold", mb: 1, color: "success.main" }}>
                                                                    Preferred appointments:
                                                                  </Typography>
                                                                  <Box display="flex" flexDirection="column" gap={1}>
                                                                    {withinRangeSlots.map((slot, idx) => {
                                                                      const start24 = slot.start || '';
                                                                      const end24 = slot.end || '';
                                                                      const isSelected = selectedSlot[bid.bid_id]?.slotIndex === idx && !selectedSlot[bid.bid_id]?.isAlternative && !selectedSlot[bid.bid_id]?.isDropoff;
                                                                      const dateStr = slot.date || '';
                                                                      const formattedDate = dateStr ? format(parseISO(dateStr.split('T')[0]), "EEE, MMM d") : '';
                                                                      const timeStr = `${formatTime(start24)} - ${formatTime(end24)}`;
                                                                      const label = slot.is_dropoff
                                                                        ? (formattedDate ? `🚗 ${formattedDate} | ${timeStr} (Drop-off)` : `🚗 ${timeStr} (Drop-off)`)
                                                                        : (formattedDate ? `⏰ ${formattedDate} | ${timeStr}` : `⏰ ${timeStr}`);

                                                                      return (
                                                                        <Chip
                                                                          key={`within-${idx}`}
                                                                          label={label}
                                                                          variant={isSelected ? "filled" : "outlined"}
                                                                          color={isSelected ? "success" : "default"}
                                                                          clickable
                                                                          onClick={() => setSelectedSlot(prev => ({
                                                                            ...prev,
                                                                            [bid.bid_id]: { slotIndex: idx, slotData: slot, isAlternative: false, isDropoff: slot.is_dropoff }
                                                                          }))}
                                                                          sx={{
                                                                            fontSize: "0.95rem",
                                                                            py: 2,
                                                                            backgroundColor: slot.is_dropoff && !isSelected ? '#fff3e0' : undefined,
                                                                            borderColor: slot.is_dropoff ? '#ff9800' : undefined
                                                                          }}
                                                                        />
                                                                      );
                                                                    })}
                                                                  </Box>
                                                                </Box>
                                                              )}

                                                              {/* Alternative appointment times (exclude drop-offs; those go under Drop availability) */}
                                                              {alternativeSlots.filter(s => !s.is_dropoff).length > 0 && (
                                                                <Box mb={2}>
                                                                  <Typography sx={{ fontSize: "1rem", fontWeight: "bold", mb: 1, color: "#2196f3" }}>
                                                                    📅 Alternative appointments:
                                                                  </Typography>
                                                                  <Box display="flex" flexDirection="column" gap={1}>
                                                                    {alternativeSlots.filter(s => !s.is_dropoff).map((slot, idx) => {
                                                                      const start24 = slot.start || '';
                                                                      const end24 = slot.end || '';
                                                                      const dateStr = slot.date || '';
                                                                      const formattedDate = dateStr ? format(parseISO(dateStr.split('T')[0]), "EEE, MMM d") : '';
                                                                      const isSelected = selectedSlot[bid.bid_id]?.slotIndex === idx && selectedSlot[bid.bid_id]?.isAlternative;

                                                                      return (
                                                                        <Chip
                                                                          key={`alt-${idx}`}
                                                                          label={`📅 ${formattedDate} | ${formatTime(start24)} - ${formatTime(end24)}`}
                                                                          variant={isSelected ? "filled" : "outlined"}
                                                                          color={isSelected ? "primary" : "default"}
                                                                          clickable
                                                                          onClick={() => setSelectedSlot(prev => ({
                                                                            ...prev,
                                                                            [bid.bid_id]: { slotIndex: idx, slotData: slot, isAlternative: true, isDropoff: false }
                                                                          }))}
                                                                          sx={{ fontSize: "0.95rem", py: 2 }}
                                                                        />
                                                                      );
                                                                    })}
                                                                  </Box>
                                                                </Box>
                                                              )}

                                                              {/* Drop-off section directly under Alternative appointments; selectable chips */}
                                                              {dropoffSlots.length > 0 && (
                                                                <Box mb={2}>
                                                                  <Typography sx={{ fontSize: "1rem", fontWeight: "bold", mb: 1, color: "#ed6c02" }}>
                                                                    Drop-off:
                                                                  </Typography>
                                                                  <Box display="flex" flexDirection="column" gap={1}>
                                                                    {dropoffSlots.map((slot, idx) => {
                                                                      const dropoffTime = formatTime(slot.start);
                                                                      const pickupTime = formatTime(slot.end);
                                                                      const dateStr = slot.date || '';
                                                                      const formattedDate = dateStr ? format(parseISO(dateStr.split('T')[0]), "EEE, MMM d") : '';
                                                                      const isSelected = selectedSlot[bid.bid_id]?.slotIndex === idx && selectedSlot[bid.bid_id]?.isDropoff;

                                                                      return (
                                                                        <Chip
                                                                          key={`dropoff-${idx}`}
                                                                          label={`🚗 ${formattedDate} (Drop-off: ${dropoffTime} - Pickup: ${pickupTime})`}
                                                                          variant={isSelected ? "filled" : "outlined"}
                                                                          color={isSelected ? "warning" : "default"}
                                                                          clickable
                                                                          onClick={() => setSelectedSlot(prev => ({
                                                                            ...prev,
                                                                            [bid.bid_id]: { slotIndex: idx, slotData: slot, isAlternative: false, isDropoff: true }
                                                                          }))}
                                                                          sx={{
                                                                            fontSize: "0.95rem",
                                                                            py: 2,
                                                                            backgroundColor: isSelected ? undefined : '#fff3e0',
                                                                            borderColor: '#ff9800'
                                                                          }}
                                                                        />
                                                                      );
                                                                    })}
                                                                  </Box>
                                                                </Box>
                                                              )}
                                                            </>
                                                          );
                                                        } catch (e) {
                                                          console.error('Error parsing time slots:', e);
                                                          return null;
                                                        }
                                                      })()}
                                                    </Box>
                                                  )}

                                                  {/* BIDDING/DIRECT: Show status banners OR traditional bid UI */}
                                                  {!isAnnouncement && shopConfirmed && (
                                                    // Shop confirmed - show success
                                                    <Box>
                                                      <Alert severity="info" sx={{ mt: 2 }}>
                                                        <Typography variant="body2" fontWeight="bold">
                                                          ✅ Appointment Confirmed!
                                                        </Typography>
                                                        <Typography variant="caption">
                                                          Price: ${bid.offer_price} - Your appointment with {bid.shop_name} is confirmed
                                                        </Typography>
                                                      </Alert>
                                                    </Box>
                                                  )}

                                                  {!isAnnouncement && customerAccepted && !shopConfirmed && (
                                                    // Customer accepted, waiting for shop
                                                    <Box>
                                                      <Alert severity="warning" sx={{ mt: 2 }}>
                                                        <Typography variant="body2" fontWeight="bold">
                                                          ⏳ Waiting for Shop Confirmation
                                                        </Typography>
                                                        <Typography variant="caption" display="block">
                                                          You accepted ${bid.offer_price} from {bid.shop_name}. The shop will confirm your appointment shortly.
                                                        </Typography>
                                                        <Typography variant="caption" display="block" sx={{ mt: 1, fontStyle: 'italic' }}>
                                                          We'll notify you once they confirm!
                                                        </Typography>
                                                      </Alert>
                                                    </Box>
                                                  )}

                                                  {!isAnnouncement && entry.request_type !== 'direct' && !customerAccepted && !shopConfirmed && (bid.selected_available_date || mechanicTimes.length > 0) ? (
                                                    <>
                                                      {/* Separate times into three groups */}
                                                      {(() => {
                                                        // Filter mechanic times by type
                                                        const appointmentTimes = mechanicTimes.filter(slot => !slot.is_dropoff);
                                                        const dropoffTimes = mechanicTimes.filter(slot => slot.is_dropoff);
                                                        const hasPreferredTime = bid.selected_available_date && bid.proposed_time && !bid.is_dropoff;

                                                        return (
                                                          <>
                                                            {/* Times within customer's preferred range */}
                                                            {hasPreferredTime && (
                                                              <>
                                                                <Typography
                                                                  sx={{
                                                                    fontSize: "1rem",
                                                                    fontWeight: "bold",
                                                                    mb: 1,
                                                                    color: '#4caf50'
                                                                  }}
                                                                >
                                                                  ✓ Time within your preferred range:
                                                                </Typography>
                                                                <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
                                                                  {(() => {
                                                                    const dateOnly = bid.selected_available_date.split("T")[0];
                                                                    const formattedDate = format(parseISO(bid.selected_available_date), "EEE, MMM d");
                                                                    const formattedTime = format(parseISO(`${dateOnly}T${bid.proposed_time}`), "h:mm a");
                                                                    const isSelected = selectedSlot[bid.bid_id]?.date === bid.selected_available_date &&
                                                                      selectedSlot[bid.bid_id]?.start_time === bid.proposed_time;

                                                                    return (
                                                                      <Chip
                                                                        key="customer-slot"
                                                                        label={`${formattedDate} @ ${formattedTime}`}
                                                                        clickable
                                                                        color={isSelected ? "primary" : "default"}
                                                                        variant={isSelected ? "filled" : "outlined"}
                                                                        onClick={() =>
                                                                          setSelectedSlot((prev) => ({
                                                                            ...prev,
                                                                            [bid.bid_id]: {
                                                                              date: bid.selected_available_date,
                                                                              start_time: bid.proposed_time,
                                                                            },
                                                                          }))
                                                                        }
                                                                      />
                                                                    );
                                                                  })()}
                                                                </Box>
                                                              </>
                                                            )}

                                                            {/* Alternative appointment times */}
                                                            {appointmentTimes.length > 0 && (
                                                              <>
                                                                <Typography
                                                                  sx={{
                                                                    fontSize: "1rem",
                                                                    fontWeight: "bold",
                                                                    mb: 1,
                                                                    color: '#2196f3'
                                                                  }}
                                                                >
                                                                  📅 Alternative appointments:
                                                                </Typography>
                                                                <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
                                                                  {appointmentTimes.map((slot, idx) => {
                                                                    const dateOnly = slot.date.split("T")[0];
                                                                    const formattedDate = format(parseISO(slot.date), "EEE, MMM d");
                                                                    const formattedTime = format(parseISO(`${dateOnly}T${slot.start_time}`), "h:mm a");
                                                                    const formattedEndTime = format(parseISO(`${dateOnly}T${slot.end_time}`), "h:mm a");
                                                                    const isSelected = selectedSlot[bid.bid_id]?.date === slot.date &&
                                                                      selectedSlot[bid.bid_id]?.start_time === slot.start_time;

                                                                    return (
                                                                      <Chip
                                                                        key={`apt-${idx}`}
                                                                        label={`${formattedDate} @ ${formattedTime} - ${formattedEndTime}`}
                                                                        clickable
                                                                        color={isSelected ? "primary" : "default"}
                                                                        variant={isSelected ? "filled" : "outlined"}
                                                                        onClick={() =>
                                                                          setSelectedSlot((prev) => ({
                                                                            ...prev,
                                                                            [bid.bid_id]: {
                                                                              date: slot.date,
                                                                              start_time: slot.start_time,
                                                                            },
                                                                          }))
                                                                        }
                                                                      />
                                                                    );
                                                                  })}
                                                                </Box>
                                                              </>
                                                            )}

                                                            {/* Drop-off options */}
                                                            {dropoffTimes.length > 0 && (
                                                              <>
                                                                <Typography
                                                                  sx={{
                                                                    fontSize: "1rem",
                                                                    fontWeight: "bold",
                                                                    mb: 1,
                                                                    color: '#ff9800'
                                                                  }}
                                                                >
                                                                  🚗 Drop-off service options:
                                                                </Typography>
                                                                <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
                                                                  {dropoffTimes.map((slot, idx) => {
                                                                    const dateOnly = slot.date.split("T")[0];
                                                                    const formattedDate = format(parseISO(slot.date), "EEE, MMM d");
                                                                    const dropoffTime = format(parseISO(`${dateOnly}T${slot.start_time}`), "h:mm a");
                                                                    const pickupTime = format(parseISO(`${dateOnly}T${slot.end_time}`), "h:mm a");
                                                                    const isSelected = selectedSlot[bid.bid_id]?.date === slot.date &&
                                                                      selectedSlot[bid.bid_id]?.start_time === slot.start_time;

                                                                    return (
                                                                      <Chip
                                                                        key={`dropoff-${idx}`}
                                                                        label={`${formattedDate} (Drop-off: ${dropoffTime} - Pickup: ${pickupTime})`}
                                                                        clickable
                                                                        color={isSelected ? "primary" : "default"}
                                                                        variant={isSelected ? "filled" : "outlined"}
                                                                        onClick={() =>
                                                                          setSelectedSlot((prev) => ({
                                                                            ...prev,
                                                                            [bid.bid_id]: {
                                                                              date: slot.date,
                                                                              start_time: slot.start_time,
                                                                            },
                                                                          }))
                                                                        }
                                                                        sx={{
                                                                          backgroundColor: isSelected ? undefined : '#fff3e0',
                                                                          borderColor: '#ff9800'
                                                                        }}
                                                                      />
                                                                    );
                                                                  })}
                                                                </Box>
                                                              </>
                                                            )}
                                                          </>
                                                        );
                                                      })()}
                                                    </>
                                                  ) : !isAnnouncement ? (
                                                    <Typography
                                                      sx={{
                                                        fontSize: "1rem",
                                                        color: "gray",
                                                      }}
                                                    >
                                                      {/* No times provided. Waiting on
                                                      mechanic. */}
                                                    </Typography>
                                                  ) : null}
                                                </Box>

                                                {/* RIGHT SIDE: Accept or Decline */}
                                                <Box
                                                  mt={2}
                                                  display="flex"
                                                  flexDirection="column"
                                                  gap={1}
                                                >
                                                  {(isAnnouncement || entry.request_type === 'direct') && !customerAccepted && !shopConfirmed ? (
                                                    // Announcement or Direct - This quote is pending: Show accept/decline (same flow)
                                                    <>
                                                      {anotherBidAccepted ? (
                                                        // Another shop's quote was accepted - show disabled state with message
                                                        <Box>
                                                          <Button
                                                            size="small"
                                                            variant="outlined"
                                                            disabled
                                                            fullWidth
                                                            sx={{
                                                              color: "#999",
                                                              borderColor: "#ddd",
                                                              cursor: "not-allowed",
                                                              mb: 1
                                                            }}
                                                          >
                                                            Awaiting Confirmation from {acceptedShopName}
                                                          </Button>
                                                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontStyle: 'italic', textAlign: 'center' }}>
                                                            This quote will become available if {acceptedShopName} cancels or counter-offers
                                                          </Typography>
                                                        </Box>
                                                      ) : (
                                                        <>
                                                          <Button
                                                            size="small"
                                                            variant="contained"
                                                            disabled={!selectedSlot[bid?.bid_id]}
                                                            onClick={() =>
                                                              handleAcceptAnnouncementQuote(
                                                                bid.bid_id,
                                                                selectedSlot[bid.bid_id]
                                                              )
                                                            }
                                                            sx={{ backgroundColor: "#4caf50", "&:hover": { backgroundColor: "#45a049" } }}
                                                          >
                                                            ✅ Accept Quote & Select Time
                                                          </Button>
                                                          <Button
                                                            size="small"
                                                            variant="outlined"
                                                            color="error"
                                                            onClick={() =>
                                                              handleDeclineBid(
                                                                entry.request_id,
                                                                bid.bid_id
                                                              )
                                                            }
                                                          >
                                                            Decline Quote
                                                          </Button>
                                                        </>
                                                      )}
                                                    </>
                                                  ) : (isAnnouncement || entry.request_type === 'direct') && (customerAccepted || shopConfirmed) ? (
                                                    // Announcement/Direct - This quote accepted/confirmed: No action buttons
                                                    null
                                                  ) : !isAnnouncement && entry.request_type !== 'direct' && !customerAccepted && !shopConfirmed ? (
                                                    // Direct/Bidding - Pending: Show Accept/Decline buttons
                                                    <>
                                                      <Button
                                                        size="small"
                                                        variant="contained"
                                                        disabled={
                                                          (entry.request_type === 'bidding' &&
                                                            new Date().getTime() -
                                                            new Date(
                                                              entry.request_date + "Z"
                                                            ).getTime() <
                                                            24 * 60 * 60 * 1000) ||
                                                          !selectedSlot[bid?.bid_id]
                                                        }
                                                        onClick={() =>
                                                          handleAcceptBid(
                                                            entry.request_id,
                                                            bid.bid_id,
                                                            selectedSlot[bid.bid_id].date,
                                                            `${selectedSlot[bid.bid_id]
                                                              .start_time
                                                            }:00`
                                                          )
                                                        }
                                                      >
                                                        {entry.request_type === 'direct' ? 'Accept Quote' : 'Accept Bid'}
                                                      </Button>
                                                      <Button
                                                        size="small"
                                                        variant="contained"
                                                        color="error"
                                                        onClick={() =>
                                                          handleDeclineBid(
                                                            entry.request_id,
                                                            bid.bid_id
                                                          )
                                                        }
                                                      >
                                                        Decline
                                                      </Button>
                                                    </>
                                                  ) : (
                                                    // Direct/Bidding - Accepted or Confirmed: No action buttons
                                                    null
                                                  )}
                                                </Box>
                                              </Box>
                                            );
                                          })}
                                        </Box>
                                      )}
                                    </Box>
                                  )}
                                </Box>
                              )}
                            </React.Fragment>
                          );
                        })
                    )}
                  </List>
                </CardContent>
              </Card>
            </Slide>

            {/* Schedule a Service - Collapsible */}
            <Card
              sx={{
                mb: 3,
                boxShadow: 3,
                borderRadius: 2,
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  bgcolor: "#e63926",
                  color: "#fff",
                  py: 2,
                  px: 3,
                  cursor: "pointer",
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
                onClick={() => setScheduleServiceExpanded(!scheduleServiceExpanded)}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <AddIcon sx={{ mr: 1 }} />
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    Schedule a Service
                  </Typography>
                </Box>
                {scheduleServiceExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </Box>

              {scheduleServiceExpanded && (
                <CardContent>
                  <ServiceRequestForm
                    cars={cars}
                    selectedCarId={selectedCarId}
                    setSelectedCarId={setSelectedCarId}
                    selectedAutoshop={selectedAutoshop}
                    setSelectedAutoshop={setSelectedAutoshop}
                    newRadius={newRadius}
                    setNewRadius={setNewRadius}
                    autoshops={autoshops}
                    selectedServices={selectedServices}
                    setSelectedServices={setSelectedServices}
                    services={services}
                    serviceDescription={serviceDescription}
                    setServiceDescription={setServiceDescription}
                    availableDates={availableDates}
                    setAvailableDates={setAvailableDates}
                    submitServiceRequest={submitServiceRequest}
                    isSubmittingServiceRequest={isSubmittingServiceRequest}
                    onSuccess={fetchServicesInProgress}
                    userZip={userZip}
                    setUserZip={setUserZip}
                    zipCode={zipCode}
                    onZipSubmit={onZipSubmit}
                    latitude={latitude}
                    longitude={longitude}
                    requestType={requestType}
                    setRequestType={setRequestType}
                    availableRequestTypes={availableRequestTypes}
                    requestTypesLoaded={requestTypesLoaded}
                    dropoffWeekStart={dropoffWeekStart}
                    setDropoffWeekStart={setDropoffWeekStart}
                    dropoffAvailableDays={dropoffAvailableDays}
                    setDropoffAvailableDays={setDropoffAvailableDays}
                  />
                </CardContent>
              )}
            </Card>
            {/* Saved OBD2 Codes */}
            <Slide direction="up" in mountOnEnter unmountOnExit>
              <Card
                sx={{
                  p: 2,
                  mb: 4,
                  borderRadius: 2,
                  boxShadow: 3,
                  backgroundColor: "#fff",
                }}
              >
                <CardContent>
                  <Box
                    sx={{
                      fontWeight: 600,
                      mb: 4,
                      p: 2,
                      borderRadius: 2,
                      boxShadow: 3,
                      backgroundColor: "#e63926",
                      color: "#fff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <BuildIcon sx={{ mr: 1 }} />
                      <Typography variant="h6">
                        Diagnostic Code History (OBD2)
                      </Typography>
                    </Box>
                    <Tooltip
                      title="OBD2 (On-Board Diagnostics) codes are diagnostic trouble codes that identify specific issues with your vehicle. View your complete diagnostic history and search for code meanings."
                      placement="left"
                      arrow
                    >
                      <IconButton size="small" sx={{ color: "#fff" }}>
                        <InfoOutlined fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  {obd2HistoryByCar[selectedCarId] &&
                    obd2HistoryByCar[selectedCarId].length > 0 ? (
                    <List
                      sx={{
                        maxHeight: "70vh",
                        overflowY: "auto",
                      }}
                    >
                      {obd2HistoryByCar[selectedCarId].map((entry, idx) => (
                        <ListItem key={idx} divider>
                          <ListItemText
                            primary={`Code: ${entry.code}`}
                            secondary={
                              <>
                                <Typography variant="body2">
                                  {entry.description}
                                </Typography>
                                <Typography variant="caption">
                                  Searched on:{" "}
                                  {new Date(
                                    entry.search_date
                                  ).toLocaleDateString()}
                                </Typography>
                              </>
                            }
                          />
                          <Chip
                            label={entry.is_active ? "Active" : "Inactive"}
                            color={entry.is_active ? "success" : "default"}
                            size="small"
                          />
                        </ListItem>
                      ))}
                    </List>
                  ) : (
                    <Typography sx={{ mt: 2 }}>
                      No OBD2 codes saved for this car.
                    </Typography>
                  )}
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      navigate("/obd");
                      // Scroll to top after navigation
                      setTimeout(() => {
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }, 100);
                    }}
                  >
                    Search Diagnostic Code
                  </Button>
                </CardContent>
              </Card>
            </Slide>

            {/* Visited Shops Section */}
            <Box mt={6}>
              <Card
                sx={{
                  p: 2,
                  mb: 4,
                  borderRadius: 2,
                  boxShadow: 3,
                  backgroundColor: "#fff",
                }}
              >
                <CardContent>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      mb: 4,
                      p: 2,
                      borderRadius: 2,
                      boxShadow: 3,
                      backgroundColor: "#e63926",
                      color: "#fff",
                    }}
                  >
                    <BuildIcon sx={{ mr: 1 }} /> Visited AutoShops Websites
                  </Typography>

                  {visitedShops.length === 0 ? (
                    <Typography sx={{ fontSize: "1.1rem" }}>
                      No shops visited yet.
                    </Typography>
                  ) : (
                    <List
                      sx={{
                        maxHeight: "70vh",
                        overflowY: "auto",
                      }}
                    >
                      {visitedShops.map((shop, index) => (
                        <ListItem key={index} divider>
                          <ListItemText
                            primary={
                              <Typography variant="h6">
                                {shop.shop_name}
                              </Typography>
                            }
                            secondary={
                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                }}
                              >
                                <Typography variant="h7" color="text.secondary">
                                  <BusinessIcon
                                    fontSize="small"
                                    sx={{ mr: 1 }}
                                  />
                                  {shop.shop_address}
                                </Typography>
                                <Typography variant="h7" color="text.secondary">
                                  <PhoneIcon fontSize="small" sx={{ mr: 1 }} />
                                  {shop.shop_phone
                                    ? `Phone: ${shop.shop_phone}`
                                    : "No phone number"}
                                </Typography>
                                {shop.voice_phone ? (
                                  <Typography variant="h7" color="text.secondary">
                                    <PhoneIcon fontSize="small" sx={{ mr: 1 }} />
                                    {`AI Phone: ${shop.voice_phone}`}
                                  </Typography>
                                ) : null}
                                <Typography variant="h7" color="text.secondary">
                                  <StarIcon fontSize="small" sx={{ mr: 1 }} />
                                  Rating:{" "}
                                  {shop.shop_rating
                                    ? `⭐ ${shop.shop_rating}`
                                    : "No rating"}
                                </Typography>
                                <Typography variant="h7" color="text.secondary">
                                  {shop.shop_network
                                    ? "✅ In Network"
                                    : "❌ Out of Network"}
                                </Typography>
                                <Typography variant="h7" color="text.secondary">
                                  <LocationOnIcon
                                    fontSize="small"
                                    sx={{ mr: 1 }}
                                  />
                                  Visited on:{" "}
                                  {new Date(shop.visit_date).toDateString()}
                                </Typography>
                              </Box>
                            }
                          />
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => window.open(shop.shop_url, "_blank")}
                          >
                            Visit Site
                          </Button>

                          <Button
                            sx={{
                              ml: 1,
                              fontSize: "25px"
                            }}
                            onClick={() => deleteSearch(shop.autoshop_id)}
                          >
                            🗑️
                          </Button>
                        </ListItem>
                      ))}
                    </List>
                  )}
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => navigate("/google-wrapper")}
                  >
                    View Shops Nearby
                  </Button>
                </CardContent>
              </Card>
            </Box>

            {/* VIN Decoder */}
            {/* <Box mt={6}>
              <Card sx={{ p: 2 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    <SettingsIcon sx={{ mr: 1 }} /> Enter VIN and Mileage
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="VIN Number"
                        fullWidth
                        value={vin}
                        onChange={(e) => setVin(e.target.value)}
                        placeholder="Enter your VIN"
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Current Mileage"
                        fullWidth
                        value={mileage}
                        onChange={(e) => setMileage(e.target.value)}
                        placeholder="e.g. 75000"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Button variant="outlined" onClick={handleVinSubmit}>
                        Submit
                      </Button>
                    </Grid>

                    {decodedInfo && (
                      <Grid item xs={12} mt={2}>
                        <Typography variant="subtitle1" gutterBottom>
                          Vehicle Info:
                        </Typography>
                        <Typography>Make: {decodedInfo.make}</Typography>
                        <Typography>Model: {decodedInfo.model}</Typography>
                        <Typography>Year: {decodedInfo.year}</Typography>
                        <Typography>
                          Mileage: {decodedInfo.mileage} miles
                        </Typography>
                        <Typography sx={{ mt: 1 }} color="primary">
                          Recommended Service:{" "}
                          {decodedInfo.mileage > 80000
                            ? "Full Maintenance Check"
                            : "Oil Change & Brake Check"}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </CardContent>
              </Card>
            </Box> */}
          </Container>
        </Box>

        {/* Email Change Modal */}
        <Dialog
          open={emailChangeModalOpen}
          onClose={() => {
            setEmailChangeModalOpen(false);
            setNewEmail("");
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Change Email Address</DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Current email: <strong>{user.email}</strong>
            </Typography>
            <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
              You will receive verification emails at both your current and new email addresses.
              Your current email can be used to cancel this change if it was unauthorized.
            </Typography>
            <TextField
              autoFocus
              margin="dense"
              label="New Email Address"
              type="email"
              fullWidth
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              disabled={emailChangeLoading}
            />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setEmailChangeModalOpen(false);
                setNewEmail("");
              }}
              disabled={emailChangeLoading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleEmailChangeRequest}
              variant="contained"
              disabled={emailChangeLoading}
            >
              {emailChangeLoading ? "Sending..." : "Send Verification"}
            </Button>
          </DialogActions>
        </Dialog>
        {/* Google Reviews Modal */}
        <GoogleReviewsModal
          open={reviewsModalOpen}
          onClose={() => setReviewsModalOpen(false)}
          shopName={selectedShopForReviews?.shopName}
          shopId={selectedShopForReviews?.shopId}
          placeId={selectedShopForReviews?.placeId}
          reviews={shopRatings[selectedShopForReviews?.shopId]?.reviews || []}
          loading={false}
        />

        {/* Customer Rating Detail Modal */}
        <Dialog
          open={customerRatingModalOpen}
          onClose={() => setCustomerRatingModalOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            Customer Rating
          </DialogTitle>
          <DialogContent>
            {selectedServiceDetail?.status === 'Completed' && platformRatings.customer_rating && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Rating from Mechanic
                </Typography>
                <Box sx={{ mb: 3 }}>
                  <Rating
                    value={platformRatings.customer_rating.rating || 0}
                    size="large"
                    readOnly
                  />
                  <Typography variant="body2" sx={{ mt: 1, color: '#666' }}>
                    {platformRatings.customer_rating.rating || 0} out of 5 stars
                  </Typography>
                </Box>
                {platformRatings.customer_rating.comment && (
                  <Box>
                    <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                      Comment
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        p: 2,
                        bgcolor: '#f5f5f5',
                        borderRadius: 2,
                        fontStyle: 'italic',
                        color: '#333',
                      }}
                    >
                      "{platformRatings.customer_rating.comment}"
                    </Typography>
                  </Box>
                )}
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
                  This rating was provided by the mechanic and cannot be edited.
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCustomerRatingModalOpen(false)}>
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* Mechanic Rating Modal */}
        <Dialog
          open={mechanicRatingModalOpen}
          onClose={() => setMechanicRatingModalOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Rate the Mechanic</DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                Rate the Mechanic
              </Typography>
              <Rating
                value={mechanicRating}
                size="large"
                onChange={(event, newValue) => {
                  if (newValue !== null) {
                    setMechanicRating(newValue);
                  }
                }}
                sx={{ cursor: 'pointer' }}
              />
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Comment (optional)"
                value={mechanicComment}
                onChange={(e) => setMechanicComment(e.target.value)}
                sx={{ mt: 2 }}
                placeholder="Share your experience with this mechanic..."
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setMechanicRatingModalOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveMechanicRating}
              variant="contained"
              disabled={savingMechanicRating || mechanicRating < 1}
            >
              {savingMechanicRating ? 'Saving...' : 'Save Rating'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Platform Rating Modal */}
        <Dialog
          open={platformRatingModalOpen}
          onClose={() => setPlatformRatingModalOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Rate Autoshops.com</DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                Rate Autoshops.com
              </Typography>
              <Rating
                value={platformRating}
                size="large"
                onChange={(event, newValue) => {
                  if (newValue !== null) {
                    setPlatformRating(newValue);
                  }
                }}
                sx={{ cursor: 'pointer' }}
              />
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Comment (optional)"
                value={platformComment}
                onChange={(e) => setPlatformComment(e.target.value)}
                sx={{ mt: 2 }}
                placeholder="Share your experience with Autoshops.com..."
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPlatformRatingModalOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSavePlatformRating}
              variant="contained"
              disabled={savingPlatformRating || platformRating < 1}
            >
              {savingPlatformRating ? 'Saving...' : 'Save Rating'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Conversation Modal */}
        <ConversationModal
          open={conversationModalOpen}
          onClose={() => {
            setConversationModalOpen(false);
            // Refresh message indicators when modal closes (messages were marked as read)
            if (servicesInProgress.length > 0) {
              checkForNewMessages(servicesInProgress);
            }
          }}
          shopName={selectedServiceForConversation?.shop_name}
          serviceRequestId={selectedServiceForConversation?.id}
          comments={conversationComments}
          loading={loadingConversation}
          onSendMessage={handleSendMessage}
          currentUserType="customer"
        />
      </ThemeProvider>
      <Footer />
    </div>
  );
};

export default UserProfile;
